/**
 * SalesDialerLive.jsx — fully-mocked demo of the Sales Dialer redesign
 * per `EMERGENT_IMPLEMENTATION_DIALER.md`.
 *
 * Three-panel layout: Call Queue (left) · Call Stage + Live Transcript
 * (center) · Talk Track (right). Floating note-taker bottom-right.
 * All telephony actions are mocked locally — no RingCentral/Twilio
 * round-trips. Pulls call queue from `leadFocusDemoData.js`.
 */
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Phone, PhoneOff, Mic, MicOff, Pause, Circle, Hash, Settings,
  StickyNote, X, Sparkles, ChevronUp, ChevronDown, GripVertical,
  CheckCircle2, Calendar as CalendarIcon, AlertTriangle, Send, Copy,
  ExternalLink, ArrowLeft, ChevronLeft, ChevronRight, Loader2, FileText, User as UserIcon,
  PhoneForwarded, Users,
  // Iter 210.11 (Feb 2026 · Step 4 rebuild — attachments / follow-up email /
  // tasks-with-notes / schedule demo). Extra glyphs used by the new
  // sub-components inside `step === 4`.
  Mail, Paperclip, Upload, Trash2, Plus, Clock,
} from "lucide-react";
import { realLeadToFocusShape } from "@/lib/leadRealToFocusShape";
import RcReconnectBanner from "@/components/rc/RcReconnectBanner";
import { getLeadScore } from "@/lib/getLeadScore";
import DialerContactsAddressesCard from "@/components/DialerContactsAddressesCard";
import { DialerEmailComposer } from "@/pages/DialerDemoExtensions";
import LeadContactSections from "@/pages/LeadContactSections";
import { prefillLeadInformation } from "@/lib/leadInformationPrefill";
import TransferConferencePicker from "@/components/TransferConferencePicker";
import { parseDispositionNotes, parseDispositionNotesAI, APPLIED_LABELS } from "@/lib/dispositionAIAutoMap";
// Iter 340 — Products Discussed multi-select uses the canonical slugs
// from Iter 172 §3's product tag catalog so historical data flows
// stay coherent (backend `productTags` field is unchanged).
import { PRODUCT_TAG_OPTIONS } from "@/components/ProductTagPills";
// Iter 346 — shared bottom-right lane geometry so Live Notes never
// overlaps AI Assist / Notifications / RingCentral controls.
import {
  DOCK_BOTTOM,
  PANEL_ANCHOR_BOTTOM,
  LANE_WIDTH,
  Z_DOCK_PANEL,
} from "@/lib/floatingLayout";
// Iter 177.3 (Danny Feb 2026) — post-call disposition modal STAGE
// dropdown was hardcoded to the legacy stages ("new", "qualified",
// "negotiation", "contract", "active", …). It now reads from the v2
// pipeline source of truth so the dropdown stays in sync with the
// Lead Focus page and Leads kanban automatically as we evolve the
// pipeline schema.
import { STAGES as PIPELINE_STAGES, getSubStatuses } from "@/lib/pipelineStages";
import LeadProfileSlideIn from "@/components/LeadProfileSlideIn";
// Iter 210.11 (Feb 2026 · Phase D — Schedule demo from disposition Step 4).
// We reuse the polished ScheduleMeetingModal that Lead Focus and the
// Demo Bookings dashboard both use, then translate its onSave payload
// into the `/api/demo-booking/book-internal` shape (same translator
// pattern DemoBookings.jsx already uses in ScheduleDemoUnified). This
// keeps the popup UX identical everywhere while landing the record on
// the demo_bookings dashboard where bookings agents expect it.
import ScheduleMeetingModal from "@/components/ScheduleMeetingModal";
// Iter 210.14 (Feb 2026) — shared email composer used by both Lead
// Focus Email tab and the Sales Dialer disposition modal Step 4.
// Brings template dropdown (Introduction / Follow-up / Demo Invitation /
// Quote Follow-up + all user-published APCM/CCM/RCM/MIPS templates),
// Resources slide-in, 8/10 MB guards, m365 OAuth handler, and the
// polished error messaging. Behavior-identical on both surfaces.
import ComposeEmailPanel from "@/components/ComposeEmailPanel";
// Iter 210.15 (Feb 2026) — reuse the polished "Create task" modal used
// by the Tasks page and Lead Focus so Step 4's task-creation flow is
// identical everywhere. Same testids, same fields (Title *, Description,
// Priority, Category incl. Outreach, Due date, real Assign-to picker,
// Related lead banner). Tasks POST individually via the modal instead
// of the old Iter 210.11 batch-on-save loop.
import CreateTaskModal from "@/components/CreateTaskModal";
import { CommittedCallGateBanner } from "@/components/CommittedCallGateBanner";
// Iter 240 (Aug 2026) — align disposition-modal sub-status picker with
// the Leads page grouped/tiered UI. When stage=contacted, the flat
// 32-item <select> is replaced by the same collapsible-group picker
// LeadFocus uses (Gatekeeper / Administrative / Clinical / Executive /
// No live contact). Untiered stages fall through to a flat visual list
// via the same component.
import TieredSubStatusSelect, { TIER_META } from "@/components/TieredSubStatusSelect";
import { formatEST } from "@/lib/datetime";
import axios from "axios";
import { useAuth } from "@/App";
import { PersonalReasonTag } from "@/components/PersonalCampaignEntry";
import { BackupBanner } from "@/components/dialer/PcBackupSelector";
import { TaskDueText } from "@/components/TaskDueTime";

// BW-02/BW-04 (Feb 12 PM-5) — server bridge. Demo leads (id starts with
// "d-" or otherwise not a uuid-shaped string) silently skip the backend
// round-trip so the demo route still works offline. Real leads get
// persisted to Mongo via the new PATCH/POST endpoints.
const _API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const _isPersistableLeadId = (id) => typeof id === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(id);
const _authHeaders = () => ({ Authorization: `Bearer ${(typeof localStorage !== "undefined" && localStorage.getItem("token")) || ""}` });

const fmtTime = (sec) => {
  const m = Math.floor(sec / 60).toString().padStart(2, "0");
  const s = (sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};
const initials = (name) => (name || "").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

/* ── Talk-track content per specialty ────────────────── */
const TALK_TRACKS = {
  Cardiology: [
    { section: "Opening", lines: ["Hi Dr. {lastName}, I'm calling from Enable Healthcare. Got a quick moment to chat about your practice?", "I noticed your practice handles around {benes} Medicare beneficiaries — wanted to share something specific to cardiology workflows."] },
    { section: "Discovery", lines: ["What's your current EHR situation? Any pain points around {hcpcs0} or {hcpcs1} workflows?", "How does your team currently handle echocardiogram reporting and billing?"] },
    { section: "Pitch", lines: ["Our cardiology module has built-in HCPCS 92928 / 93306 / 93458 templates — saves your team about 12 minutes per encounter.", "We integrate directly with your stress-test equipment and auto-populate the structured fields."] },
    { section: "Objections", lines: ["I hear that — most practices we speak to are also worried about migration time. We typically have cardiology practices live in 47 days.", "Yes, the cardiology module is a $18K/yr add-on — but the average practice your size recovers that in 2.3 months from coding lift alone."] },
    { section: "Close", lines: ["Can I send a 12-minute walkthrough video and book 20 minutes Friday?", "Who else on your team should be in that demo — practice manager, billing lead?"] },
  ],
  default: [
    { section: "Opening", lines: ["Hi Dr. {lastName}, this is {agent} from Enable Healthcare. Got a couple of minutes?", "I'm reaching out specifically because of your patient volume — wanted to see if there's a fit."] },
    { section: "Discovery", lines: ["What does your current EHR / billing stack look like?", "Where's the biggest bottleneck in your weekly workflow today?"] },
    { section: "Pitch", lines: ["Our platform automates the 5 most common claim-rejection reasons.", "We typically save practices 6–8 hours per provider per week."] },
    { section: "Objections", lines: ["That's fair — most practices we work with also asked about migration cost up-front.", "Implementation is 35–47 days for practices your size."] },
    { section: "Close", lines: ["Can I send a 1-page benchmark for your specialty and book 20 mins this week?", "Who else should join — practice manager, billing lead?"] },
  ],
};

/* ── Mock transcript stream content ─────────────────── */
const TRANSCRIPT_SEED = [
  { speaker: "rep", text: "Hi Dr. Lopez, this is Demo Admin from Enable Healthcare. How are you today?" },
  { speaker: "prospect", text: "I'm good — what's this regarding?" },
  { speaker: "rep", text: "I'll be brief. I noticed Allen County Cardiology handles about 456 Medicare beneficiaries with strong stent-placement volume." },
  { speaker: "prospect", text: "Yeah, we do a lot of 92928. What's your angle?" },
  { speaker: "rep", text: "Our cardiology module has built-in 92928 and 93458 templates — most practices save 12 minutes per encounter." },
  { speaker: "prospect", text: "Hmm. We're already swamped with our current EHR rollout. Can you send something I can review later?" },
  { speaker: "rep", text: "Absolutely — I'll send a 12-minute walkthrough plus a benchmark for cardiology practices your size. Friday for a 20-min follow-up?" },
  { speaker: "prospect", text: "Friday morning works. Send it to my practice manager too." },
];

/* ── AI nudges (rotates with transcription) ─────────── */
const AI_NUDGES = [
  "Mention HCPCS 92928 — they're high-volume on this code.",
  "Practice manager Maria handles scheduling — loop her in.",
  "Lead is in Demo stage — push for a confirmed time.",
  "Greenfield EHR opportunity — emphasize 35–47 day go-live.",
  "Their MIPS score is strong — position our reporting module.",
];

/* ── Top page header ─────────────────────────────────── */
function PageHead({ state, contact, clinic, onSettings, onBack, onOpenProfile, profileOpen, profileEnabled }) {
  const eyebrowMap = {
    idle: "Idle",
    ringing: "Ringing…",
    connected: "Live call · Outbound",
    held: "On hold",
    "wrap-up": "After-call work",
  };
  return (
    <div style={{ padding: "20px 28px 16px", borderBottom: "0.5px solid var(--border-soft)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
        {onBack && (
          <button
            type="button"
            data-testid="dialer-back-btn"
            onClick={onBack}
            title="Back to dialer admin"
            style={{
              display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 10px",
              background: "transparent", border: "0.5px solid var(--border-soft)", borderRadius: 6,
              fontSize: 11, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", color: "var(--text-2)",
            }}>
            <ArrowLeft style={{ width: 12, height: 12 }} /> Back
          </button>
        )}
        <div className="eyebrow">{eyebrowMap[state] || "Idle"}</div>
      </div>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: "var(--navy)", letterSpacing: "-0.02em", marginBottom: 4 }} data-testid="dialer-page-title">Sales Dialer</h1>
          <div style={{ fontSize: 13, color: "var(--text-3)" }}>
            {state === "idle" ? "Pick a lead from the queue to dial · powered by RingCentral" :
             `Dialing ${clinic} · ${(contact || "").split(" ").slice(0, 2).join(" ")} · powered by RingCentral`}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {profileEnabled && (
            <button
              data-testid="dialer-open-profile-btn"
              onClick={onOpenProfile}
              className="btn"
              style={{
                background: profileOpen ? "var(--blue-soft)" : "var(--blue)",
                color: profileOpen ? "var(--blue)" : "#fff",
                border: profileOpen ? "1px solid var(--blue)" : "none",
                fontWeight: 600,
              }}
            >
              <UserIcon style={{ width: 13, height: 13 }} /> {profileOpen ? "Profile open" : "Lead profile"}
            </button>
          )}
          <button data-testid="dialer-settings-btn" className="btn" onClick={onSettings}>
            <Settings style={{ width: 13, height: 13 }} /> Dialer settings
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Queue classification helpers (Phase B · Section 2) ────────
 * Each lead in the dialer is bucketed into one of:
 *   • "uncontacted" — never been called (stage === "new" or no recency)
 *   • "callback"    — next-action due (Today/Tomorrow/This week) or
 *                     dispositioned with a follow-up timestamp
 *   • "contacted"   — last bucket, lowest priority
 */
const BUCKET_ORDER = { uncontacted: 0, callback: 1, contacted: 2 };
const BUCKET_LABEL = {
  uncontacted: "Not yet contacted",
  callback:    "Callbacks due",
  contacted:   "Already contacted",
};
function classifyLead(l) {
  if (!l) return "contacted";
  // Iter 181 (Feb 2026) — fix stage prioritization. Previously this
  // used `!l.recency_label`, but that field is never set anywhere in
  // the app, so every lead classified as "uncontacted" and the bucket
  // sort was a silent no-op. Now uses the snapshot-enriched `stage`
  // + `contact_attempts_count` to actually distinguish never-touched
  // Stage=New leads from contacted follow-ups. NOTE: we intentionally
  // do NOT check `last_contact_date` here — realLeadToFocusShape falls
  // that back to `updated_at`, which every lead has, so the check
  // would always fire. contact_attempts_count is the reliable signal.
  const stage = String(l.stage || "").trim().toLowerCase().replace(/\s+/g, "_");
  const attempts = Number(l.contact_attempts_count || l.attempts || 0);
  if ((stage === "new" || stage === "new_lead" || !stage) && attempts === 0) {
    return "uncontacted";
  }
  const when = String(l.next_action?.when || "").toLowerCase();
  if (/today|tomorrow|this week|mon|tue|wed|thu|fri/.test(when)) return "callback";
  return "contacted";
}
function bucketCounts(arr) {
  return arr.reduce((acc, l) => { const b = classifyLead(l); acc[b] = (acc[b] || 0) + 1; return acc; }, { uncontacted: 0, callback: 0, contacted: 0 });
}



/* ── Left panel: Call Queue ─────────────────────────── */
function CallQueuePanel({
  queue, activeIdx, onPick, powerDial, setPowerDial, onOpenLead,
  uncontactedOnly, setUncontactedOnly, displayedQueue, queueMode, onResetQueue,
  queueMeta,
}) {
  const counts = useMemo(() => bucketCounts(queue), [queue]);
  // Group displayedQueue rows so we can render section headers between buckets.
  let lastBucket = null;
  // Iter 278 (Jul 2026) — build the audience-vs-callable subtitle when
  // the parent has resolved a campaign detail. Shows only when there's a
  // meaningful divergence (audience > queue length) so clean campaigns
  // stay uncluttered. Priority: prefer the persisted `callable` when
  // available (post first Start-Calling); otherwise fall back to the
  // live queue length which effectively IS the callable set right now.
  const audience = Number(queueMeta?.audience || 0);
  const callableFromDoc = (typeof queueMeta?.callable === "number") ? queueMeta.callable : null;
  const effectiveCallable = callableFromDoc ?? queue.length;
  const mine = queueMeta?.dialable_summary?.mine;
  const showSubtitle = !mine && audience > 0 && audience > effectiveCallable;
  const mineBreakdown = mine ? Object.entries(mine.not_dialable_breakdown || {}).map(([k, v]) => `${v.toLocaleString()} ${k}`).join(", ") : "";
  const excludedTotal = showSubtitle ? (audience - effectiveCallable) : 0;
  const dncN = Number(queueMeta?.excluded_dnc || 0);
  const wrongN = Number(queueMeta?.excluded_wrong_specialty || 0);
  const excludedBreakdown = (dncN + wrongN) > 0
    ? `${dncN.toLocaleString()} DNC · ${wrongN.toLocaleString()} wrong-specialty`
    : "DNC + wrong-specialty";
  return (
    <div className="card" style={{ padding: 0, overflow: "hidden", display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ padding: "12px 16px", borderBottom: "0.5px solid var(--border-soft)" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <span style={{ fontFamily: "var(--font-head)", fontSize: 14, fontWeight: 600, color: "var(--navy)" }}>Call Queue</span>
          {queueMode === "followup" && (
            <span data-testid="queue-mode-followup" style={{ marginLeft: 8, padding: "2px 7px", borderRadius: 999, fontSize: 9, fontWeight: 700, letterSpacing: "0.04em", background: "#FFF6E0", color: "#A6660B" }}>FOLLOW-UP</span>
          )}
          <span className="num" style={{ marginLeft: "auto", fontSize: 11, color: "var(--text-3)" }}>
            {displayedQueue.length} of {queue.length}
          </span>
        </div>
        {/* Iter 278 (Jul 2026) — audience-vs-callable transparency line.
            Answers the exact confusion a rep has when they see "Call
            Queue 5,017 of 5,017" but remember the campaign card said
            6,663. The 1,646 delta is DNC + wrong-specialty filtered
            at queue-materialization time. */}
        {mine && (
          <div data-testid="call-queue-dialable-subtitle" style={{ marginTop: 4, fontSize: 10, color: "#0F766E", fontWeight: 500, lineHeight: 1.35 }}>
            {queue.length.toLocaleString()} in your queue
            {mine.not_dialable > 0 && (
              <span data-testid="call-queue-not-dialable" style={{ color: "#94A3B8", fontWeight: 400 }}>
                {" · "}{mine.not_dialable.toLocaleString()} not dialable ({mineBreakdown})
              </span>
            )}
          </div>
        )}
        {showSubtitle && (
          <div
            data-testid="call-queue-callable-subtitle"
            title={`Audience snapshot: ${audience.toLocaleString()}\nCallable in queue: ${effectiveCallable.toLocaleString()}\nExcluded ${excludedTotal.toLocaleString()}: ${excludedBreakdown}`}
            style={{ marginTop: 4, fontSize: 10, color: "#0F766E", fontWeight: 500, lineHeight: 1.35 }}
          >
            {effectiveCallable.toLocaleString()} of {audience.toLocaleString()} leads callable
            <span style={{ color: "#94A3B8", fontWeight: 400 }}>
              {" · "}{excludedTotal.toLocaleString()} excluded ({excludedBreakdown})
            </span>
          </div>
        )}
      </div>
      <div style={{ padding: "10px 16px", borderBottom: "0.5px solid var(--border-soft)", display: "flex", alignItems: "center", gap: 10 }}>
        <label style={{ fontSize: 11, color: "var(--text-2)", display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", flex: 1 }}>
          <input
            type="checkbox"
            data-testid="queue-uncontacted-only"
            checked={uncontactedOnly}
            onChange={(e) => setUncontactedOnly(e.target.checked)}
            style={{ width: 13, height: 13, cursor: "pointer" }}
          />
          Uncontacted only
          <span style={{ fontSize: 10, color: "var(--text-4)" }}>({counts.uncontacted})</span>
        </label>
        <span style={{ fontSize: 11, color: "var(--text-2)" }}>Power dial</span>
        <button
          data-testid="power-dial-toggle"
          onClick={() => setPowerDial(!powerDial)}
          style={{
            width: 32, height: 18, borderRadius: 9, border: "none", padding: 0,
            background: powerDial ? "var(--success)" : "var(--border)",
            position: "relative", cursor: "pointer", transition: "background 120ms ease",
          }}
        >
          <span style={{ position: "absolute", top: 2, left: powerDial ? 16 : 2, width: 14, height: 14, borderRadius: "50%", background: "#fff", transition: "left 120ms ease" }} />
        </button>
      </div>
      <div style={{ flex: 1, overflowY: "auto" }}>
        {displayedQueue.length === 0 ? (
          <div data-testid="queue-empty" style={{ padding: "24px 16px", textAlign: "center", fontSize: 11, color: "var(--text-3)" }}>
            No leads match the current filter.
            <button onClick={() => setUncontactedOnly(false)} style={{ display: "block", margin: "8px auto 0", background: "none", border: "none", color: "var(--blue)", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>Show all leads</button>
          </div>
        ) : displayedQueue.map((row) => {
          const { lead: l, idx: i } = row;
          const bucket = classifyLead(l);
          const showHeader = bucket !== lastBucket && queueMeta?.source !== "personal_live";
          lastBucket = bucket;
          const isActive = i === activeIdx;
          return (
            <React.Fragment key={l.id}>
              {showHeader && (
                <div data-testid={`queue-divider-${bucket}`} style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "8px 16px 4px",
                  fontSize: 9, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
                  color: bucket === "uncontacted" ? "#1D9E75" : bucket === "callback" ? "#A6660B" : "var(--text-3)",
                  background: bucket === "uncontacted" ? "#E9F8F1" : bucket === "callback" ? "#FFF6E0" : "var(--subtle)",
                  borderTop: "0.5px solid var(--border-soft)",
                  borderBottom: "0.5px solid var(--border-soft)",
                }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: bucket === "uncontacted" ? "#1D9E75" : bucket === "callback" ? "#A6660B" : "#888" }} />
                  {BUCKET_LABEL[bucket]}
                  <span style={{ marginLeft: "auto", fontWeight: 500, opacity: 0.7 }}>{counts[bucket] || 0}</span>
                </div>
              )}
              <div
                data-testid={`queue-row-${i}`}
                data-bucket={bucket}
                onClick={() => onPick(i)}
                style={{
                  display: "flex", alignItems: "center", gap: 10, padding: "10px 16px",
                  borderBottom: "0.5px solid var(--border-soft)",
                  background: isActive ? "var(--blue-soft)" : "transparent",
                  borderLeft: isActive ? "2px solid var(--blue)" : "2px solid transparent",
                  cursor: "pointer",
                }}
              >
                <GripVertical style={{ width: 11, height: 11, color: "var(--text-4)" }} />
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: "var(--navy)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 600, flexShrink: 0 }}>
                  {initials(l.clinic)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "var(--navy)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.clinic}</div>
                  <div style={{ fontSize: 11, color: "var(--text-3)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.contacts[0]?.name?.replace(/^Dr\.?\s*/, "")}</div>
                  <div className="num" style={{ fontSize: 10, color: "var(--text-4)", marginTop: 1 }}>{l.contacts[0]?.phone || "—"}</div>
                  {l._personal_reason_key && (
                    <div style={{ marginTop: 3 }}>
                      <PersonalReasonTag reasonKey={l._personal_reason_key} label={l._personal_reason_label} testid={`queue-reason-tag-${i}`} />
                    </div>
                  )}
                </div>
                <button
                  data-testid={`queue-open-lead-${i}`}
                  title="Open in Lead Focus"
                  onClick={(e) => { e.stopPropagation(); onOpenLead(l); }}
                  style={{ background: "none", border: "0.5px solid var(--border)", borderRadius: 6, padding: "3px 5px", cursor: "pointer", color: "var(--blue)", display: "inline-flex" }}
                >
                  <ExternalLink style={{ width: 10, height: 10 }} />
                </button>
                <span style={{
                  padding: "2px 7px", borderRadius: 999, fontSize: 9, fontWeight: 700, letterSpacing: "0.04em",
                  background: l.tier === "A" ? "#F4EAFE" : l.tier === "B" ? "var(--navy)" : "var(--subtle)",
                  color: l.tier === "A" ? "#5B6CFF" : l.tier === "B" ? "#fff" : "var(--text-3)",
                }}>{l.tier}</span>
              </div>
            </React.Fragment>
          );
        })}
      </div>
      <div style={{ padding: "10px 16px", borderTop: "0.5px solid var(--border-soft)", display: "flex", alignItems: "center", gap: 8 }}>
        <button data-testid="add-to-queue-btn" onClick={() => toast.info("Lead picker (mock)")} style={{ background: "none", border: "none", color: "var(--blue)", fontSize: 11, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", padding: 0 }}>
          + Add to queue
        </button>
        {queueMode === "followup" && (
          <button data-testid="queue-reset-btn" onClick={onResetQueue} style={{ marginLeft: "auto", background: "none", border: "none", color: "var(--text-3)", fontSize: 11, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", padding: 0 }}>
            Back to primary
          </button>
        )}
      </div>
    </div>
  );
}

/* ── Center: Call Stage ──────────────────────────────── */
function CallStage({ lead, state, timer, onAction, transcript, muted, keypadOpen, onDtmf, currentCallId, telephonySessionId, partyId }) {
  const transcriptRef = useRef(null);
  useEffect(() => { if (transcriptRef.current) transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight; }, [transcript]);
  // Iter 185 (Feb 2026) — Transfer / Conference picker state.
  const [xferPickerOpen, setXferPickerOpen] = useState(false);
  const [confPickerOpen, setConfPickerOpen] = useState(false);
  const [warmXferActive, setWarmXferActive] = useState(false);
  // Listen for warm-transfer state changes emitted by RCWebPhone.
  useEffect(() => {
    const onXferState = (ev) => {
      const s = ev?.detail?.state;
      if (s === "target-ringing") setWarmXferActive(true);
      else if (s === "completed" || s === "cancelled" || s === "error") setWarmXferActive(false);
    };
    window.addEventListener("webphone:transfer-state", onXferState);
    return () => window.removeEventListener("webphone:transfer-state", onXferState);
  }, []);
  const dispatchControl = (action, detail = {}) => {
    window.dispatchEvent(new CustomEvent("webphone:control", { detail: { action, ...detail } }));
  };

  const orbStyle = {
    width: 100, height: 100, borderRadius: "50%",
    background: "linear-gradient(135deg, #1b365D, #2A4B7C)",
    color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
    fontFamily: "var(--font-head)", fontSize: 30, fontWeight: 700,
    boxShadow: state === "connected" ? "0 0 0 8px rgba(37,99,235,0.12), 0 12px 32px rgba(15,23,42,0.18)" : "0 8px 24px rgba(15,23,42,0.12)",
    transition: "box-shadow 240ms ease",
    position: "relative",
  };

  return (
    <div className="card" style={{ padding: 0, overflow: "auto", display: "flex", flexDirection: "column", height: "100%", minHeight: "min-content" }}>
      <div style={{ flex: 1, padding: "32px 24px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, background: "linear-gradient(180deg, var(--paper), var(--subtle) 95%)", borderBottom: "0.5px solid var(--border-soft)" }}>
        <div data-testid="call-orb" style={orbStyle}>
          {initials(lead.clinic)}
          {state === "ringing" && (
            <span style={{
              position: "absolute", inset: -12, borderRadius: "50%",
              border: "3px solid rgba(37,99,235,0.5)",
              animation: "ringPulse 1.4s ease-out infinite",
            }} />
          )}
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontFamily: "var(--font-head)", fontSize: 22, fontWeight: 700, color: "var(--navy)" }}>{lead.contacts?.[0]?.name || lead.contact_name || "—"}</div>
          {lead._personal_reason_key && (
            <div style={{ marginTop: 6 }}>
              <PersonalReasonTag reasonKey={lead._personal_reason_key} label={lead._personal_reason_label} testid="call-stage-reason-tag" />
            </div>
          )}
          <div style={{ fontSize: 13, color: "var(--text-3)", marginTop: 4 }}>
            {lead.clinic}
            {(lead.locations?.[0]?.city || lead.locations?.[0]?.state) && (
              <> · {[lead.locations[0].city, lead.locations[0].state].filter(Boolean).join(", ")}</>
            )}
            {lead._real && lead.specialty && <> · {lead.specialty}</>}
          </div>
          {lead._real && !!(lead.ai_score || lead.value) && (
            <div data-testid="call-stage-real-meta" style={{ display: "inline-flex", gap: 10, marginTop: 8, fontSize: 11, color: "var(--text-3)" }}>
              {lead.ai_score != null && <span>AI <span className="num" style={{ fontWeight: 600, color: "var(--navy)" }}>{getLeadScore(lead)}</span>/100</span>}
              {lead.value && <span>Pipeline <span className="num" style={{ fontWeight: 600, color: "var(--navy)" }}>${(lead.value / 1000).toFixed(0)}K</span></span>}
              {lead.stage && <span>Stage <span style={{ fontWeight: 600, color: "var(--navy)" }}>{lead.stage}</span></span>}
            </div>
          )}
        </div>
        <div data-testid="call-timer" className="num" style={{ fontSize: 32, fontWeight: 600, color: "var(--navy)", letterSpacing: "0.04em", fontFamily: "JetBrains Mono, ui-monospace, SF Mono, Menlo, monospace" }}>
          {fmtTime(timer)}
        </div>
        {state === "connected" && (
          <div style={{ fontSize: 11, color: "var(--text-3)", display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Circle style={{ width: 7, height: 7, fill: "var(--error)", color: "var(--error)" }} /> Call is being recorded
          </div>
        )}
        <div style={{ display: "flex", gap: 12, marginTop: 8, flexWrap: "wrap", justifyContent: "center", maxWidth: "100%" }}>
          {/* Super-user fix May 2026: mute / keypad / hold are now usable
              during the entire call lifecycle (ringing → connected → held)
              so reps can navigate IVRs ("press 1 for English") and mute
              before the callee picks up. Only the idle (no-active-call)
              state disables them. */}
          <CallButton testid="call-mute" icon={muted ? MicOff : Mic} label="Mute" disabled={state === "idle"} active={muted} onClick={() => onAction("mute")} />
          <CallButton testid="call-keypad" icon={Hash} label="Keypad" disabled={state === "idle"} onClick={() => onAction("keypad")} />
          <CallButton testid="call-hold" icon={Pause} label="Hold" disabled={state === "idle" || state === "ringing"} onClick={() => onAction("hold")} active={state === "held"} />
          <CallButton testid="call-record" icon={Circle} label="Record" disabled={state === "idle"} onClick={() => onAction("record")} />
          {/* Iter 185 (Feb 2026) — Transfer + Conference. Only surface
              during a connected/held call — transfer on ringing/idle is
              undefined RC-side. Warm-transfer surfaces Complete/Cancel
              inline once the target ringback starts. */}
          {warmXferActive ? (
            <>
              <CallButton
                testid="call-warm-complete"
                icon={PhoneForwarded}
                label="Complete"
                tone="success"
                onClick={() => { dispatchControl("transfer-warm-complete"); setWarmXferActive(false); }}
              />
              <CallButton
                testid="call-warm-cancel"
                icon={X}
                label="Cancel"
                tone="danger"
                onClick={() => { dispatchControl("transfer-warm-cancel"); setWarmXferActive(false); }}
              />
            </>
          ) : (
            <>
              <CallButton
                testid="call-transfer"
                icon={PhoneForwarded}
                label="Transfer"
                disabled={state === "idle" || state === "ringing"}
                active={xferPickerOpen}
                onClick={() => { setConfPickerOpen(false); setXferPickerOpen((v) => !v); }}
              />
              <CallButton
                testid="call-conference"
                icon={Users}
                label="Conference"
                disabled={state === "idle" || state === "ringing"}
                active={confPickerOpen}
                onClick={() => { setXferPickerOpen(false); setConfPickerOpen((v) => !v); }}
              />
            </>
          )}
          {state === "idle" ? (
            <CallButton testid="call-start" icon={Phone} label="Call" tone="success" onClick={() => onAction("start")} />
          ) : (
            <CallButton testid="call-end" icon={PhoneOff} label="End" tone="danger" onClick={() => onAction("end")} />
          )}
        </div>
        {keypadOpen && state !== "idle" && (
          <div data-testid="dtmf-keypad" style={{
            display: "grid", gridTemplateColumns: "repeat(3, 1fr)",
            gap: 6, marginTop: 12, maxWidth: 220, marginInline: "auto",
          }}>
            {["1","2","3","4","5","6","7","8","9","*","0","#"].map((d) => (
              <button
                key={`live-dtmf-${d}`}
                data-testid={`live-dtmf-${d}`}
                onClick={() => onDtmf(d)}
                style={{
                  padding: "10px 0", borderRadius: 8,
                  background: "var(--paper)", border: "0.5px solid var(--border)",
                  color: "var(--navy)", fontSize: 18, fontWeight: 600,
                  cursor: "pointer", fontFamily: "inherit",
                }}
              >
                {d}
              </button>
            ))}
          </div>
        )}
      </div>
      {/* Iter 185 — Transfer / Conference picker popovers. Positioned
          relative to the outer card so they float above the transcript. */}
      <div style={{ position: "relative" }}>
        <TransferConferencePicker
          mode="transfer"
          open={xferPickerOpen}
          onClose={() => setXferPickerOpen(false)}
          fromCallId={currentCallId}
          leadId={lead?.id}
          onAction={(action, target) => {
            // Blind = fire-and-close. Warm = begin a warmTransfer leg.
            window.dispatchEvent(new CustomEvent("webphone:control", {
              detail: { action, target: target.extension || target.phone },
            }));
          }}
        />
        <TransferConferencePicker
          mode="conference"
          open={confPickerOpen}
          onClose={() => setConfPickerOpen(false)}
          fromCallId={currentCallId}
          telephonySessionId={telephonySessionId}
          partyId={partyId}
          leadId={lead?.id}
          onAction={(action, target, backendResp) => {
            // If backend returned mode=fallback, drive SDK warm-transfer.
            if (backendResp?.mode === "fallback") {
              window.dispatchEvent(new CustomEvent("webphone:control", {
                detail: { action: "conference-add", target: target.extension || target.phone, fallback: true },
              }));
            }
          }}
        />
      </div>
      <div style={{ background: "var(--subtle)", padding: "14px 20px 18px", maxHeight: 220, overflowY: "auto" }} ref={transcriptRef}>
        <div className="eyebrow" style={{ marginBottom: 8 }}>Live transcription</div>
        {transcript.length === 0 ? (
          <div style={{ fontSize: 12, color: "var(--text-4)", fontStyle: "italic" }}>Transcription will appear here once the call connects…</div>
        ) : transcript.map((u, i) => (
          <div key={i} data-testid={`transcript-${i}`} style={{ fontSize: 13, color: "var(--text-2)", lineHeight: 1.55, marginBottom: 6 }}>
            <span style={{ fontWeight: 700, color: u.speaker === "rep" ? "var(--blue)" : "var(--text)" }}>{u.speaker === "rep" ? "Rep" : "Prospect"}:</span> {u.text}
          </div>
        ))}
      </div>
    </div>
  );
}

function CallButton({ icon: Icon, label, onClick, disabled, active, tone, testid }) {
  const tones = {
    success: { bg: "var(--success)", color: "#fff", border: "var(--success)" },
    danger:  { bg: "var(--error)", color: "#fff", border: "var(--error)" },
    default: active
      ? { bg: "var(--navy)", color: "#fff", border: "var(--navy)" }
      : { bg: "var(--paper)", color: "var(--text-2)", border: "var(--border)" },
  };
  const t = tones[tone] || tones.default;
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      title={label}
      style={{
        width: 48, height: 48, borderRadius: "50%",
        background: t.bg, color: t.color, border: `0.5px solid ${t.border}`,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.4 : 1,
        boxShadow: tone === "danger" || tone === "success" ? "0 6px 16px rgba(15,23,42,0.16)" : "0 2px 6px rgba(15,23,42,0.06)",
        transition: "background-color 120ms ease, box-shadow 120ms ease",
        fontFamily: "inherit",
      }}
    >
      <Icon style={{ width: 18, height: 18, strokeWidth: 2 }} />
    </button>
  );
}

/* ── Right: Talk Track ─────────────────────────────────
 * Demo leads carry rich NPPES/CMS/HCPCS data so the talk-track interpolates
 * specialty-specific cues. Real-mode leads don't — we fall back to a generic
 * specialty-aware template that pulls from the CRM fields we DO have
 * (specialty, products_of_interest, pain_point_category, next_action, ai_score).
 */
function TalkTrackPanel({ lead, currentNudge }) {
  const lastName = (lead.contacts?.[0]?.name || lead.contact_name || "").split(" ").slice(-1)[0];
  const copyLine = (text) => { navigator.clipboard?.writeText(text); toast.success("Copied to clipboard"); };

  // ── Real-mode talk track — built from real CRM fields ──
  const realTrack = (() => {
    const _toList = (v) => Array.isArray(v) ? v.map((s) => String(s).trim()).filter(Boolean) : String(v || "").split(/[;,]/).map((s) => s.trim()).filter(Boolean);
    const products = _toList(lead.products_of_interest);
    const painPoints = _toList(lead.pain_points);
    const timeline = lead.prospect_decision_timeline || "";
    const greet = lastName ? `Dr. ${lastName}` : (lead.contact_name || "there");
    const t = [
      { section: "Opening", lines: [
        `Hi ${greet}, thanks for taking my call. I'm reaching out from Enable Healthcare — wanted to share a quick reason I think we could be a fit for ${lead.clinic}.`,
        `${greet}, I won't take more than 2 minutes — is now still a good time?`,
      ]},
    ];
    if (lead.specialty || products.length) {
      const specialty = lead.specialty || "your specialty";
      t.push({ section: "Hook", lines: [
        products.length
          ? `I noticed you're evaluating **${products[0]}**${products[1] ? ` and ${products[1]}` : ""} — we've helped ${specialty} practices stand that up in under 6 weeks, typically with an ROI inside the first quarter.`
          : `We work with a lot of ${specialty} practices on streamlining their tech stack — most see their first measurable wins inside 30 days.`,
        getLeadScore(lead) >= 70
          ? `Based on what we know about ${lead.clinic}, we think you're a strong fit — our AI scoring puts you in the top tier.`
          : `Most practices like yours are leaving revenue on the table — I'd love to walk through where we typically see the biggest unlocks.`,
      ]});
    }
    if (painPoints.length) {
      t.push({ section: "Pain points", lines: painPoints.slice(0, 3).map((p) => `Last time we spoke you mentioned **${p}** — is that still the biggest priority, or has anything shifted?`)});
    } else {
      t.push({ section: "Discovery", lines: [
        `If you had to pick one thing about the current workflow that's costing you the most time — what would it be?`,
        `How is your team handling ${lead.specialty ? `${lead.specialty} ` : ""}billing and documentation today? What's working, what's painful?`,
        `Walk me through what a typical day looks like for your front-office staff right now.`,
      ]});
    }
    if (timeline) {
      t.push({ section: "Close", lines: [
        `You mentioned a timeline of **${timeline}** — what would it take, from your side, to keep us moving forward on that schedule?`,
        `If we got you a tailored proposal by ${timeline.includes("0-30") ? "end of week" : timeline.includes("30-60") ? "end of next week" : "end of month"}, would that fit your evaluation cycle?`,
      ]});
    } else {
      t.push({ section: "Close", lines: [
        `If everything makes sense today, what does your decision process typically look like?`,
        `What's the best next step for you — do you want me to put together a proposal, or get a colleague on a call first?`,
      ]});
    }
    if (lead.next_action) {
      t.push({ section: "Recap (next action)", lines: [
        `Let me recap so we're aligned — the next step is: **${lead.next_action}**. Anything you want to add to that?`,
      ]});
    }
    return t;
  })();

  // ── Demo-mode talk track (specialty rich) ──
  const demoTrack = (() => {
    const specialty = lead.nppes?.specialty_group;
    const baseTrack = TALK_TRACKS[specialty] || TALK_TRACKS.default;
    const hcpcs = lead.top_hcpcs || [];
    const interp = (text) => text
      .replace("{lastName}", lastName)
      .replace("{benes}", lead.medicare?.beneficiaries?.toLocaleString() || "—")
      .replace("{hcpcs0}", hcpcs[0]?.code || "92928")
      .replace("{hcpcs1}", hcpcs[1]?.code || "93306")
      .replace("{agent}", "Demo Admin");
    return baseTrack.map((s) => ({ section: s.section, lines: s.lines.map(interp) }));
  })();

  const track = lead._real ? realTrack : demoTrack;
  return (
    <div className="card" style={{ padding: 0, overflow: "hidden", display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", padding: "12px 16px", borderBottom: "0.5px solid var(--border-soft)" }}>
        <span style={{ fontFamily: "var(--font-head)", fontSize: 14, fontWeight: 600, color: "var(--navy)" }}>Talk Track</span>
        <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10, color: "var(--blue)", fontWeight: 600 }}>
          <Sparkles style={{ width: 11, height: 11 }} /> {lead._real ? "CRM-coached" : "AI-coached"}
        </span>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: 14 }}>
        {track.map((s, si) => (
          <div key={s.section} style={{ marginBottom: 14 }}>
            <div className="eyebrow" style={{ marginBottom: 6 }}>{s.section}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {s.lines.map((line, li) => (
                <button
                  key={li}
                  data-testid={`tt-${si}-${li}`}
                  onClick={() => copyLine(line)}
                  style={{
                    textAlign: "left", padding: "8px 12px", borderRadius: 8,
                    background: "var(--subtle)", borderLeft: "2px solid var(--blue)",
                    border: "0.5px solid var(--border-soft)", borderLeftWidth: 2,
                    fontSize: 12, color: "var(--text-2)", lineHeight: 1.55,
                    cursor: "pointer", fontFamily: "inherit",
                    transition: "background-color 120ms ease",
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.background = "var(--blue-soft)")}
                  onMouseOut={(e) => (e.currentTarget.style.background = "var(--subtle)")}
                  dangerouslySetInnerHTML={{ __html: line.replace(/\*\*(.+?)\*\*/g, '<strong style="color:var(--navy)">$1</strong>') }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
      <div style={{ padding: 12, borderTop: "0.5px solid var(--border-soft)", background: "linear-gradient(135deg, #EEF6FF, #F4EAFE)" }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: "#5B6CFF", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6, display: "inline-flex", alignItems: "center", gap: 4 }}>
          <Sparkles style={{ width: 11, height: 11 }} /> AI nudge · live
        </div>
        <div style={{ fontSize: 12, color: "var(--text-2)", lineHeight: 1.5 }} data-testid="ai-nudge">{currentNudge}</div>
      </div>
    </div>
  );
}

/* ── Floating note-taker ─────────────────────────────── */
function NoteTaker({ open, setOpen, lead, timer, notes, setNotes, activeTag, setActiveTag, onPolish }) {
  const [draft, setDraft] = useState("");
  const { token } = useAuth();
  const TAGS = [
    { id: "objection", label: "Objection", color: "var(--error)", short: "⌘1" },
    { id: "next-step", label: "Next step", color: "var(--blue)", short: "⌘2" },
    { id: "decision-maker", label: "Decision-maker", color: "var(--success)", short: "⌘3" },
    { id: "competitor", label: "Competitor", color: "var(--warning)", short: "⌘4" },
  ];
  const save = () => {
    if (!draft.trim()) return;
    const note = { id: `n-${Date.now()}`, ts: timer, body: draft.trim(), tag: activeTag };
    setNotes([...notes, note]);
    setDraft("");
    // Persist to backend immediately so the note survives navigation away
    // from /dialer/live before the rep dispositions the call. Otherwise
    // the in-call notes only live in React state and vanish if the rep
    // jumps to another tab or refreshes mid-call (super-user fix May 2026
    // — "I hit enter and it was there. Looking back now it's not in the
    //  notes!" reports).
    if (lead?.id && token) {
      const tagSuffix = activeTag ? ` #${activeTag}` : "";
      axios.post(`${_API}/leads/${lead.id}/notes`,
        { body: `${note.body}${tagSuffix}` },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 8000 },
      ).then(() => toast.success("Note saved"))
       .catch((e) => toast.error(`Note saved locally but not persisted: ${e?.response?.data?.detail || "retry"}`));
    } else {
      toast.success("Note saved");
    }
  };
  useEffect(() => {
    const h = (e) => {
      const meta = e.metaKey || e.ctrlKey;
      if (!meta) return;
      if (e.key === "n") { e.preventDefault(); setOpen(!open); }
      else if (e.key === "1") { setActiveTag("objection"); }
      else if (e.key === "2") { setActiveTag("next-step"); }
      else if (e.key === "3") { setActiveTag("decision-maker"); }
      else if (e.key === "4") { setActiveTag("competitor"); }
      else if (e.key === "Enter" && open) { save(); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, draft, activeTag, notes, timer]);

  // Iter 346 — the FAB now lives INSIDE the shared bottom-dock in
  // App.js (portaled into `#bottom-dock-page-slot`) so it can never
  // overlap AI Assist / Notifications / RC controls. If for any reason
  // the slot isn't in the DOM (e.g., rendered outside MainLayout in a
  // future refactor), we fall back to a floating FAB anchored above the
  // reserved dock strip.
  const dockSlot = typeof document !== "undefined"
    ? document.getElementById("bottom-dock-page-slot")
    : null;

  if (!open) {
    const fabButton = (
      <button
        data-testid="notetaker-fab"
        onClick={() => setOpen(true)}
        title="Live notes (⌘N)"
        aria-label="Open live notes"
        style={{
          width: 36, height: 36, borderRadius: "50%",
          background: "var(--navy)", color: "#fff",
          border: "none", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 2px 6px rgba(15,23,42,0.20)", flexShrink: 0,
        }}
      >
        <StickyNote style={{ width: 16, height: 16 }} />
      </button>
    );
    return dockSlot ? createPortal(fabButton, dockSlot) : (
      // Fallback: anchored above the reserved dock strip, not on top of it.
      <div style={{ position: "fixed", right: DOCK_BOTTOM, bottom: PANEL_ANCHOR_BOTTOM, zIndex: Z_DOCK_PANEL }}>
        {fabButton}
      </div>
    );
  }
  const panelBody = (
    <div
      data-testid="notetaker-panel"
      style={{
        position: "fixed",
        bottom: PANEL_ANCHOR_BOTTOM,
        right: DOCK_BOTTOM,
        width: LANE_WIDTH,
        background: "var(--paper)",
        borderRadius: 14,
        boxShadow: "0 24px 60px rgba(15,23,42,0.18)",
        border: "0.5px solid var(--border)",
        zIndex: Z_DOCK_PANEL,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        maxHeight: `calc(100vh - ${PANEL_ANCHOR_BOTTOM + 24}px)`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 14px", borderBottom: "0.5px solid var(--border-soft)" }}>
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--error)", animation: "pulse 1.4s ease-in-out infinite" }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--navy)" }}>Live notes</div>
          <div className="num" style={{ fontSize: 10, color: "var(--text-3)", marginTop: 1 }}>{lead?.clinic} · {fmtTime(timer)}</div>
        </div>
        <button data-testid="notetaker-flush" onClick={() => { toast.success(`${notes.length} notes saved to lead`); setNotes([]); }} title="Save all to lead" style={{ background: "none", border: "none", color: "var(--text-3)", cursor: "pointer" }}><Send style={{ width: 13, height: 13 }} /></button>
        <button data-testid="notetaker-close" onClick={() => setOpen(false)} style={{ background: "none", border: "none", color: "var(--text-3)", cursor: "pointer" }}><X style={{ width: 14, height: 14 }} /></button>
      </div>
      <div style={{ display: "flex", gap: 4, padding: "10px 14px", flexWrap: "wrap" }}>
        {TAGS.map((t) => (
          <button key={t.id} data-testid={`tag-${t.id}`} onClick={() => setActiveTag(activeTag === t.id ? null : t.id)} style={{ padding: "3px 8px", fontSize: 10, fontWeight: 500, borderRadius: 999, border: `0.5px solid ${activeTag === t.id ? t.color : "var(--border)"}`, background: activeTag === t.id ? t.color : "transparent", color: activeTag === t.id ? "#fff" : "var(--text-3)", cursor: "pointer", fontFamily: "inherit" }}>
            {t.label} <span className="num" style={{ marginLeft: 4, fontSize: 9, opacity: 0.7 }}>{t.short}</span>
          </button>
        ))}
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "0 14px 8px", maxHeight: 200 }}>
        {notes.length === 0 && <div style={{ fontSize: 11, color: "var(--text-4)", fontStyle: "italic", padding: "8px 0" }}>No notes yet. Type below + ⌘Enter to save.</div>}
        {notes.map((n) => {
          const tagCfg = TAGS.find((t) => t.id === n.tag);
          return (
            <div key={n.id} style={{ display: "flex", gap: 6, padding: "6px 0", borderBottom: "0.5px solid var(--border-soft)" }}>
              <span className="num" style={{ fontSize: 10, color: "var(--text-4)", minWidth: 36 }}>{fmtTime(n.ts)}</span>
              {tagCfg && <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 999, background: tagCfg.color, color: "#fff", fontWeight: 600, alignSelf: "center" }}>{tagCfg.label}</span>}
              <span style={{ flex: 1, fontSize: 11, color: "var(--text-2)", lineHeight: 1.4 }}>{n.body}</span>
            </div>
          );
        })}
      </div>
      {notes.length >= 3 && (
        <button data-testid="notetaker-polish" onClick={onPolish} style={{ margin: "0 14px 8px", padding: "8px 12px", borderRadius: 8, background: "linear-gradient(135deg, #EEF6FF, #F4EAFE)", border: "none", fontSize: 11, fontWeight: 500, color: "#5B6CFF", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "inherit" }}>
          <Sparkles style={{ width: 12, height: 12 }} /> AI ready to polish {notes.length} notes
        </button>
      )}
      <div style={{ padding: 10, borderTop: "0.5px solid var(--border-soft)" }}>
        <textarea
          data-testid="notetaker-input"
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={activeTag ? `Add ${activeTag} note…` : "Type a note…"}
          rows={2}
          className="input"
          style={{ resize: "none", fontSize: 12, marginBottom: 6 }}
        />
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span className="num" style={{ flex: 1, fontSize: 9, color: "var(--text-4)" }}>⌘Enter save · ⌘N hide · ⌘1–4 tag</span>
          <button data-testid="notetaker-save" onClick={save} className="btn btn-primary" style={{ padding: "5px 12px", fontSize: 11 }}>Save</button>
        </div>
      </div>
    </div>
  );
  return panelBody;
}

/* ── Disposition modal — detailed (carries old LeadFocusView LogCallModal fields) ─ */
// Iter 340 (Feb 2026) — Sales Dialer disposition defaults + call-log
// vocabulary aligned to real rep behavior. User feedback: the demo-funnel
// biased defaults ("Demo" / "Connected · Demo booked" / "Schedule demo")
// forced reps to actively re-select on every cold call. Now Cold Call is
// the default type; outcome and next-step both start empty ("— Select —")
// so reps only fill them when applicable, and BOTH gained affirmative
// "no action" choices (No Action Needed / No follow-up needed) for calls
// that genuinely close out with nothing to do.
const DISP_OUTCOMES = [
  "Connected",
  "Voicemail",
  "No answer",
  "Wrong number",
  "Do not call",
  "Busy",
  "Gatekeeper",
  "Decision-maker reached",
  "Demo booked",
  "Demo declined",
  "Sent to follow-up nurture",
  "Disqualified",
  // Iter 340 — new outcomes requested by the field team.
  "Auto Attendant / IVR",
  "Left on Hold",
  "Callback Requested",
  "Not Interested",
  "Hospital Affiliated (Cannot Purchase)",
  "No Action Needed",
];
const DISP_CALL_TYPES = ["Cold call", "Discovery call", "Demo", "Follow-up", "Pricing call", "Closing call", "Implementation kickoff", "QBR / Check-in"];
const DISP_SENTIMENTS = [
  { id: "very_positive", label: "Very positive", color: "var(--success)" },
  { id: "positive", label: "Positive", color: "#86efac" },
  { id: "neutral", label: "Neutral", color: "var(--text-3)" },
  { id: "negative", label: "Negative", color: "#fca5a5" },
  { id: "very_negative", label: "Very negative", color: "var(--error)" },
];
const DISP_NEXT_STEPS = [
  "Send proposal",
  "Schedule demo",
  "Send pricing",
  "Send case study",
  "Loop in technical resource",
  "Wait for response",
  "Re-engage in 30 days",
  "Closed",
  // Iter 340 — explicit "no follow-up" choice paired with the Outcome
  // "No Action Needed" so reps can affirmatively close-out a call
  // without leaving Next Step blank (blank = "didn't decide yet").
  "No follow-up needed",
];

// ═════════════════════════════════════════════════════════════════════
// Iter 210.12 · Step 5 Review & Confirm helpers.
// Small presentational components used only by the review screen.
// Kept co-located with SalesDialerLive rather than extracted to a
// separate file — they're 4 tiny functions and only used here.
// ═════════════════════════════════════════════════════════════════════

// Human-readable label for a leadEdits key. Falls back to the raw key
// with underscores → spaces + Title Case for anything we don't ship a
// canonical label for.
const _LEAD_EDIT_KEY_LABELS = {
  contact_name: "Contact name",
  phone: "Phone",
  email: "Email",
  company: "Company",
  title: "Title",
  organizational_role: "Role",
  practice_type: "Practice type",
  specialty: "Specialty",
  specialty_multi: "Specialty (multi)",
  time_zone: "Time zone",
  timezone: "Time zone",
  addresses: "Addresses",
  providers_multi: "Providers",
  practice_characteristics: "Practice characteristics",
  description: "Description",
  contacts: "Contacts",
  lead_stage: "Lead stage",
  lead_source: "Lead source",
  lead_rating: "Lead rating",
  lead_status: "Lead status",
  lead_tag: "Lead tag",
  assigned_users: "Assigned users",
  followers: "Followers",
  key_decision_makers: "Key decision makers",
  product_type: "Product type",
  products_of_interest: "Products of interest",
  feature_interest: "Feature interest",
  budget_range: "Budget range",
  budget: "Budget",
  prospect_decision_timeline: "Decision timeline",
  closing_date_expected: "Expected close",
  interest_level: "Interest level",
  engagement_level: "Engagement level",
  overall_sentiment: "Overall sentiment",
  reason_category: "Reason",
  pain_point_category: "Pain point",
  pain_points: "Pain points",
  next_action_post_demo: "Next action (post-demo)",
  next_action_owner: "Next action owner",
  next_action_due_date: "Next action due",
  notes: "Notes",
  tags: "Tags",
  lead_notes: "Lead notes",
  additional_info: "Additional info",
  lost_reason_name: "Lost reason",
};
function formatLeadEditKey(k) {
  if (_LEAD_EDIT_KEY_LABELS[k]) return _LEAD_EDIT_KEY_LABELS[k];
  return String(k).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// Render a leadEdits value safely across scalars / arrays / objects.
function formatLeadEditValue(v) {
  if (v == null || v === "") return <em style={{ color: "var(--text-3)" }}>—</em>;
  if (Array.isArray(v)) return v.length ? v.join(", ") : <em style={{ color: "var(--text-3)" }}>—</em>;
  if (typeof v === "object") {
    try { return JSON.stringify(v); } catch { return "[object]"; }
  }
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
}

// Read-only review card wrapper. Header carries an ✎ Edit button that
// calls onEdit (usually `setStep(N)`) to jump the modal back to the
// source step. State persists — the modal never unmounts across step
// changes — so returning to a card is lossless.
function ReviewCard({ num, title, subtitle, empty, emptyHint, onEdit, children }) {
  return (
    <section
      data-testid={`disp-review-card-${num}`}
      style={{
        border: "0.5px solid var(--border-soft)",
        borderRadius: 10,
        background: "#fff",
        overflow: "hidden",
      }}
    >
      <button
        onClick={onEdit}
        data-testid={`disp-review-card-${num}-edit`}
        title={`Go back to Step ${num}`}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 14px",
          background: "var(--subtle)",
          border: "none",
          borderBottom: "0.5px solid var(--border-soft)",
          cursor: "pointer",
          fontFamily: "inherit",
          textAlign: "left",
        }}
      >
        <span style={{ width: 22, height: 22, borderRadius: "50%", background: "var(--blue)", color: "#fff", fontSize: 11, fontWeight: 600, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          {num}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--navy)" }}>{title}</div>
          {subtitle && <div style={{ fontSize: 10, color: "var(--text-3)", marginTop: 1 }}>{subtitle}</div>}
        </div>
        <span style={{ fontSize: 10, color: "var(--blue)", fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 3 }}>
          ✎ Edit
        </span>
      </button>
      <div style={{ padding: "10px 14px", display: "flex", flexDirection: "column", gap: 4 }}>
        {empty ? (
          <div style={{ fontSize: 11, color: "var(--text-3)", fontStyle: "italic", padding: "4px 0" }}>
            {emptyHint || "Nothing captured here."}
          </div>
        ) : children}
      </div>
    </section>
  );
}

// Single label-value row inside a ReviewCard.
function ReviewRow({ label, value }) {
  return (
    <div style={{ display: "flex", gap: 12, fontSize: 11, lineHeight: 1.5, alignItems: "flex-start" }}>
      <div style={{ width: 140, color: "var(--text-3)", flexShrink: 0 }}>{label}</div>
      <div style={{ flex: 1, minWidth: 0, color: "var(--text-1)" }}>{value}</div>
    </div>
  );
}

// Show first 4 lines of the notes with a "Show full notes" toggle.
// Kept lightweight — inline expand, no overlay.
function ExpandableNotesPreview({ text }) {
  const [expanded, setExpanded] = useState(false);
  const lines = String(text || "").split(/\n/);
  const shouldTruncate = lines.length > 4 || String(text || "").length > 400;
  const shown = expanded || !shouldTruncate ? text : lines.slice(0, 4).join("\n");
  return (
    <div>
      <div
        data-testid="disp-review-notes-preview"
        style={{ fontSize: 11, color: "var(--text-1)", lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
      >
        {shown}
      </div>
      {shouldTruncate && (
        <button
          onClick={() => setExpanded((v) => !v)}
          data-testid="disp-review-notes-toggle"
          style={{ marginTop: 4, background: "none", border: "none", color: "var(--blue)", fontSize: 10, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", padding: 0 }}
        >
          {expanded ? "⌃ Collapse" : "⌄ Show full notes"}
        </button>
      )}
    </div>
  );
}


// Iter 241 (Aug 2026) — DispositionModal is now shared with the Leads
// page. LeadFocus.jsx imports it as `AfterCallWorkModal` so a call
// placed from a lead profile opens the SAME 3-step wizard reps see
// in the Sales Dialer, with the same super-admin-only close X and the
// same tiered sub-status picker. The function stays defined here for
// history / co-location with `onSaveDisposition`, but is exported for
// cross-page reuse. See LeadFocus.jsx `<AfterCallWorkModal>` render.
// Campaign owner being covered in backup mode (follow-up tasks default to them).
let _backupOwner = null;

export function DispositionModal({ lead, timer, notes, contactsCalled = [], onClose, onSave }) {
  // Iter 210.8 — super-admin exception. Non-super-admin reps see a
  // mandatory modal (no X close, no Skip). Super-admins retain the
  // close X so they can dismiss without saving for QA / debug work.
  const { user } = useAuth();
  const isSuperAdmin = String(user?.role || "").toLowerCase() === "super_admin";
  const dispNotesSeed = useMemo(() => notes.map((n) => `[${fmtTime(n.ts)}] ${n.body}`).join("\n"), [notes]);

  // ── Step navigation (3-step wizard) ──────────────────────────────────
  // Step 1 — Call Outcome (call type · outcome · stage · sentiment · qualifiers · next step · follow-up)
  // Step 2 — Notes + AI Auto-Map (textarea with auto-summarize on blur)
  // Step 3 — Update Lead Info (Information-tab fields via LeadContactSections)
  // Step 4 — Attachments + Follow-up tasks
  // Step 5 — Timeline review + final note
  const [step, setStep] = useState(1);

  // ── Recent outcomes panel — last 5 dispositions for this lead ────────
  // Descending by updated_at (endpoint default). Shows date · time ·
  // outcome · who logged it, so the rep has context before logging.
  const [recentOutcomes, setRecentOutcomes] = useState([]);
  const [outcomePage, setOutcomePage] = useState(0);
  useEffect(() => {
    if (!lead?.id) return;
    let cancelled = false;
    // Merge two outcome sources for this lead: call dispositions
    // (`dispositions` collection) + logged demo outcomes
    // (`demo_bookings` collection). Sorted newest-first across both.
    const dispReq = axios
      .get(`${_API}/dispositions`, { params: { lead_id: lead.id, limit: 50 }, headers: _authHeaders(), timeout: 8000 })
      .then((r) => (Array.isArray(r.data) ? r.data : []))
      .catch(() => []);
    const demoReq = axios
      .get(`${_API}/demo-bookings`, { params: { lead_id: lead.id }, headers: _authHeaders(), timeout: 8000 })
      .then((r) => (Array.isArray(r.data) ? r.data : []))
      .catch(() => []);
    Promise.all([dispReq, demoReq]).then(([disps, demos]) => {
      if (cancelled) return;
      const callRows = disps.map((d) => ({ ...d, _source: "call" }));
      const demoRows = (demos || [])
        .filter((b) => b.outcome) // only demos with a logged outcome
        .map((b) => ({
          _source: "demo",
          id: b.id,
          updated_at: b.completed_at || b.updated_at || b.created_at,
          created_at: b.completed_at || b.created_at,
          outcome: b.outcome,
          next_step: b.next_step,
          outcome_notes: b.outcome_notes,
          actor_name: b.host_name || b.organizer_name || "",
          demo_company: b.company || b.contact_name || b.name || "",
        }));
      const merged = [...callRows, ...demoRows].sort((a, b) => {
        const ta = new Date(a.updated_at || a.created_at || 0).getTime();
        const tb = new Date(b.updated_at || b.created_at || 0).getTime();
        return tb - ta;
      });
      setRecentOutcomes(merged);
      setOutcomePage(0);
    });
    return () => { cancelled = true; };
  }, [lead?.id]);
  const fmtOutcomeLabel = (o) =>
    String(o || "—")
      .replace(/[-_]+/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  const fmtOutcomeStamp = (iso) => formatEST(iso);
  const prettyOutcomeVal = (v) =>
    v === null || v === undefined || v === "" ? "—" : fmtOutcomeLabel(v);
  const outcomeInitials = (name) =>
    String(name || "?")
      .trim()
      .split(/\s+/)
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?";
  const dm = (r) =>
    r?.decision_maker_reached === true ? "Yes" : r?.decision_maker_reached === false ? "No" : "—";
  // Iter 247 — when a disposition has no outcome/outcome_detail, show the
  // union of Stage-to + Sub-status instead of a bare "—".
  const outcomeText = (r) => {
    const v = r.outcome_detail || r.outcome;
    if (v) return prettyOutcomeVal(v);
    const parts = [];
    if (r.stage_to) parts.push(fmtOutcomeLabel(r.stage_to));
    if (r.substatus) parts.push(r.substatus);
    return parts.join(" · ") || "—";
  };
  // Light tint of a tier's dot colour for its group header background.
  const hexToRgba = (hex, a) => {
    const h = String(hex || "#888").replace("#", "");
    const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
    const n = parseInt(full, 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
  };

  // Step 1 form state
  // Iter 340 (Feb 2026) — defaults revised per user request: Cold Call is
  // the natural first-touch call type, outcome + next step both start
  // empty so reps only pick a value when applicable (a leading
  // "— Select … —" option in each dropdown makes the empty state
  // render legitimately).
  const [outcome, setOutcome] = useState("");
  const [callType, setCallType] = useState("Cold call");
  const [sentiment, setSentiment] = useState("positive");
  const [stage, setStage] = useState(lead.stage);
  const [substatus, setSubstatus] = useState(lead.subStatus || "");
  const [nextStep, setNextStep] = useState("");
  const [followup, setFollowup] = useState("");
  const [decisionMakerReached, setDecisionMakerReached] = useState(true);
  const [budgetConfirmed, setBudgetConfirmed] = useState(false);
  const [recordingUrl, setRecordingUrl] = useState("");
  // Iter 340 — Products Discussed multi-select. Uses the canonical
  // ProductTagPills slugs (backend Iter 172 §3 `productTags` field
  // already accepts List[str]). No cap — reps can tag as many products
  // as they actually covered on the call. AI auto-map (Step 2) can
  // pre-fill via fieldUpdates.productTags without disturbing rep picks.
  const [productsDiscussed, setProductsDiscussed] = useState([]);

  // Pre-fill from latest RC call recording for this lead (best-effort).
  useEffect(() => {
    if (!lead?.id) return;
    let cancelled = false;
    try {
      const token = (typeof localStorage !== "undefined" && localStorage.getItem("token")) || "";
      if (!token) return;
      axios.get(`${_API}/integrations/rc/latest-recording?lead_id=${encodeURIComponent(lead.id)}`, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 5000,
      }).then((r) => {
        if (cancelled) return;
        const rec = r?.data?.recording_id;
        if (rec) {
          const base = process.env.REACT_APP_BACKEND_URL || "";
          setRecordingUrl(`${base}/api/integrations/ringcentral/recording/${rec}`);
        }
      }).catch(() => { /* silent — disposition can be saved without a recording */ });
    } catch { /* ignore */ }
    return () => { cancelled = true; };
  }, [lead?.id]);

  // Step 2 — notes + AI auto-mapper
  const [notesText, setNotesText] = useState(dispNotesSeed);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiApplied, setAiApplied] = useState({}); // { fieldUpdates, leadUpdates, appliedKeys, summary }
  const [autoRan, setAutoRan] = useState(false);
  const autoRunTimer = useRef(null);

  // Iter 192m — pull the latest RingCentral Whisper transcript for this
  // lead so the AI auto-map sees the full call (not just the rep's
  // typed bullets). Same source the Rep Activity reports use for AI
  // summaries + product tagging. Polls for up to 90s because Whisper
  // typically lands 30-90s after hangup.
  const [rcTranscript, setRcTranscript] = useState("");
  const [transcriptPending, setTranscriptPending] = useState(true);
  useEffect(() => {
    if (!lead?.id) { setTranscriptPending(false); return undefined; }
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 9; // 9 * 10s = 90s budget
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const r = await axios.get(`${_API}/integrations/rc/latest-transcript`, {
          params: { lead_id: lead.id },
          headers: _authHeaders(),
          timeout: 8000,
        });
        const t = String(r?.data?.transcript || "").trim();
        if (t) {
          if (!cancelled) {
            setRcTranscript(t);
            setTranscriptPending(false);
          }
          return; // done
        }
      } catch (_e) { /* ignore — keep polling */ }
      if (attempts >= MAX_ATTEMPTS) {
        if (!cancelled) setTranscriptPending(false);
        return;
      }
      setTimeout(poll, 10000);
    };
    setTranscriptPending(true);
    poll();
    return () => { cancelled = true; };
  }, [lead?.id]);

  // Step 3 — Lead info edits
  const prefilled = useMemo(() => prefillLeadInformation(lead), [lead]);
  const [leadEdits, setLeadEdits] = useState({});
  const mergedLead = useMemo(() => ({ ...prefilled, ...leadEdits }), [prefilled, leadEdits]);
  // Iter 210.12 — Every rep edit inside Step 3's LeadContactSections is
  // marked in touchedFieldsRef under `leadEdits.<key>` so a subsequent
  // AI Summary re-run won't overwrite it. See runAIAutoMap above.
  const onLeadFieldUpdate = useCallback((k, v) => {
    touchedFieldsRef.current.add(`leadEdits.${k}`);
    setLeadEdits((p) => ({ ...p, [k]: v }));
  }, []);

  // Step 4 — Attachments + Follow-up tasks (Iter 210.11 rebuild)
  //
  // BEFORE (through Iter 210.9): Step 4 was a mock. `ATTACHMENT_LIBRARY`
  // was 5 hardcoded stub items that pointed at nothing, `+Add task`
  // stored a local `{id,title,due,on}` snapshot never inserted into
  // `tasks`, and the "Send follow-up email" toggle fired a fake toast.
  //
  // NOW: attachments are real files (Resources library + local upload
  // → `/api/leads/{lead_id}/files`), tasks have a notes/description
  // field and get POSTed to `/api/tasks` on save (visible in the Tasks
  // tab immediately), and the email path uses the real M365 send
  // endpoint (`/api/integrations/m365/email/send`) with an AI draft
  // helper (`/api/dialer/follow-up-email`).
  //
  // Attachment shape: `{id, name, size?, content_type?, url?, kind?,
  // resource_id?, source: 'library'|'upload'}`. `resource_id` is the
  // digital_assets id when the file came from the library — used by
  // the M365 send endpoint to attach without re-uploading.
  const [attachments, setAttachments] = useState([]);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryAssets, setLibraryAssets] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [librarySearch, setLibrarySearch] = useState("");
  const fileInputRef = useRef(null);
  const [uploadingCount, setUploadingCount] = useState(0);

  // Follow-up tasks. `description` is the user's ask — a notes/context
  // area that shows on the task in the Tasks tab. `expanded` (UI-only)
  // tracks whether the details row is unfolded. On disposition save
  // each on:true task is POSTed to `/api/tasks` and the returned id
  // lands in `task_id` for audit + Call Review lightbox linkage.
  // Follow-up tasks. Iter 210.15 rewrite:
  //   • `followupTasks` now holds CREATED tasks only (each row has a
  //     real backend `id` from POST /api/tasks). No more inline
  //     editable rows — the shared CreateTaskModal owns authoring.
  //   • `aiTaskProposals` holds AI auto-mapped suggestions that
  //     haven't been created yet. Each has a "+ Create task" button
  //     that pre-fills CreateTaskModal so reps can tweak before
  //     committing. Rejected proposals are removed via ✕ Dismiss.
  //   • `createTaskOpen` / `createTaskPrefill` drive the modal
  //     overlay — clicking "+ Add task" opens it blank; clicking a
  //     proposal's "+ Create task" opens it pre-filled.
  const [followupTasks, setFollowupTasks] = useState([]);
  const [aiTaskProposals, setAiTaskProposals] = useState([]);
  const [createTaskOpen, setCreateTaskOpen] = useState(false);
  const [createTaskPrefill, setCreateTaskPrefill] = useState(null); // {title, description, priority, category, due_date} | null

  // Follow-up email composer (Phase B). Replaces the mock "Will send
  // follow-up email" toggle. Send is IMMEDIATE — clicking Send POSTs
  // to /api/integrations/m365/email/send right away, so the rep sees
  // success/failure in real time rather than a fake toast on save.
  const _defaultRecipient = useMemo(() => {
    const c = lead?.contacts?.[0];
    return c?.email || lead?.email || "";
  }, [lead]);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ to: "", cc: "", subject: "", body: "" });
  // Iter 210.14 — `emailDrafting` and `emailSending` local state were
  // dropped along with the inline composer. The shared
  // ComposeEmailPanel owns those flags internally. We still keep
  // `emailForm` in the parent as a summary (To + Subject shown on the
  // Step 5 review card and the "Email sent" banner after send).
  const [emailSentId, setEmailSentId] = useState(null); // Graph message id after send

  // Schedule demo (Phase D). Expandable inline section that opens the
  // shared ScheduleMeetingModal. Once the demo is booked into
  // demo_bookings, `scheduledDemo` holds the summary card data.
  const [demoExpanded, setDemoExpanded] = useState(false);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduledDemo, setScheduledDemo] = useState(null);
  // { id, date, time, product, host_rep_name, meeting_link }

  // Step 5 — Review & Confirm (Iter 210.12 rewrite)
  //
  // BEFORE: Step 5 was a tiny "New Timeline Entry (Preview)" card plus
  // an optional `timelineNote` textarea whose content was appended to
  // lead.lead_notes on save. Reps had no way to review the ~60
  // choices/edits from Steps 1-4 before saving.
  //
  // NOW: Step 5 is a read-only review of every Step 1-4 choice, laid
  // out as 4 cards each with an ✎ Edit shortcut that jumps back to
  // the source step (state persists — the modal never unmounts). The
  // free-text `timelineNote` textarea is REMOVED: reps already write
  // narrative notes in Step 2, which is what appends to lead.lead_notes.
  //
  // Field-touch registry — records which Step 1/2/3 fields the rep has
  // manually changed. Used by runAIAutoMap to skip fields the rep
  // explicitly edited, so re-running "AI Summary" never clobbers a
  // manual choice. Kept in a ref (not state) because ref updates don't
  // trigger renders — we only READ it inside runAIAutoMap.
  const touchedFieldsRef = useRef(new Set());
  const markTouched = useCallback((field) => {
    touchedFieldsRef.current.add(field);
  }, []);
  // Setter wrappers — every rep-driven Step 1/2 field goes through
  // these so touchedFieldsRef stays honest. AI's setters still call
  // the raw setState (bypassing markTouched) so re-running AI doesn't
  // accidentally protect its own values.
  const setStageTouched = useCallback((v) => { markTouched("stage"); setStage(v); }, [markTouched]);
  const setSubstatusTouched = useCallback((v) => { markTouched("substatus"); setSubstatus(v); }, [markTouched]);
  const setSentimentTouched = useCallback((v) => { markTouched("sentiment"); setSentiment(v); }, [markTouched]);
  const setNextStepTouched = useCallback((v) => { markTouched("nextStep"); setNextStep(v); }, [markTouched]);
  const setFollowupTouched = useCallback((v) => { markTouched("followup"); setFollowup(v); }, [markTouched]);
  const setNotesTextTouched = useCallback((v) => { markTouched("notesText"); setNotesText(v); }, [markTouched]);
  const setDecisionMakerReachedTouched = useCallback((v) => { markTouched("decisionMakerReached"); setDecisionMakerReached(v); }, [markTouched]);
  const setBudgetConfirmedTouched = useCallback((v) => { markTouched("budgetConfirmed"); setBudgetConfirmed(v); }, [markTouched]);
  // Per-contact dispositions (only when 2+ contacts spoken to)
  const multiContact = contactsCalled.length > 1;
  const [contactDispositions, setContactDispositions] = useState(() =>
    contactsCalled.map((c, i) => ({
      contact_id: c?.id || `cd-${i}`,
      name: c?.name || "Unknown",
      role: c?.role || "",
      phone: c?.phone || "",
      outcome: i === contactsCalled.length - 1 ? "Decision-maker reached" : "Gatekeeper",
      note: "",
    }))
  );
  const updateContactDisp = (idx, patch) => setContactDispositions((prev) => prev.map((d, i) => (i === idx ? { ...d, ...patch } : d)));

  const STAGES_OPT = PIPELINE_STAGES.map((s) => ({ id: s.id, label: s.label }));
  // Sub-status dropdown options follow the currently-selected stage.
  // Empty array means "no sub-status options for this stage" → fall
  // back to the free-text input (kept for stages like new_lead /
  // closed_won that have no defined sub-status list).
  const SUB_STATUS_OPT = useMemo(() => getSubStatuses(stage), [stage]);
  // Group sub-statuses by tier so we can render tier-headed button blocks
  // (Iter 248 — replaced the dropdown/tiered popover with inline buttons).
  const SUB_STATUS_GROUPS = useMemo(() => {
    const hasTier = SUB_STATUS_OPT.some((o) => o.tier);
    if (!hasTier) return [{ key: "_flat", meta: null, items: SUB_STATUS_OPT }];
    const by = {};
    SUB_STATUS_OPT.forEach((o) => {
      const k = o.tier || "no_contact";
      (by[k] = by[k] || []).push(o);
    });
    return Object.entries(by)
      .map(([key, items]) => ({ key, meta: TIER_META[key] || TIER_META.no_contact, items }))
      .sort((a, b) => (a.meta?.order || 99) - (b.meta?.order || 99));
  }, [SUB_STATUS_OPT]);
  // Iter 250 — derive the lead's stage journey from the merged Recent
  // Outcomes feed (newest-first). Used to colour-code the Stage blocks:
  // most-recent stage = darkest, oldest = lightest; badge = chronological
  // order (1 = first stage the lead was in).
  const stageJourney = useMemo(() => {
    const seenSet = new Set();
    const seen = [];
    recentOutcomes.forEach((o) => {
      const st = o._source === "demo" ? "demo" : o.stage_to;
      if (st && !seenSet.has(st)) { seenSet.add(st); seen.push(st); }
    });
    const total = seen.length;
    const map = {};
    seen.forEach((st, i) => { map[st] = { recencyRank: i, order: total - i, total }; });
    return map;
  }, [recentOutcomes]);
  // If the rep switches Stage to one whose new sub-statuses don't
  // include the previously-selected value, clear the sub-status so we
  // never persist an out-of-vocabulary string.
  useEffect(() => {
    if (!substatus) return;
    if (SUB_STATUS_OPT.length && !SUB_STATUS_OPT.some((o) => o.value === substatus)) {
      setSubstatus("");
    }
  }, [stage, SUB_STATUS_OPT, substatus]);

  // ── AI auto-map: parse free-form notes and auto-apply field updates ──
  // LLM-first via /api/ai/disposition-parse (BW-03); silently falls back to
  // the local heuristic on any LLM error so the rep is never blocked.
  const runAIAutoMap = useCallback((source) => {
    const hasNotes = notesText && notesText.trim().length >= 12;
    const hasTranscript = rcTranscript && rcTranscript.trim().length >= 12;
    if (!hasNotes && !hasTranscript) {
      toast.info("Type a few sentences in the notes first");
      return;
    }
    setAiBusy(true);
    // Strip the AI-summary block before re-parsing so we don't double-count
    // the heuristic hits from text the AI itself wrote.
    const cleanNotes = notesText.includes("— Rep notes —")
      ? notesText.split("— Rep notes —").pop().trim()
      : notesText;
    // Iter 192m — pass the RC Whisper transcript so the backend AI blends
    // it with the rep's notes (same source the rep-activity AI summary uses).
    parseDispositionNotesAI(cleanNotes, {
      apiBase: _API,
      token: (typeof localStorage !== "undefined" && localStorage.getItem("token")) || "",
      transcript: rcTranscript || undefined,
    }).then((parsed) => {
      // Iter 210.12 — respect the rep's manual edits. If the rep has
      // already changed a field (tracked via touchedFieldsRef), the AI
      // won't overwrite it. AI still fills in what the rep left blank
      // and still overrides its OWN previous values (setStage/setNotesText
      // below are the raw setters, not the touched wrappers).
      const skip = touchedFieldsRef.current;
      let keptCount = 0;
      const applyIfUntouched = (name, apply) => {
        if (skip.has(name)) { keptCount += 1; return; }
        apply();
      };

      // Auto-apply field updates
      const fu = parsed.fieldUpdates;
      if (fu.sentiment)               applyIfUntouched("sentiment", () => setSentiment(fu.sentiment));
      if (fu.stage)                   applyIfUntouched("stage", () => setStage(fu.stage));
      if (fu.substatus)               applyIfUntouched("substatus", () => setSubstatus(fu.substatus));
      if (fu.nextStep)                applyIfUntouched("nextStep", () => setNextStep(fu.nextStep));
      if (fu.followup)                applyIfUntouched("followup", () => setFollowup(fu.followup));
      if (fu.decisionMakerReached)    applyIfUntouched("decisionMakerReached", () => setDecisionMakerReached(true));
      if (fu.budgetConfirmed)         applyIfUntouched("budgetConfirmed", () => setBudgetConfirmed(true));
      // Auto-apply lead-info updates (step 3) — merge only untouched keys.
      if (Object.keys(parsed.leadUpdates).length) {
        setLeadEdits((p) => {
          const merged = { ...p };
          for (const [k, v] of Object.entries(parsed.leadUpdates)) {
            const guard = `leadEdits.${k}`;
            if (skip.has(guard)) { keptCount += 1; continue; }
            merged[k] = v;
          }
          return merged;
        });
      }
      // Replace the textarea with the structured summary (rep can still edit).
      // Guarded: if the rep manually edited the notes, keep their edit.
      if (parsed.summary) applyIfUntouched("notesText", () => setNotesText(parsed.summary));
      setAiApplied(parsed);
      if (source === "manual" && keptCount > 0) {
        toast.info(`AI Summary re-run · kept your ${keptCount} manual edit${keptCount === 1 ? "" : "s"}`);
      }

      // Iter 210.11 — the previous AI auto-map suggested attachments
      // from the 5-item stub library (`att-pricing`, `att-ccm-overview`,
      // …). Those stubs no longer exist (Step 4 now attaches real
      // files via the Resources library or local upload). We drop the
      // stub-based suggestion block entirely; rep manually picks from
      // real assets in Step 4. The AI auto-derived follow-up TASKS
      // below still fire and remain useful.
      const suggestedTasks = [];

      // Auto-derived follow-up task PROPOSALS (Iter 210.15). Previously
      // these auto-populated `followupTasks` as editable inline rows and
      // POSTed as a batch on disposition save. Now they land in
      // `aiTaskProposals` — the rep sees a compact strip with a "+ Create
      // task" button per proposal that opens the shared CreateTaskModal
      // pre-filled so they can tweak title/assignee/etc. before hitting
      // Create. Matches the "rep is always in control" pattern the rest
      // of the modal uses.
      const fuDate = parsed.fieldUpdates.followupDate || new Date(Date.now() + 2 * 86400000).toISOString().slice(0, 10);
      const contactName = lead?.contacts?.[0]?.name || lead?.contact_name || "the lead";
      const company = lead?.company || "";
      const stageCtx = parsed.fieldUpdates.stage ? ` Stage moved to ${parsed.fieldUpdates.stage}.` : "";
      if (parsed.fieldUpdates.nextStep === "Send proposal") suggestedTasks.push({
        id: "t-prop",
        title: `Send proposal to ${contactName}`,
        description: `Send tailored proposal to ${contactName}${company ? ` at ${company}` : ""} covering the products/pricing discussed on today's call.${stageCtx}`,
        due_date: fuDate, priority: "high", category: "follow_up",
      });
      if (parsed.fieldUpdates.nextStep === "Schedule demo") suggestedTasks.push({
        id: "t-demo",
        title: `Schedule demo with ${contactName}`,
        description: `Book a product demo with ${contactName}${company ? ` at ${company}` : ""}. Confirm attendees + preferred day/time in the follow-up email.`,
        due_date: fuDate, priority: "high", category: "demo",
      });
      if (parsed.fieldUpdates.stage === "demo") suggestedTasks.push({
        id: "t-prep",
        title: "Prepare demo deck + benchmark",
        description: `Prep demo assets tailored to ${company || "this prospect"}: specialty deck, benchmark slide, and any product modules called out on the call.`,
        due_date: fuDate, priority: "medium", category: "demo",
      });
      if (parsed.fieldUpdates.decisionMakerReached) suggestedTasks.push({
        id: "t-recap",
        title: "Send recap + next-step email",
        description: `Decision-maker reached. Send a recap email summarizing what we agreed, next steps, and the timeline captured today.`,
        // Iter 210.15 — was `email`; Tasks-page category set uses `outreach`
        // instead. Bring in line for parity.
        due_date: fuDate, priority: "high", category: "outreach",
      });
      if (suggestedTasks.length) setAiTaskProposals((cur) => {
        const ids = new Set(cur.map((t) => t.id));
        return [...cur, ...suggestedTasks.filter((t) => !ids.has(t.id))];
      });

      setAiBusy(false);
      const n = parsed.appliedKeys.length;
      const tag = parsed.source === "llm" ? " · Azure AI" : "";
      if (n > 0) toast.success(`✦ AI auto-mapped ${n} field${n === 1 ? "" : "s"}${source === "auto" ? " from your notes" : ""}${tag}`);
      else toast.info("AI couldn't find anything to auto-map — try richer notes");
    });
  }, [notesText, rcTranscript, lead]);

  // Iter 192m — when the RC transcript lands AFTER the modal opened (Whisper
  // is async), automatically re-run the AI auto-map so stage/substatus/
  // pain-points get filled in from the actual call audio. We only re-run
  // if the rep hasn't already manually selected a stage (so we don't
  // overwrite their choice).
  useEffect(() => {
    if (!rcTranscript || aiBusy) return;
    if (stage && stage !== "") return; // rep already picked — don't clobber
    runAIAutoMap("transcript-arrived");
  }, [rcTranscript]);

  // Auto-trigger AI parsing 1.5s after the user stops typing/changing notes
  // (only once per modal-open, debounced; rep can re-run manually).
  useEffect(() => {
    if (autoRan || aiBusy) return;
    if (!notesText || notesText.trim().length < 30) return;
    if (autoRunTimer.current) clearTimeout(autoRunTimer.current);
    autoRunTimer.current = setTimeout(() => {
      setAutoRan(true);
      runAIAutoMap("auto");
    }, 1500);
    return () => { if (autoRunTimer.current) { clearTimeout(autoRunTimer.current); autoRunTimer.current = null; } };
  }, [notesText, autoRan, aiBusy, runAIAutoMap]);
  // Hard-clear the debounce on unmount in case the rep closes the modal mid-typing.
  useEffect(() => () => { if (autoRunTimer.current) clearTimeout(autoRunTimer.current); }, []);

  // ── Save handlers ──
  //
  // Iter 210.13 (Feb 2026 · bug — disposition notes never landed on
  // the Lead page). The backend guards its `lead.lead_notes` append
  // with `if body.completed and body.notes:` (server.py:24379), but
  // the frontend never sent `completed=true`, so the guard silently
  // failed on every save. Both "Save & stop" and "Save & next call"
  // are FINAL saves from the rep's perspective — the modal closes
  // and the rep moves on — so both pass `isFinal=true` (the default).
  // A future real autosave path (if we ever build one behind the
  // Pause button) would opt out with `buildPayload(false, false)`.
  const buildPayload = (advance, isFinal = true) => ({
    outcome, callType, sentiment, stage, substatus, nextStep,
    decisionMakerReached, budgetConfirmed, recordingUrl,
    notes: notesText, followup, advance,
    completed: isFinal,
    contactDispositions,
    leadEdits, aiApplied,
    // Iter 210.11 — attachments is now a list of real files
    // (Resources library rows carry a `resource_id`; local uploads
    // carry the /api/uploads url). The disposition doc stores them
    // as a snapshot; if the rep also sent the follow-up email, the
    // real email carried the actual attachments via
    // `/api/integrations/m365/email/send`.
    attachments,
    followupTasks: followupTasks,
    // Iter 210.11 — Phase B email is now sent live via the composer,
    // so this legacy boolean is deprecated. Kept in the payload
    // (always true if the rep hit Send and it succeeded) so
    // downstream analytics that read this flag don't break.
    sendFollowupEmail: !!emailSentId,
    // New Iter 210.11 audit fields — surface what actually got created.
    emailSentMessageId: emailSentId || null,
    demoBookingId: scheduledDemo?.id || null,
    // `createdTaskIds` is stamped at save() time after tasks POST — see below.
    // Iter 210.12 — `timelineNote` (a separate free-text textarea on
    // Step 5) has been removed from the UI. Reps already write their
    // narrative in Step 2, which lands on lead.lead_notes on save. Kept
    // the payload field intentionally OMITTED here so the backend
    // treats it as absent; the /api/dispositions endpoint tolerates
    // missing/empty timelineNote (backward compat via extra="allow").
    // Iter 340 — Products Discussed multi-select. Sent to backend as
    // `productTags` (Iter 172 §3 field, unchanged). Null when empty
    // lets the backend AI tagger fall through and auto-detect from
    // the transcript.
    productTags: productsDiscussed.length ? productsDiscussed : null,
  });

  const stepLabels = ["Call outcome", "Notes & AI summary", "Update lead info", "Attachments & follow-up", "Timeline review"];
  const TOTAL_STEPS = stepLabels.length;

  return (
    <>
      {/* Iter 192j (Ioannis June 24 video) — "If they scroll accidentally,
          the modal closes." The backdrop used to fire onClose on click,
          which on trackpads/touch is easy to trigger by accident with a
          tap-and-drag scroll motion — losing the rep's entire in-flight
          disposition (notes, lead edits, attachments, AI auto-map). Now
          the backdrop is purely visual; close requires the explicit X
          button in the header (line below) or the Cancel button in the
          footer. data-testid kept stable for testing. */}
      <div data-testid="disposition-backdrop" style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.42)", zIndex: 70 }} />
      <div data-testid="disposition-modal" data-step={step} style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: 1360, maxWidth: "97vw", height: "90vh", maxHeight: "95vh", overflow: "hidden", background: "var(--paper)", borderRadius: 14, boxShadow: "0 24px 60px rgba(15,23,42,0.24)", zIndex: 80, display: "flex", flexDirection: "column" }}>

        {/* ── Header + step indicator ── */}
        <div style={{ padding: "16px 22px", borderBottom: "0.5px solid var(--border-soft)", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="eyebrow">After-call work · <span className="num">{fmtTime(timer)}</span></div>
              <div style={{ fontFamily: "var(--font-head)", fontSize: 18, fontWeight: 700, color: "var(--navy)", marginTop: 4 }}>Log call · {lead.clinic}</div>
            </div>
            {/* Iter 245 — prominent header Save (top-right), available on
                every step. Bigger size + font than the footer Save. Same
                save+close contract; Stage stays mandatory. */}
            <button
              data-testid="disp-save-top"
              onClick={async () => {
                if (!stage) return;
                const ok = await onSave(buildPayload(false));
                if (ok !== false) onClose?.();
              }}
              disabled={!stage}
              title={!stage ? "Stage is required — pick one on Step 1" : "Save this disposition now"}
              className="btn btn-primary"
              style={{ fontSize: 15, fontWeight: 700, padding: "10px 22px", borderRadius: 10, display: "inline-flex", alignItems: "center", gap: 7, flexShrink: 0, ...(!stage ? { opacity: 0.5, cursor: "not-allowed" } : {}) }}
            >
              <FileText style={{ width: 16, height: 16 }} /> Save
            </button>
            {/* Iter 210.7 · Mandatory disposition. Iter 210.8 · Super-admin
                exception — super_admin users retain the close X so they
                can dismiss without saving (needed for QA / debug work).
                Everyone else must Complete or Pause. */}
            {isSuperAdmin && (
              <button
                onClick={onClose}
                data-testid="disp-close-superadmin"
                title="Close (super-admin only)"
                style={{ background: "none", border: "none", color: "var(--text-3)", cursor: "pointer" }}
              >
                <X style={{ width: 16, height: 16 }} />
              </button>
            )}
          </div>
          <div data-testid="disposition-stepper" style={{ display: "flex", gap: 6, marginTop: 14 }}>
            {stepLabels.map((lbl, i) => {
              const n = i + 1;
              const done = step > n;
              const active = step === n;
              return (
                <button
                  key={lbl}
                  type="button"
                  data-testid={`disp-step-${n}`}
                  onClick={() => setStep(n)}
                  style={{
                    flex: 1, padding: "6px 8px", borderRadius: 7, textAlign: "left", cursor: "pointer", fontFamily: "inherit", minWidth: 0,
                    border: active ? "1px solid var(--blue)" : "0.5px solid var(--border-soft)",
                    background: active ? "var(--blue-soft)" : done ? "#E9F8F1" : "var(--subtle)",
                  }}
                >
                  <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: "0.06em", color: active ? "var(--blue)" : done ? "#1D9E75" : "var(--text-3)" }}>
                    {n}{done ? " · DONE" : active ? " · CURRENT" : ""}
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 500, color: active ? "var(--navy)" : "var(--text-2)", marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{lbl}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Body — switches per step ── */}
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "20px 22px" }}>

          {step === 1 && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 400px", gap: 20, alignItems: "start" }}>
            <div data-testid="disp-body-step1" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {/* Iter 210.8 (Feb 2026 · AriaOne "unify after-call-work"
                  simplification). REMOVED FIELDS on this step:
                    · Call Type       — served no downstream KPI; only
                                        appeared in success toast text.
                    · Outcome         — deleted from UI; backend now
                                        derives the report-side outcome
                                        from Sub-Status (see
                                        server.py:24001 mapping added
                                        this iteration). Rep Activity
                                        Report continues to show a
                                        populated Outcome column.
                    · Recording       — auto-populated by RC webhook;
                                        reps never edited it.
                    · Qualifiers      — Decision-Maker Reached is now
                                        derived from sub-status role
                                        tier (already automatic in
                                        server.py:24111 via
                                        is_decision_maker). DM Contact
                                        Rate KPI + DM Hunter leaderboard
                                        unaffected. Budget Confirmed
                                        was unused downstream.
                  RETAINED as optional:
                    · Next Step, Follow-up (usefulness under review) */}
              <Field label="Stage" required>
                <div data-testid="disp-stage" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 8 }}>
                  {STAGES_OPT.map((s) => {
                    const active = stage === s.id;
                    const j = stageJourney[s.id];
                    const visited = !!j;
                    // 1 = most recent … 0 = oldest, for the tint intensity.
                    const recencyT = j && j.total > 1 ? 1 - j.recencyRank / (j.total - 1) : 1;
                    const tintA = 0.14 + recencyT * 0.34;
                    let bg = "var(--paper)", border = "0.5px solid var(--border)", color = "var(--text-2)";
                    if (active) { bg = "var(--navy)"; border = "1.5px solid var(--navy)"; color = "#fff"; }
                    else if (visited) { bg = hexToRgba("#10B981", tintA); border = `0.5px solid ${hexToRgba("#10B981", 0.5)}`; color = "#065F46"; }
                    return (
                      <button
                        key={s.id}
                        type="button"
                        data-testid={`disp-stage-${s.id}`}
                        onClick={() => setStageTouched(s.id)}
                        style={{ position: "relative", padding: "11px 12px", borderRadius: 10, textAlign: "left", cursor: "pointer", fontSize: 13, fontWeight: active || visited ? 700 : 500, fontFamily: "inherit", border, background: bg, color, transition: "background-color 120ms, border-color 120ms, color 120ms" }}
                      >
                        {s.label}
                        {visited && (
                          <span
                            title={`Stage ${j.order} of ${j.total} in this lead's journey`}
                            style={{ position: "absolute", top: -7, right: -7, minWidth: 18, height: 18, padding: "0 5px", borderRadius: 999, background: active ? "#fff" : "#10B981", color: active ? "var(--navy)" : "#fff", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 1px 3px rgba(0,0,0,0.25)" }}
                          >
                            {j.order}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
                {Object.keys(stageJourney).length > 0 && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, fontSize: 10, color: "var(--text-4)" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 3, background: hexToRgba("#10B981", 0.48) }} /> recent
                      <span style={{ width: 10, height: 10, borderRadius: 3, background: hexToRgba("#10B981", 0.16), marginLeft: 6 }} /> older
                    </span>
                    <span>· badge = order this lead moved through ({Object.keys(stageJourney).length} stage{Object.keys(stageJourney).length > 1 ? "s" : ""})</span>
                  </div>
                )}
              </Field>
              {SUB_STATUS_OPT.length > 0 && (
              <Field label="Sub-status">
                  {/* Iter 248 — inline tier-headed button blocks (replaced the
                      dropdown/tiered popover). Sub-status + sub-sub-status
                      (e.g. Demo Completed → outcomes) come alive as buttons
                      grouped by tier the moment a Stage is picked. Iter 249 —
                      the whole field is hidden for stages with no sub-statuses
                      (New Leads, Closed Won) instead of a free-text box. */}
                  <div data-testid="disp-substatus" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {SUB_STATUS_GROUPS.map((g) => {
                      const accent = g.meta ? g.meta.dot : "var(--navy)";
                      const buttons = (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {g.items.map((o) => {
                            const active = substatus === o.value;
                            return (
                              <button
                                key={o.value}
                                type="button"
                                data-testid={`disp-substatus-${o.value.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}
                                onClick={() => setSubstatusTouched(o.value)}
                                style={{ padding: "6px 12px", borderRadius: 999, fontSize: 12, fontWeight: active ? 700 : 500, fontFamily: "inherit", cursor: "pointer", border: active ? `1.5px solid ${accent}` : "0.5px solid var(--border)", background: active ? accent : "var(--paper)", color: active ? "#fff" : "var(--text-2)", transition: "background-color 120ms, border-color 120ms, color 120ms" }}
                              >
                                {o.label}
                              </button>
                            );
                          })}
                        </div>
                      );
                      // Flat (untiered) stage — plain chip row, no card.
                      if (!g.meta) return <div key={g.key}>{buttons}</div>;
                      // Tiered stage — each sub-status group is a highlighted
                      // card: accent left border + tinted header bar, with its
                      // sub-sub-status chips inside.
                      const hasSelected = g.items.some((o) => o.value === substatus);
                      return (
                        <div key={g.key} style={{ border: `0.5px solid ${hasSelected ? accent : "var(--border-soft)"}`, borderLeft: `3px solid ${accent}`, borderRadius: 10, overflow: "hidden", transition: "border-color 120ms" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: hexToRgba(g.meta.dot, 0.1) }}>
                            <span aria-hidden style={{ width: 8, height: 8, borderRadius: "50%", background: accent, flexShrink: 0 }} />
                            <span style={{ fontSize: 12, fontWeight: 700, color: accent, letterSpacing: "0.02em" }}>{g.meta.label}</span>
                            <span style={{ marginLeft: "auto", fontSize: 10, fontWeight: 700, color: accent, opacity: 0.65 }}>{g.items.length}</span>
                          </div>
                          <div style={{ padding: "10px 12px" }}>{buttons}</div>
                        </div>
                      );
                    })}
                  </div>
              </Field>
              )}
              <Field label="Sentiment">
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {DISP_SENTIMENTS.map((s) => (
                    <button key={s.id} data-testid={`disp-sentiment-${s.id}`} onClick={() => setSentimentTouched(s.id)} style={{ padding: "4px 10px", borderRadius: 999, fontSize: 11, fontWeight: 500, border: sentiment === s.id ? `0.5px solid ${s.color}` : "0.5px solid var(--border)", background: sentiment === s.id ? s.color : "transparent", color: sentiment === s.id ? "#fff" : "var(--text-3)", cursor: "pointer", fontFamily: "inherit" }}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="Next step">
                <select data-testid="disp-next-step" className="select" value={nextStep} onChange={(e) => setNextStepTouched(e.target.value)}>
                  <option value="">— Select next step —</option>
                  {DISP_NEXT_STEPS.map((o) => <option key={o}>{o}</option>)}
                </select>
              </Field>
              <Field label="Follow-up">
                <input data-testid="disp-followup" type="datetime-local" className="input" value={followup} onChange={(e) => setFollowupTouched(e.target.value)} />
              </Field>
              {/* Iter 210.8 — Recording + Qualifiers fields removed. See
                  header block above for rationale (auto-populated by RC
                  webhook / DM-reached derived from sub-status tier). */}
              {/* Iter 340 — Products Discussed multi-select. Backed by
                  the canonical `productTags` field (Iter 172 §3). No
                  hard cap — tag every product the rep actually
                  discussed. AI still auto-detects from the transcript
                  if the rep leaves it blank. */}
              <Field label={`Products discussed · ${productsDiscussed.length ? `${productsDiscussed.length} tagged` : "optional"}`} full>
                <div
                  data-testid="disp-products-discussed"
                  style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "4px 0" }}
                >
                  {PRODUCT_TAG_OPTIONS.map((o) => {
                    const active = productsDiscussed.includes(o.slug);
                    return (
                      <button
                        key={o.slug}
                        type="button"
                        data-testid={`disp-product-${o.slug}`}
                        onClick={() => {
                          setProductsDiscussed((prev) =>
                            prev.includes(o.slug)
                              ? prev.filter((t) => t !== o.slug)
                              : [...prev, o.slug]
                          );
                        }}
                        style={{
                          padding: "4px 10px",
                          borderRadius: 14,
                          fontSize: 11,
                          cursor: "pointer",
                          fontFamily: "inherit",
                          border: active ? "1px solid #1D4F91" : "0.5px solid var(--border)",
                          background: active ? "#EEF6FF" : "transparent",
                          color: active ? "#1D4F91" : "var(--text-2)",
                          fontWeight: active ? 600 : 400,
                          transition: "all 120ms ease",
                        }}
                      >
                        {o.label}
                      </button>
                    );
                  })}
                </div>
              </Field>
              {multiContact && (
                <Field label={`Per-contact outcomes · ${contactDispositions.length} contacts spoken to`} full>
                  <div data-testid="disp-per-contact" style={{ display: "flex", flexDirection: "column", gap: 8, border: "0.5px solid var(--border-soft)", borderRadius: 8, padding: 10, background: "var(--subtle)" }}>
                    {contactDispositions.map((d, i) => (
                      <div key={d.contact_id} data-testid={`disp-contact-${i}`}
                        style={{ display: "grid", gridTemplateColumns: "minmax(0,1.1fr) minmax(0,1fr) minmax(0,1.6fr)", gap: 8, alignItems: "center", padding: "6px 8px", background: "var(--paper)", borderRadius: 6 }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--navy)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.name}</div>
                          <div style={{ fontSize: 10, color: "var(--text-3)" }}>{d.role || "—"}{d.phone ? ` · ${d.phone}` : ""}</div>
                        </div>
                        <select
                          data-testid={`disp-contact-outcome-${i}`}
                          className="select"
                          value={d.outcome}
                          onChange={(e) => updateContactDisp(i, { outcome: e.target.value })}
                          style={{ fontSize: 11, padding: "4px 8px" }}>
                          {DISP_OUTCOMES.map((o) => <option key={o}>{o}</option>)}
                        </select>
                        <input
                          data-testid={`disp-contact-note-${i}`}
                          className="input"
                          placeholder="One-line note (e.g. promised to call back Tue)"
                          value={d.note}
                          onChange={(e) => updateContactDisp(i, { note: e.target.value })}
                          style={{ fontSize: 11, padding: "5px 8px" }} />
                      </div>
                    ))}
                  </div>
                </Field>
              )}
            </div>
            {/* ── Right-side Recent Outcomes panel (Step 1) ── */}
            <div
              data-testid="disp-recent-outcomes"
              style={{ border: "0.5px solid var(--border-soft)", borderRadius: 10, background: "var(--subtle)", display: "flex", flexDirection: "column", overflow: "hidden", position: "sticky", top: 0 }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "8px 12px", borderBottom: "0.5px solid var(--border-soft)" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 9, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--text-3)" }}>
                  <Clock style={{ width: 12, height: 12 }} /> Recent outcomes
                </span>
                {recentOutcomes.length > 5 && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <button
                      type="button"
                      data-testid="disp-recent-prev"
                      disabled={outcomePage === 0}
                      onClick={() => setOutcomePage((p) => Math.max(0, p - 1))}
                      style={{ background: "var(--paper)", border: "0.5px solid var(--border-soft)", borderRadius: 6, padding: "2px 6px", cursor: outcomePage === 0 ? "not-allowed" : "pointer", opacity: outcomePage === 0 ? 0.35 : 1, display: "inline-flex", alignItems: "center" }}
                      title="Newer"
                    >
                      <ChevronLeft style={{ width: 13, height: 13 }} />
                    </button>
                    <span style={{ fontSize: 10, color: "var(--text-3)", fontVariantNumeric: "tabular-nums", minWidth: 30, textAlign: "center" }}>
                      {outcomePage + 1} / {Math.ceil(recentOutcomes.length / 5)}
                    </span>
                    <button
                      type="button"
                      data-testid="disp-recent-next"
                      disabled={(outcomePage + 1) * 5 >= recentOutcomes.length}
                      onClick={() => setOutcomePage((p) => ((p + 1) * 5 < recentOutcomes.length ? p + 1 : p))}
                      style={{ background: "var(--paper)", border: "0.5px solid var(--border-soft)", borderRadius: 6, padding: "2px 6px", cursor: (outcomePage + 1) * 5 >= recentOutcomes.length ? "not-allowed" : "pointer", opacity: (outcomePage + 1) * 5 >= recentOutcomes.length ? 0.35 : 1, display: "inline-flex", alignItems: "center" }}
                      title="Older"
                    >
                      <ChevronRight style={{ width: 13, height: 13 }} />
                    </button>
                  </span>
                )}
              </div>
              {recentOutcomes.length === 0 ? (
                <div data-testid="disp-recent-empty" style={{ padding: "22px 12px", fontSize: 11, color: "var(--text-4)", textAlign: "center" }}>
                  No outcomes logged yet.
                </div>
              ) : (
                <>
                  <div style={{ flex: 1 }}>
                    {recentOutcomes.slice(outcomePage * 5, outcomePage * 5 + 5).map((o, i) => {
                      const gIdx = outcomePage * 5 + i;
                      const stamp = fmtOutcomeStamp(o.updated_at || o.created_at);
                      // ── Demo-outcome row (from demo_bookings) ──
                      if (o._source === "demo") {
                        return (
                          <div
                            key={o.id || gIdx}
                            data-testid={`disp-recent-outcome-row-${gIdx}`}
                            style={{ padding: "12px 14px", borderTop: i === 0 ? "none" : "0.5px solid var(--border-soft)" }}
                          >
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 7, minWidth: 0 }}>
                                <span style={{ width: 22, height: 22, borderRadius: "50%", background: "#7C3AED", color: "#fff", fontSize: 9, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                  {outcomeInitials(o.actor_name || "Demo")}
                                </span>
                                <span style={{ fontSize: 12, fontWeight: 600, color: "var(--navy)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {o.actor_name || o.demo_company || "Demo"}
                                </span>
                              </span>
                              <span style={{ fontSize: 10, color: "var(--text-3)", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", flexShrink: 0 }}>
                                {stamp.d} · {stamp.t}
                              </span>
                            </div>
                            <div style={{ marginBottom: 8 }}>
                              <span style={{ padding: "1px 8px", borderRadius: 999, background: "#F3E8FF", color: "#7C3AED", fontWeight: 700, fontSize: 9, letterSpacing: "0.05em", textTransform: "uppercase" }}>Demo</span>
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "96px 1fr", rowGap: 6, columnGap: 10, fontSize: 11 }}>
                              <span style={{ color: "var(--text-4)", fontWeight: 500 }}>Outcome</span>
                              <span style={{ color: "var(--text-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{prettyOutcomeVal(o.outcome)}</span>
                              <span style={{ color: "var(--text-4)", fontWeight: 500 }}>Next step</span>
                              <span style={{ color: "var(--text-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{prettyOutcomeVal(o.next_step)}</span>
                              {o.outcome_notes ? (
                                <>
                                  <span style={{ color: "var(--text-4)", fontWeight: 500 }}>Notes</span>
                                  <span style={{ color: "var(--text-2)", whiteSpace: "normal" }}>{o.outcome_notes}</span>
                                </>
                              ) : null}
                            </div>
                          </div>
                        );
                      }
                      // ── Call-disposition row (from dispositions) ──
                      // Compare against the chronologically previous (older)
                      // CALL disposition — rows are sorted newest-first, so the
                      // predecessor is the NEXT index in the full array. We only
                      // diff against another call row (demo rows have no
                      // comparable fields).
                      const rawOf = (r) => (r ? {
                        "Outcome": outcomeText(r),
                        "Call type": r.call_type || "",
                        "Sentiment": r.sentiment || "",
                        "Stage to": r.stage_to || "",
                        "Stage from": r.stage_from || "",
                        "Sub-status": r.substatus || "",
                        "Next step": r.next_step || "",
                        "Decision maker": r.decision_maker_reached === true ? "Yes" : r.decision_maker_reached === false ? "No" : "",
                      } : null);
                      const curRaw = rawOf(o);
                      const _prevRow = recentOutcomes[gIdx + 1];
                      const prevRaw = _prevRow && _prevRow._source !== "demo" ? rawOf(_prevRow) : null;
                      const changedCount = prevRaw
                        ? Object.keys(curRaw).filter((k) => String(curRaw[k]) !== String(prevRaw[k])).length
                        : 0;
                      const fields = [
                        ["Outcome", outcomeText(o)],
                        ["Call type", prettyOutcomeVal(o.call_type)],
                        ["Sentiment", prettyOutcomeVal(o.sentiment)],
                        ["Stage to", prettyOutcomeVal(o.stage_to)],
                        ["Stage from", prettyOutcomeVal(o.stage_from)],
                        ["Sub-status", prettyOutcomeVal(o.substatus)],
                        ["Next step", prettyOutcomeVal(o.next_step)],
                        ["Decision maker", dm(o)],
                      ];
                      return (
                        <div
                          key={o.id || gIdx}
                          data-testid={`disp-recent-outcome-row-${gIdx}`}
                          style={{ padding: "12px 14px", borderTop: i === 0 ? "none" : "0.5px solid var(--border-soft)" }}
                        >
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 9 }}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 7, minWidth: 0 }}>
                              <span style={{ width: 22, height: 22, borderRadius: "50%", background: "var(--navy)", color: "#fff", fontSize: 9, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                {outcomeInitials(o.actor_name || o.actor_role)}
                              </span>
                              <span style={{ fontSize: 12, fontWeight: 600, color: "var(--navy)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {o.actor_name || o.actor_role || "—"}
                              </span>
                            </span>
                            <span style={{ fontSize: 10, color: "var(--text-3)", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", flexShrink: 0 }}>
                              {stamp.d} · {stamp.t}
                            </span>
                          </div>
                          {/* Change summary vs the previous outcome */}
                          {prevRaw && (
                            <div data-testid={`disp-recent-changed-${gIdx}`} style={{ marginBottom: 8, fontSize: 9, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: changedCount > 0 ? "#B45309" : "var(--text-4)" }}>
                              {changedCount > 0 ? `▲ ${changedCount} change${changedCount > 1 ? "s" : ""} vs previous` : "No change vs previous"}
                            </div>
                          )}
                          <div style={{ display: "grid", gridTemplateColumns: "96px 1fr", rowGap: 6, columnGap: 10, fontSize: 11 }}>
                            {fields.map(([k, v]) => {
                              const changed = prevRaw && String(curRaw[k]) !== String(prevRaw[k]);
                              return (
                                <React.Fragment key={k}>
                                  <span style={{ color: "var(--text-4)", fontWeight: 500 }}>{k}</span>
                                  <span
                                    title={changed ? `Changed from "${prettyOutcomeVal(prevRaw[k])}"` : (typeof v === "string" ? v : "")}
                                    style={{ color: changed ? "#B45309" : "var(--text-2)", fontWeight: changed ? 700 : 400, background: changed ? "rgba(180,83,9,0.10)" : "transparent", borderRadius: 4, padding: changed ? "0 5px" : 0, margin: changed ? "0 -5px" : 0 }}
                                  >
                                    {v}
                                    {changed && prevRaw[k] ? (
                                      <span style={{ color: "var(--text-4)", fontWeight: 400, marginLeft: 5, fontSize: 10 }}>· was {prettyOutcomeVal(prevRaw[k])}</span>
                                    ) : null}
                                  </span>
                                </React.Fragment>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="disp-body-step2" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  {/* Iter 210.9 (Feb 2026 · AriaOne "call notes → lead notes"
                      unification). Renamed the section from "CALL NOTES"
                      to "LEAD NOTES" to reflect the new backend behavior:
                      on save, the disposition note is appended (with
                      timestamp + author) to `lead.lead_notes`, which is
                      what the Lead page → Information → Next Actions &
                      Ownership → Lead Notes history renders. Reps get
                      one source of truth for lead-level notes across
                      every capture surface. */}
                  <div className="eyebrow">LEAD NOTES</div>
                  <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>
                    Timestamped + attributed to you. Also appears on the Lead page under Next Actions &amp; Ownership → Lead Notes. AI auto-summarizes + maps fields ~1.5s after you stop typing.
                  </div>
                </div>
                <button data-testid="disp-ai-summary" onClick={() => runAIAutoMap("manual")} disabled={aiBusy} className="btn btn-accent" style={{ padding: "6px 12px", fontSize: 11 }}>
                  {aiBusy ? <Loader2 className="animate-spin" style={{ width: 11, height: 11 }} /> : <Sparkles style={{ width: 11, height: 11 }} />}
                  {aiBusy ? "Summarising…" : "Re-run AI auto-map"}
                </button>
              </div>

              {/* Iter 192m — show a tiny pending pill while we poll for the
                  RingCentral Whisper transcript. When it lands, the modal
                  auto-re-runs the AI map so stage/substatus/pain-points
                  get populated from the actual call audio. */}
              {transcriptPending && (
                <div
                  data-testid="disp-transcript-pending"
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "5px 10px", borderRadius: 999,
                    background: "linear-gradient(135deg, rgba(99,102,241,0.10) 0%, rgba(59,130,246,0.10) 100%)",
                    border: "1px solid rgba(99,102,241,0.30)",
                    color: "var(--text-2)", fontSize: 11, fontWeight: 500,
                    alignSelf: "flex-start",
                  }}
                >
                  <Loader2 className="animate-spin" style={{ width: 11, height: 11, color: "rgb(99,102,241)" }} />
                  <span>✦ AI summary pending transcript… (Whisper takes 30–90s after hangup)</span>
                </div>
              )}
              {rcTranscript && !transcriptPending && (
                <div
                  data-testid="disp-transcript-ready"
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "5px 10px", borderRadius: 999,
                    background: "rgba(16,185,129,0.10)",
                    border: "1px solid rgba(16,185,129,0.30)",
                    color: "rgb(5,150,105)", fontSize: 11, fontWeight: 500,
                    alignSelf: "flex-start",
                  }}
                >
                  <Sparkles style={{ width: 11, height: 11 }} />
                  <span>Transcript loaded · AI mapped from call audio + your notes</span>
                </div>
              )}

              <textarea
                data-testid="disp-notes"
                rows={10}
                className="input"
                value={notesText}
                onChange={(e) => { setNotesTextTouched(e.target.value); setAutoRan(false); }}
                placeholder="Spoke with Dr. Reynolds — 600 Medicare patients. Interested in CCM. Demo scheduled Friday 10am. Budget approved, decision-maker reached…"
                style={{ resize: "vertical", fontSize: 12, fontFamily: "inherit", minHeight: 180, lineHeight: 1.55 }}
              />

              {aiBusy && (
                <div data-testid="disp-ai-busy" style={{ padding: "10px 14px", background: "#EEF2FF", borderRadius: 10, border: "0.5px solid #C8D5FF", display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#3F5BFF" }}>
                  <Loader2 className="animate-spin" style={{ width: 14, height: 14 }} />
                  Reading the notes, summarizing the call, and mapping to fields…
                </div>
              )}

              {!aiBusy && aiApplied?.appliedKeys?.length > 0 && (
                <div data-testid="disp-ai-applied" style={{ padding: "12px 14px", background: "#EEF2FF", borderRadius: 10, border: "0.5px solid #C8D5FF" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                    <Sparkles style={{ width: 12, height: 12, color: "#3F5BFF" }} />
                    <span style={{ fontSize: 10, fontWeight: 700, color: "#3F5BFF", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                      AI auto-applied {aiApplied.appliedKeys.length} field{aiApplied.appliedKeys.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {[...new Set(aiApplied.appliedKeys)].map((k) => (
                      <span key={k} data-testid={`disp-ai-applied-${k}`} style={{
                        padding: "3px 8px", background: "#fff", borderRadius: 999,
                        fontSize: 10, fontWeight: 500, color: "#3F5BFF",
                        border: "0.5px solid #C8D5FF",
                      }}>
                        {APPLIED_LABELS[k] || k}
                      </span>
                    ))}
                  </div>
                  <div style={{ fontSize: 10, color: "var(--text-3)", marginTop: 8 }}>
                    Review the auto-applied values in Step 1 (form) and Step 3 (lead info) before saving. Attachments + follow-up tasks are suggested in Step 4. The original notes are preserved at the bottom of the textarea.
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div data-testid="disp-body-step3">
              <div style={{ padding: "10px 14px", background: "#FFF7E0", border: "0.5px solid #FFE4A1", borderRadius: 10, marginBottom: 14, display: "flex", alignItems: "flex-start", gap: 8 }}>
                <UserIcon style={{ width: 14, height: 14, color: "#A6660B", marginTop: 2 }} />
                <div style={{ fontSize: 12, color: "#7A4F08", lineHeight: 1.5 }}>
                  <strong>Update lead information before saving.</strong>{" "}
                  AI pre-filled {Object.keys(leadEdits).length} field{Object.keys(leadEdits).length === 1 ? "" : "s"} from your notes — review and correct as needed.
                  Mission-critical fields are highlighted.
                </div>
              </div>
              <LeadContactSections
                lead={mergedLead}
                onFieldUpdate={onLeadFieldUpdate}
                userRole="sales_manager"
                currentUser={user}
                missionCriticalKeys={["lead_stage", "lead_source", "lead_rating", "specialty", "pain_point_category", "products_of_interest", "budget_range", "prospect_decision_timeline"]}
                highlightMissing={false}
              />
            </div>
          )}

          {step === 4 && (
            <div data-testid="disp-body-step4" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              {/* ═══════ ATTACHMENTS (Iter 210.11 · Phase A) ═══════
                  Was: 5 hardcoded stub items ("att-pricing", etc.) that
                  pointed at nothing. Now: real files — pick from the
                  Resources library OR upload from computer. Each chip
                  has a × to remove. */}
              <section>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  <div>
                    <div className="eyebrow">ATTACHMENTS · {attachments.length} attached</div>
                    <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>
                      Attach real files from your library or your computer.
                      They&apos;ll be recorded on this disposition and included on any follow-up email you send below.
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button
                      data-testid="disp-attach-from-library"
                      onClick={async () => {
                        setLibraryOpen((v) => !v);
                        if (!libraryOpen && libraryAssets.length === 0) {
                          setLibraryLoading(true);
                          try {
                            const r = await axios.get(`${_API}/assets`, { headers: _authHeaders(), timeout: 8000 });
                            setLibraryAssets(Array.isArray(r.data) ? r.data : []);
                          } catch {
                            toast.error("Couldn't load Resources library");
                            setLibraryAssets([]);
                          } finally {
                            setLibraryLoading(false);
                          }
                        }
                      }}
                      style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 10px", borderRadius: 6, fontSize: 11, fontWeight: 500, fontFamily: "inherit", cursor: "pointer", border: "0.5px solid var(--border)", background: libraryOpen ? "var(--blue-soft)" : "#fff", color: libraryOpen ? "var(--blue)" : "var(--text-2)" }}
                    >
                      <Paperclip style={{ width: 11, height: 11 }} /> Resources
                    </button>
                    <button
                      data-testid="disp-attach-upload"
                      onClick={() => fileInputRef.current?.click()}
                      style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 10px", borderRadius: 6, fontSize: 11, fontWeight: 500, fontFamily: "inherit", cursor: "pointer", border: "0.5px solid var(--border)", background: "#fff", color: "var(--text-2)" }}
                    >
                      <Upload style={{ width: 11, height: 11 }} /> Upload
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      style={{ display: "none" }}
                      data-testid="disp-attach-file-input"
                      onChange={async (e) => {
                        const files = Array.from(e.target.files || []);
                        e.target.value = ""; // reset so same file can be re-selected later
                        if (!files.length) return;
                        const currentLead = lead;
                        if (!_isPersistableLeadId(currentLead?.id)) {
                          // Demo/unsaved lead — attach locally without persistence.
                          const staged = files.map((f) => ({
                            id: `local-${Date.now()}-${f.name}`,
                            name: f.name,
                            size: f.size,
                            content_type: f.type || "application/octet-stream",
                            source: "upload",
                            local_only: true,
                          }));
                          setAttachments((cur) => [...cur, ...staged]);
                          toast.info(`${files.length} file${files.length === 1 ? "" : "s"} staged (demo lead — not uploaded)`);
                          return;
                        }
                        setUploadingCount((n) => n + files.length);
                        for (const f of files) {
                          // Iter 210.11 · Phase B fix (testing agent 212.M2):
                          // read the file as base64 UP FRONT so we can
                          // pipe it as an inline attachment on the m365
                          // email send below. Uploaded lead files are
                          // stored in `lead.files` (object storage) and
                          // are NOT registered in `digital_assets`, so
                          // passing `resource_id` for them silently
                          // resolves to nothing. Stashing `data_b64`
                          // lets the send handler include them as
                          // inline `attachments` (same shape the m365
                          // endpoint already accepts alongside
                          // resource_ids). Reading happens once at
                          // upload time so there's no re-read during
                          // send.
                          let data_b64 = null;
                          try {
                            data_b64 = await new Promise((resolve, reject) => {
                              const rd = new FileReader();
                              rd.onload = () => {
                                const s = String(rd.result || "");
                                const comma = s.indexOf(",");
                                resolve(comma >= 0 ? s.slice(comma + 1) : s);
                              };
                              rd.onerror = () => reject(rd.error);
                              rd.readAsDataURL(f);
                            });
                          } catch (err) {
                            console.warn("[disp-attach] base64 read failed", f.name, err);
                          }
                          const fd = new FormData();
                          fd.append("file", f);
                          try {
                            const r = await axios.post(`${_API}/leads/${currentLead.id}/files`, fd, {
                              headers: { ..._authHeaders(), "Content-Type": "multipart/form-data" },
                              timeout: 60000,
                            });
                            const saved = r?.data || {};
                            const downloadUrl = saved.url
                              || (saved.id ? `${_API}/leads/${currentLead.id}/files/${saved.id}/download` : "")
                              || saved.storage_path
                              || "";
                            setAttachments((cur) => [...cur, {
                              id: saved.id || `up-${Date.now()}-${f.name}`,
                              name: saved.name || f.name,
                              size: saved.size || f.size,
                              content_type: saved.content_type || f.type,
                              url: downloadUrl,
                              // NB: no `resource_id` — lead-uploaded files
                              // aren't digital_assets, so we send them
                              // as inline base64 instead (see email send
                              // handler below).
                              data_b64,
                              source: "upload",
                            }]);
                          } catch {
                            toast.error(`Failed to upload ${f.name}`);
                          } finally {
                            setUploadingCount((n) => Math.max(0, n - 1));
                          }
                        }
                      }}
                    />
                  </div>
                </div>

                {/* Selected-attachment chips (empty state message when 0) */}
                {attachments.length === 0 && !uploadingCount ? (
                  <div data-testid="disp-attach-empty" style={{ padding: "14px 16px", textAlign: "center", fontSize: 11, color: "var(--text-3)", border: "0.5px dashed var(--border)", borderRadius: 8 }}>
                    No attachments yet. Click Resources or Upload above.
                  </div>
                ) : (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {attachments.map((a, i) => (
                      <div key={a.id} data-testid={`disp-attach-chip-${i}`} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 8px 5px 10px", background: "var(--blue-soft)", border: "0.5px solid var(--blue)", borderRadius: 999, fontSize: 11, color: "var(--navy)" }}>
                        <FileText style={{ width: 11, height: 11, color: "var(--blue)" }} />
                        <span style={{ maxWidth: 220, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.name}</span>
                        <button
                          data-testid={`disp-attach-remove-${i}`}
                          onClick={() => setAttachments((cur) => cur.filter((_, j) => j !== i))}
                          title="Remove"
                          style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center", color: "var(--text-3)" }}
                        >
                          <X style={{ width: 11, height: 11 }} />
                        </button>
                      </div>
                    ))}
                    {uploadingCount > 0 && (
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px", background: "#F1F5F9", border: "0.5px dashed var(--border)", borderRadius: 999, fontSize: 11, color: "var(--text-3)" }}>
                        <Loader2 style={{ width: 11, height: 11, animation: "spin 1s linear infinite" }} />
                        Uploading {uploadingCount}…
                      </div>
                    )}
                  </div>
                )}

                {/* Resources library picker (inline expandable) */}
                {libraryOpen && (
                  <div data-testid="disp-library-panel" style={{ marginTop: 10, border: "0.5px solid var(--border)", borderRadius: 10, background: "var(--subtle)", padding: 10 }}>
                    <input
                      className="input"
                      placeholder="Search library…"
                      value={librarySearch}
                      onChange={(e) => setLibrarySearch(e.target.value)}
                      data-testid="disp-library-search"
                      style={{ width: "100%", fontSize: 12, padding: "6px 10px", marginBottom: 8, boxSizing: "border-box" }}
                    />
                    <div style={{ maxHeight: 200, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
                      {libraryLoading ? (
                        <div style={{ textAlign: "center", padding: 12, fontSize: 11, color: "var(--text-3)" }}>
                          <Loader2 style={{ width: 11, height: 11, animation: "spin 1s linear infinite", verticalAlign: "middle", marginRight: 4 }} /> Loading…
                        </div>
                      ) : libraryAssets.length === 0 ? (
                        <div style={{ textAlign: "center", padding: 12, fontSize: 11, color: "var(--text-3)" }}>
                          No resources in library yet. Upload files from the Resources page.
                        </div>
                      ) : (
                        libraryAssets
                          .filter((a) => !librarySearch.trim() || (a.name || "").toLowerCase().includes(librarySearch.trim().toLowerCase()))
                          .slice(0, 40)
                          .map((a) => {
                            const alreadyAttached = attachments.some((x) => x.resource_id === a.id);
                            return (
                              <button
                                key={a.id}
                                data-testid={`disp-library-item-${a.id}`}
                                disabled={alreadyAttached}
                                onClick={() => {
                                  setAttachments((cur) => [...cur, {
                                    id: a.id,
                                    name: a.name || "Resource",
                                    size: a.size || null,
                                    content_type: a.content_type || a.type || "application/octet-stream",
                                    url: a.url || a.thumbnail_url || "",
                                    resource_id: a.id,
                                    source: "library",
                                  }]);
                                }}
                                style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", background: alreadyAttached ? "var(--subtle)" : "#fff", border: "0.5px solid var(--border-soft)", borderRadius: 6, cursor: alreadyAttached ? "default" : "pointer", textAlign: "left", fontFamily: "inherit", opacity: alreadyAttached ? 0.55 : 1 }}
                              >
                                <FileText style={{ width: 12, height: 12, color: "var(--blue)", flexShrink: 0 }} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 12, color: "var(--navy)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.name || "Resource"}</div>
                                  {a.description && <div style={{ fontSize: 10, color: "var(--text-3)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.description}</div>}
                                </div>
                                {alreadyAttached ? <CheckCircle2 style={{ width: 11, height: 11, color: "var(--success)" }} /> : <Plus style={{ width: 11, height: 11, color: "var(--text-3)" }} />}
                              </button>
                            );
                          })
                      )}
                    </div>
                  </div>
                )}
              </section>

              {/* ═══════ FOLLOW-UP EMAIL COMPOSER (Iter 210.11 · Phase B)
                  Replaces the mock "Will send follow-up email" toggle.
                  Real Microsoft 365 send via /api/integrations/m365/email/send
                  with attachments piped through from the section above.
                  "✦ AI Draft" uses /api/dialer/follow-up-email to pre-fill
                  a personalized subject+body from the transcript. */}
              <section>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  <div>
                    <div className="eyebrow">FOLLOW-UP EMAIL {emailSentId ? "· ✓ SENT" : ""}</div>
                    <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>
                      Send a real follow-up email via Microsoft 365. Attachments above ride along.
                    </div>
                  </div>
                  {!emailSentId && (
                    <button
                      data-testid="disp-email-toggle"
                      onClick={() => {
                        setEmailOpen((v) => {
                          const next = !v;
                          if (next && !emailForm.to && _defaultRecipient) {
                            setEmailForm((f) => ({ ...f, to: _defaultRecipient }));
                          }
                          return next;
                        });
                      }}
                      style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px", borderRadius: 999, fontSize: 11, fontWeight: 500, fontFamily: "inherit", cursor: "pointer", border: emailOpen ? "1px solid var(--blue)" : "0.5px solid var(--border)", background: emailOpen ? "var(--blue-soft)" : "#fff", color: emailOpen ? "var(--blue)" : "var(--text-2)" }}
                    >
                      <Mail style={{ width: 11, height: 11 }} /> {emailOpen ? "Hide composer" : "Compose email"}
                      {emailOpen ? <ChevronUp style={{ width: 11, height: 11 }} /> : <ChevronDown style={{ width: 11, height: 11 }} />}
                    </button>
                  )}
                </div>

                {emailSentId ? (
                  <div data-testid="disp-email-sent-summary" style={{ padding: "10px 12px", background: "#E9F8F1", border: "0.5px solid #9FE1CB", borderRadius: 8, fontSize: 11, color: "#0F6E56", display: "flex", alignItems: "center", gap: 8 }}>
                    <CheckCircle2 style={{ width: 12, height: 12 }} />
                    <div>
                      <div style={{ fontWeight: 600 }}>Email sent to {emailForm.to}</div>
                      <div style={{ fontSize: 10, color: "var(--text-3)", marginTop: 1 }}>Subject: {emailForm.subject || "(no subject)"} · via Microsoft 365</div>
                    </div>
                  </div>
                ) : emailOpen && (
                  // Iter 210.14 — replaced the inline composer with the
                  // shared ComposeEmailPanel used by the Lead Focus Email
                  // tab. Reps now get: template dropdown (Introduction /
                  // Follow-up / Demo Invitation / Quote Follow-up + every
                  // user-published template), the polished Resources
                  // slide-in, 📎 local attach + 📚 From Resources, 10 MB
                  // pre-flight guard, m365 OAuth auto-handler, HTML-escape,
                  // and the "Sends from your connected Outlook · saved to
                  // your Sent Items" copy. AI Draft still hits the
                  // transcript-aware `/dialer/follow-up-email` endpoint.
                  <div data-testid="disp-email-composer">
                    <ComposeEmailPanel
                      variant="inline-dialer"
                      lead={lead}
                      prefillTo={_defaultRecipient}
                      aiDraftEndpoint={`${_API}/dialer/follow-up-email`}
                      extraDraftContext={{
                        transcript: rcTranscript
                          ? String(rcTranscript).split(/\n+/).slice(-40).map((line) => ({
                              speaker: /^(rep|agent|me):/i.test(line) ? "rep" : "prospect",
                              text: line.replace(/^(rep|agent|me|prospect|customer):\s*/i, "").trim(),
                            })).filter((t) => t.text)
                          : [],
                        disposition: { outcome, substatus, nextStep, sentiment, decisionMakerReached, notes: notesText },
                      }}
                      // Iter 210.14 — attachments from the top Step 4
                      // chip row pre-populate the composer. Both surfaces
                      // stay in sync via onAttachmentsChange so the audit
                      // chip row + email payload are one list.
                      initialAttachments={attachments}
                      onAttachmentsChange={setAttachments}
                      onClose={() => setEmailOpen(false)}
                      onSent={(payload) => {
                        // ComposeEmailPanel returns the Graph message_id
                        // on `payload.message_id`. Store it so the Step 5
                        // review card + disposition audit field show the
                        // real id (fallback to a synthetic tag if the
                        // backend didn't echo one back).
                        setEmailSentId(payload?.message_id || `sent-${Date.now()}`);
                        setEmailForm((f) => ({
                          ...f,
                          to: Array.isArray(payload?.to) ? payload.to.join(", ") : (payload?.to || f.to),
                          subject: payload?.subject || f.subject,
                        }));
                        setEmailOpen(false);
                      }}
                    />
                  </div>
                )}
              </section>

              {/* ═══════ FOLLOW-UP TASKS (Iter 210.15 rewrite) ═══════
                  Was inline editable rows (Iter 210.11). Now: reuses the
                  shared CreateTaskModal from the Tasks page — same
                  fields (Title *, Description, Priority, Category incl.
                  Outreach, Due date, real Assign-to picker from
                  /api/admin/users, "Linked to: {lead}" banner). Each
                  task POSTs individually via the modal instead of
                  batching on disposition save. AI-suggested tasks show
                  as PROPOSALS in a strip above the created list — one
                  click opens CreateTaskModal pre-filled. */}
              <section>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  <div>
                    <div className="eyebrow">FOLLOW-UP TASKS · {followupTasks.length} created</div>
                    <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>Tasks created here appear on the Tasks tab and the assignee&apos;s inbox immediately.</div>
                  </div>
                  <button
                    data-testid="disp-add-task"
                    onClick={() => { setCreateTaskPrefill(null); setCreateTaskOpen(true); }}
                    style={{ background: "none", border: "none", color: "var(--blue)", fontSize: 11, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", padding: 0 }}
                  >
                    + Add task
                  </button>
                </div>

                {/* AI proposals strip — click to open CreateTaskModal pre-filled. */}
                {aiTaskProposals.length > 0 && (
                  <div data-testid="disp-ai-task-proposals" style={{ marginBottom: 8, padding: "8px 10px", background: "#F5F3FF", border: "0.5px solid #DDD6FE", borderRadius: 8 }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: "#7A48D8", letterSpacing: "0.04em", marginBottom: 6, display: "flex", alignItems: "center", gap: 4 }}>
                      <Sparkles style={{ width: 10, height: 10 }} /> AI SUGGESTS {aiTaskProposals.length} TASK{aiTaskProposals.length === 1 ? "" : "S"} · REVIEW &amp; CREATE
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                      {aiTaskProposals.map((p, i) => (
                        <div key={p.id} data-testid={`disp-ai-task-proposal-${i}`} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 8px", background: "#fff", border: "0.5px solid #E9E4FF", borderRadius: 6 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 11, fontWeight: 500, color: "var(--navy)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.title}</div>
                            <div style={{ fontSize: 10, color: "var(--text-3)", marginTop: 1 }}>
                              {p.due_date} · {p.priority} · {p.category?.replace(/_/g, "-")}
                            </div>
                          </div>
                          <button
                            data-testid={`disp-ai-task-create-${i}`}
                            onClick={() => {
                              setCreateTaskPrefill({
                                title: p.title,
                                description: p.description || "",
                                priority: p.priority || "medium",
                                category: p.category || "follow_up",
                                due_date: p.due_date || "",
                                _proposal_id: p.id, // remove from proposals on create
                              });
                              setCreateTaskOpen(true);
                            }}
                            style={{ padding: "4px 10px", borderRadius: 6, fontSize: 10, fontFamily: "inherit", cursor: "pointer", border: "1px solid #7A48D8", background: "#7A48D8", color: "#fff", fontWeight: 500 }}
                          >
                            + Create task
                          </button>
                          <button
                            data-testid={`disp-ai-task-dismiss-${i}`}
                            onClick={() => setAiTaskProposals((cur) => cur.filter((_, j) => j !== i))}
                            title="Dismiss suggestion"
                            style={{ background: "none", border: "none", color: "var(--text-3)", cursor: "pointer", padding: 2, display: "flex" }}
                          >
                            <X style={{ width: 11, height: 11 }} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Created tasks — read-only summary with ✕ Remove (fires DELETE). */}
                {followupTasks.length === 0 && aiTaskProposals.length === 0 ? (
                  <div data-testid="disp-tasks-empty" style={{ padding: "14px 16px", textAlign: "center", fontSize: 11, color: "var(--text-3)", border: "0.5px dashed var(--border)", borderRadius: 8 }}>
                    No tasks yet. Run AI auto-map in Step 2 or click &quot;+ Add task&quot;.
                  </div>
                ) : followupTasks.length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {followupTasks.map((t, i) => (
                      <div key={t.id || i} data-testid={`disp-task-${i}`} style={{ padding: "8px 10px", border: "0.5px solid var(--border-soft)", borderRadius: 8, background: "#fff", display: "flex", alignItems: "flex-start", gap: 8 }}>
                        <CheckCircle2 style={{ width: 12, height: 12, color: "var(--success)", flexShrink: 0, marginTop: 2 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 500, color: "var(--navy)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {t.title}
                          </div>
                          {t.description && (
                            <div data-testid={`disp-task-description-${i}`} style={{ fontSize: 10, color: "var(--text-3)", marginTop: 2, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                              {t.description}
                            </div>
                          )}
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
                            {t.due_date && (
                              <span style={{ fontSize: 9, padding: "2px 6px", background: "#EEF2FF", color: "#3F5BFF", borderRadius: 999 }}>
                                <TaskDueText task={t} overdue testid={`disp-task-due-${i}`} />
                              </span>
                            )}
                            {t.priority && (
                              <span style={{ fontSize: 9, padding: "2px 6px", background: t.priority === "urgent" ? "#FCEBEB" : t.priority === "high" ? "#FFF3D6" : "var(--subtle)", color: t.priority === "urgent" ? "#A32D2D" : t.priority === "high" ? "#A6660B" : "var(--text-2)", borderRadius: 999, textTransform: "capitalize" }}>
                                {t.priority}
                              </span>
                            )}
                            {t.category && (
                              <span style={{ fontSize: 9, padding: "2px 6px", background: "var(--subtle)", color: "var(--text-2)", borderRadius: 999 }}>
                                {t.category.replace(/_/g, "-")}
                              </span>
                            )}
                            {t.assigned_to_name && (
                              <span style={{ fontSize: 9, padding: "2px 6px", background: "#E9F8F1", color: "#0F6E56", borderRadius: 999 }}>
                                → {t.assigned_to_name.split(" ")[0]}
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          data-testid={`disp-task-remove-${i}`}
                          title="Delete task"
                          onClick={async () => {
                            // Confirm delete since the task is already
                            // live in /api/tasks (not a draft anymore).
                            if (!window.confirm(`Delete "${t.title}"?`)) return;
                            try {
                              if (t.id) {
                                await axios.delete(`${_API}/tasks/${t.id}`, { headers: _authHeaders(), timeout: 8000 });
                              }
                              setFollowupTasks((cur) => cur.filter((_, j) => j !== i));
                              toast.success("Task deleted");
                            } catch (e) {
                              toast.error(`Couldn't delete task: ${e?.response?.data?.detail || e.message || "unknown"}`);
                            }
                          }}
                          style={{ background: "none", border: "none", color: "var(--text-3)", cursor: "pointer", padding: 2, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                        >
                          <X style={{ width: 12, height: 12 }} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}
              </section>

              {/* ═══════ SCHEDULE DEMO (Iter 210.11 · Phase D) ═══════
                  Expandable section — mounts the shared
                  ScheduleMeetingModal that DemoBookings.jsx and Lead
                  Focus already use. onSave translates the modal's
                  "meeting-shaped" payload into the demo_bookings shape
                  and POSTs to /api/demo-booking/book-internal so the
                  booking lands on the /demo-bookings dashboard and
                  triggers the Iter 210.4 auto-created "Prep demo" task
                  workflow. */}
              <section>
                <button
                  data-testid="disp-demo-toggle"
                  onClick={() => setDemoExpanded((v) => !v)}
                  style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", background: scheduledDemo ? "#E9F8F1" : "#fff", border: scheduledDemo ? "0.5px solid #9FE1CB" : "0.5px solid var(--border)", borderRadius: 8, cursor: "pointer", fontFamily: "inherit" }}
                >
                  <CalendarIcon style={{ width: 13, height: 13, color: scheduledDemo ? "#0F6E56" : "var(--text-2)" }} />
                  <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: scheduledDemo ? "#0F6E56" : "var(--navy)" }}>
                      {scheduledDemo ? "✓ Demo scheduled" : "Schedule appointment / demo"}
                    </div>
                    <div style={{ fontSize: 10, color: "var(--text-3)", marginTop: 1 }}>
                      {scheduledDemo
                        ? `${scheduledDemo.date} · ${scheduledDemo.time} · ${scheduledDemo.product || "Demo"} · Host: ${scheduledDemo.host_rep_name || "—"}`
                        : "Book a demo or follow-up on this lead — lands on the Demo Bookings dashboard."}
                    </div>
                  </div>
                  {demoExpanded ? <ChevronUp style={{ width: 13, height: 13, color: "var(--text-3)" }} /> : <ChevronDown style={{ width: 13, height: 13, color: "var(--text-3)" }} />}
                </button>
                {demoExpanded && (
                  <div data-testid="disp-demo-panel" style={{ marginTop: 8, padding: 12, border: "0.5px solid var(--border-soft)", borderRadius: 8, background: "var(--subtle)" }}>
                    {scheduledDemo ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <div style={{ fontSize: 11, color: "var(--text-2)", lineHeight: 1.5 }}>
                          <div><strong>When:</strong> {scheduledDemo.date} at {scheduledDemo.time}</div>
                          {scheduledDemo.product && <div><strong>Product:</strong> {scheduledDemo.product}</div>}
                          <div><strong>Host:</strong> {scheduledDemo.host_rep_name || "—"}</div>
                          {scheduledDemo.meeting_link && (
                            <div>
                              <strong>Link:</strong>{" "}
                              <a href={scheduledDemo.meeting_link} target="_blank" rel="noopener noreferrer" style={{ color: "var(--blue)", wordBreak: "break-all" }}>
                                {scheduledDemo.meeting_link}
                              </a>
                            </div>
                          )}
                        </div>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button
                            data-testid="disp-demo-edit"
                            onClick={() => setScheduleModalOpen(true)}
                            style={{ padding: "5px 10px", borderRadius: 6, fontSize: 11, fontFamily: "inherit", cursor: "pointer", border: "0.5px solid var(--border)", background: "#fff", color: "var(--text-2)" }}
                          >
                            Reschedule
                          </button>
                          <button
                            data-testid="disp-demo-clear"
                            onClick={() => setScheduledDemo(null)}
                            style={{ padding: "5px 10px", borderRadius: 6, fontSize: 11, fontFamily: "inherit", cursor: "pointer", border: "0.5px solid var(--border)", background: "#fff", color: "var(--text-3)" }}
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        data-testid="disp-demo-open"
                        onClick={() => setScheduleModalOpen(true)}
                        style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 6, fontSize: 11, fontWeight: 500, fontFamily: "inherit", cursor: "pointer", border: "1px solid var(--blue)", background: "var(--blue)", color: "#fff" }}
                      >
                        <CalendarIcon style={{ width: 11, height: 11 }} /> Set up demo…
                      </button>
                    )}
                  </div>
                )}
              </section>
            </div>
          )}

          {step === 5 && (
            <div data-testid="disp-body-step5" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {/* Iter 210.12 · Step 5 Review & Confirm rewrite.
                  A read-only summary of every choice the rep made in
                  Steps 1-4 with an ✎ Edit shortcut per card that jumps
                  back to that step. State persists across step
                  navigation (the modal never unmounts). The old
                  free-text `timelineNote` textarea was removed — reps
                  already write narrative notes in Step 2. */}
              <div style={{ padding: "10px 12px", background: "var(--subtle)", borderRadius: 8, fontSize: 11, color: "var(--text-2)", lineHeight: 1.5 }}>
                <strong>Review your call log before saving.</strong> Click the ✎ on any card to jump back and fix.
                Everything you entered is kept — nothing is lost by going back.
              </div>

              {/* ═══ Card ① · Call outcome (Step 1) ═══ */}
              <ReviewCard
                num={1}
                title="Call outcome"
                empty={!stage && !substatus && !sentiment && !productsDiscussed.length && !recordingUrl}
                emptyHint="No outcome captured yet — go back to Step 1."
                onEdit={() => setStep(1)}
              >
                <ReviewRow label="Stage" value={<strong>{stage || <em style={{ color: "var(--warn, #b45309)" }}>required — not set</em>}</strong>} />
                <ReviewRow label="Sub-status" value={substatus || <em style={{ color: "var(--text-3)" }}>—</em>} />
                <ReviewRow label="Sentiment" value={<span style={{ textTransform: "capitalize" }}>{(sentiment || "").replace(/_/g, " ") || <em style={{ color: "var(--text-3)" }}>—</em>}</span>} />
                <ReviewRow
                  label="Call flags"
                  value={
                    <span>
                      {decisionMakerReached ? <span style={{ color: "#0F6E56" }}>✓ DM reached</span> : <span style={{ color: "var(--text-3)" }}>DM not reached</span>}
                      {" · "}
                      {budgetConfirmed ? <span style={{ color: "#0F6E56" }}>✓ Budget confirmed</span> : <span style={{ color: "var(--text-3)" }}>Budget not confirmed</span>}
                    </span>
                  }
                />
                <ReviewRow
                  label="Products discussed"
                  value={
                    productsDiscussed.length ? (
                      <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 4 }}>
                        {productsDiscussed.map((p) => (
                          <span key={p} style={{ padding: "2px 8px", background: "#EEF2FF", color: "#3F5BFF", borderRadius: 999, fontSize: 10 }}>{p}</span>
                        ))}
                      </span>
                    ) : <em style={{ color: "var(--text-3)" }}>none tagged</em>
                  }
                />
                <ReviewRow
                  label="Recording"
                  value={recordingUrl ? (
                    <a href={recordingUrl} target="_blank" rel="noopener noreferrer" style={{ color: "var(--blue)", wordBreak: "break-all", fontSize: 11 }}>{recordingUrl}</a>
                  ) : <em style={{ color: "var(--text-3)" }}>—</em>}
                />
              </ReviewCard>

              {/* ═══ Card ② · Notes & next step (Step 2) ═══ */}
              <ReviewCard
                num={2}
                title="Notes & next step"
                empty={!notesText && !nextStep && !followup}
                emptyHint="No notes yet — go back to Step 2."
                onEdit={() => setStep(2)}
              >
                <ReviewRow label="Next step" value={nextStep || <em style={{ color: "var(--text-3)" }}>—</em>} />
                <ReviewRow label="Follow-up" value={followup || <em style={{ color: "var(--text-3)" }}>—</em>} />
                {notesText && (
                  <div style={{ marginTop: 8, paddingTop: 8, borderTop: "0.5px solid var(--border-soft)" }}>
                    <div style={{ fontSize: 10, color: "var(--text-3)", letterSpacing: "0.04em", marginBottom: 4 }}>NOTES</div>
                    <ExpandableNotesPreview text={notesText} />
                  </div>
                )}
              </ReviewCard>

              {/* ═══ Card ③ · Lead info changes (Step 3) ═══ */}
              <ReviewCard
                num={3}
                title="Lead info changes"
                subtitle={Object.keys(leadEdits).length ? `${Object.keys(leadEdits).length} field${Object.keys(leadEdits).length === 1 ? "" : "s"} edited` : "no fields edited"}
                empty={Object.keys(leadEdits).length === 0 && contactDispositions.length === 0}
                emptyHint="No lead-info edits — this is fine if the CRM record was already correct."
                onEdit={() => setStep(3)}
              >
                {Object.keys(leadEdits).length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 3, marginBottom: contactDispositions.length ? 8 : 0 }}>
                    {Object.entries(leadEdits).map(([k, v]) => (
                      <div key={k} style={{ display: "flex", gap: 8, fontSize: 11, lineHeight: 1.4 }}>
                        <span style={{ width: 4, height: 4, borderRadius: "50%", background: "var(--blue)", marginTop: 6, flexShrink: 0 }} />
                        <span style={{ color: "var(--text-3)", minWidth: 140, flexShrink: 0 }}>{formatLeadEditKey(k)}</span>
                        <span style={{ color: "var(--text-1)", wordBreak: "break-word", flex: 1 }}>{formatLeadEditValue(v)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {contactDispositions.length > 0 && (
                  <div style={{ paddingTop: 8, borderTop: Object.keys(leadEdits).length ? "0.5px solid var(--border-soft)" : "none" }}>
                    <div style={{ fontSize: 10, color: "var(--text-3)", letterSpacing: "0.04em", marginBottom: 4 }}>
                      CONTACTS DISPOSITIONED · {contactDispositions.length}
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {contactDispositions.map((cd) => (
                        <span key={cd.contact_id} style={{ padding: "3px 8px", background: "#EEF2FF", color: "#3F5BFF", borderRadius: 999, fontSize: 10 }}>
                          {cd.name}: {cd.outcome}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </ReviewCard>

              {/* ═══ Card ④ · Attachments & follow-up (Step 4) ═══ */}
              <ReviewCard
                num={4}
                title="Attachments & follow-up"
                empty={attachments.length === 0 && !emailSentId && followupTasks.length === 0 && !scheduledDemo}
                emptyHint="No attachments, email, tasks, or demo scheduled."
                onEdit={() => setStep(4)}
              >
                <ReviewRow
                  label={`Attachments (${attachments.length})`}
                  value={
                    attachments.length === 0 ? <em style={{ color: "var(--text-3)" }}>none</em> : (
                      <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 4 }}>
                        {attachments.map((a, i) => (
                          <span key={a.id || i} style={{ padding: "2px 8px", background: "#EEF2FF", color: "#3F5BFF", borderRadius: 999, fontSize: 10, display: "inline-flex", alignItems: "center", gap: 4 }}>
                            <FileText style={{ width: 9, height: 9 }} /> {a.name}
                          </span>
                        ))}
                      </span>
                    )
                  }
                />
                <ReviewRow
                  label="Follow-up email"
                  value={
                    emailSentId ? (
                      <div>
                        <div style={{ color: "#0F6E56", fontWeight: 500 }}>✓ Sent to {emailForm.to || "(recipient)"}</div>
                        {emailForm.subject && <div style={{ fontSize: 10, color: "var(--text-3)", marginTop: 1 }}>Subject: {emailForm.subject}</div>}
                      </div>
                    ) : <em style={{ color: "var(--text-3)" }}>not sent</em>
                  }
                />
                <ReviewRow
                  label={`Follow-up tasks (${followupTasks.length})`}
                  value={
                    followupTasks.length === 0 ? <em style={{ color: "var(--text-3)" }}>none</em> : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        {followupTasks.map((t) => (
                          <div key={t.id} style={{ fontSize: 11, color: "var(--text-1)" }}>
                            • {t.title}
                            <span style={{ color: "var(--text-3)", marginLeft: 6 }}>
                              {t.due_date ? <>— <TaskDueText task={t} overdue /></> : ""}{t.priority ? ` · ${t.priority}` : ""}
                            </span>
                          </div>
                        ))}
                      </div>
                    )
                  }
                />
                <ReviewRow
                  label="Scheduled demo"
                  value={
                    scheduledDemo ? (
                      <div>
                        <div style={{ color: "#0F6E56", fontWeight: 500 }}>✓ {scheduledDemo.date} at {scheduledDemo.time}</div>
                        <div style={{ fontSize: 10, color: "var(--text-3)", marginTop: 1 }}>
                          {scheduledDemo.product || "Demo"} · Host: {scheduledDemo.host_rep_name || "—"}
                        </div>
                        {scheduledDemo.meeting_link && (
                          <a href={scheduledDemo.meeting_link} target="_blank" rel="noopener noreferrer" style={{ color: "var(--blue)", fontSize: 10, wordBreak: "break-all" }}>
                            {scheduledDemo.meeting_link}
                          </a>
                        )}
                      </div>
                    ) : <em style={{ color: "var(--text-3)" }}>not scheduled</em>
                  }
                />
              </ReviewCard>

              {/* Recent activity — kept as background context */}
              <section>
                <div className="eyebrow" style={{ marginBottom: 8 }}>RECENT ACTIVITY ON THIS LEAD · LAST 5</div>
                <div data-testid="disp-recent-activity" style={{ border: "0.5px solid var(--border-soft)", borderRadius: 10, overflow: "hidden" }}>
                  {(lead?.activity?.slice(0, 5) || []).length === 0 ? (
                    <div style={{ padding: "12px 14px", textAlign: "center", fontSize: 11, color: "var(--text-3)" }}>
                      No prior activity for this lead.
                    </div>
                  ) : (lead.activity.slice(0, 5).map((a, i) => (
                    <div key={i} style={{ padding: "8px 14px", display: "flex", alignItems: "flex-start", gap: 8, borderBottom: i < Math.min(4, lead.activity.length - 1) ? "0.5px solid var(--border-soft)" : "none", background: i === 0 ? "var(--subtle)" : "#fff" }}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", marginTop: 6, flexShrink: 0, background: a.kind === "email" ? "var(--blue)" : a.kind === "call" ? "#1D9E75" : a.kind === "stage" ? "#A6660B" : "var(--text-3)" }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 11, color: "var(--text-2)" }}>{a.text}</div>
                        <div style={{ fontSize: 10, color: "var(--text-4)", marginTop: 1 }}>{a.by} · {new Date(a.at).toLocaleDateString()}</div>
                      </div>
                      <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.05em", color: "var(--text-3)", textTransform: "uppercase" }}>{a.kind}</span>
                    </div>
                  )))}
                </div>
              </section>
            </div>
          )}

        </div>

        {/* ── Footer — Pause / Back / Next / Save ── */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "12px 22px", borderTop: "0.5px solid var(--border-soft)", background: "var(--paper)", flexShrink: 0 }}>
          {/* Iter 210.7 · Mandatory-mode Pause button.
              Replaces the old "Cancel" (which silently discarded the
              draft). "Pause — I'm on another call" is the ONLY escape
              hatch: it autosaves the current draft (the modal already
              autosaves every ~2.5s) and closes. The global
              IncompleteDispositionsNotifier then surfaces the draft
              from anywhere in the app so the rep can resume. Reps who
              are NOT mid-call must Complete the disposition to close. */}
          <button
            onClick={onClose}
            className="btn"
            data-testid="disp-pause"
            title="Autosaves the draft. You'll see a reminder to resume it via the notifier."
            style={{ color: "var(--warn, #b45309)" }}
          >
            Pause — I&apos;m on another call
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 10, color: "var(--text-3)", letterSpacing: "0.04em" }} data-testid="disp-step-counter">STEP {step} OF {TOTAL_STEPS}</span>
            <div style={{ display: "flex", gap: 8 }}>
              {step > 1 && (
                <button data-testid="disp-step-back" onClick={() => setStep((s) => s - 1)} className="btn">
                  <ChevronLeft style={{ width: 12, height: 12 }} /> Back
                </button>
              )}
              {step < TOTAL_STEPS ? (
                <>
                  {/* Iter 245 — allow saving from ANY step, not just the
                      final review. Same contract as "Save & stop":
                      persists the disposition then closes. Stage stays
                      mandatory (downstream reports depend on it). */}
                  <button
                    data-testid="disp-save-any"
                    onClick={async () => {
                      if (!stage) return;
                      const ok = await onSave(buildPayload(false));
                      if (ok !== false) onClose?.();
                    }}
                    disabled={!stage}
                    title={!stage ? "Stage is required — pick one on Step 1" : "Save this disposition now"}
                    className="btn"
                    style={!stage ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                  >Save</button>
                  <button data-testid="disp-step-next" onClick={() => setStep((s) => s + 1)} className="btn btn-primary">
                    Next <ChevronRight style={{ width: 12, height: 12 }} />
                  </button>
                </>
              ) : (
                <>
                  {/* Iter 210.8 · Stage is now MANDATORY. Save buttons
                      block when stage is empty and surface a hint via
                      title tooltip. Prevents reps from persisting a
                      disposition without a stage which downstream
                      reports depend on.
                      Iter 244 (Aug 28 2026) · Both buttons now AWAIT
                      onSave and call onClose() when the save resolves
                      with anything other than an explicit `false`. This
                      finally auto-closes the modal for non-super-admin
                      reps (who don't see the close-X). The dialer's
                      onSaveDisposition already closed via its own
                      `setDisposition(false)` at line ~3624; the extra
                      onClose call is idempotent for that surface. */}
                  <button
                    data-testid="disp-stop"
                    onClick={async () => {
                      if (!stage) return;
                      const ok = await onSave(buildPayload(false));
                      if (ok !== false) onClose?.();
                    }}
                    disabled={!stage}
                    title={!stage ? "Stage is required — pick one on Step 1" : ""}
                    className="btn"
                    style={!stage ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                  >Save & stop</button>
                  <button
                    data-testid="disp-next"
                    onClick={async () => {
                      if (!stage) return;
                      const ok = await onSave(buildPayload(true));
                      if (ok !== false) onClose?.();
                    }}
                    disabled={!stage}
                    title={!stage ? "Stage is required — pick one on Step 1" : ""}
                    className="btn btn-primary"
                    style={!stage ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                  >Save & next call</button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Iter 210.11 · Phase D — Schedule demo modal. Rendered here so
          it stacks above the DispositionModal without breaking scroll
          containment. onSave receives the meeting-shaped payload
          (date, time, type, host_user_id/host_name, link, notes,
          products, meetingWith, title) — same shape DemoBookings.jsx's
          ScheduleDemoUnified consumes — and we translate it into the
          `/api/demo-booking/book-internal` request body. On success we
          stash a summary on scheduledDemo so the Step 4 section shows
          the confirmation card. */}
      {/* Iter 210.15 — Shared "Create task" modal from the Tasks page.
          Same testids, same fields (Title *, Description, Priority,
          Category incl. Outreach, Due date, real Assign-to picker,
          Related lead banner). Opened blank via "+ Add task" or
          pre-filled from an AI proposal. On onCreated we drop the
          matching proposal (if any) and add the real task to the
          created list. `defaultLead` is passed unconditionally so
          the modal shows the "Linked to: {lead}" banner. */}
      {createTaskOpen && (
        <CreateTaskModal
          open={createTaskOpen}
          onClose={() => { setCreateTaskOpen(false); setCreateTaskPrefill(null); }}
          defaultLead={lead}
          defaultTaskPrefill={_backupOwner ? { ...(createTaskPrefill || {}), assigned_to: createTaskPrefill?.assigned_to || _backupOwner.id, assigned_to_name: createTaskPrefill?.assigned_to_name || _backupOwner.name } : createTaskPrefill}
          onCreated={(task) => {
            setFollowupTasks((cur) => [...cur, task]);
            if (createTaskPrefill?._proposal_id) {
              setAiTaskProposals((cur) =>
                cur.filter((p) => p.id !== createTaskPrefill._proposal_id),
              );
            }
            setCreateTaskPrefill(null);
          }}
        />
      )}
      {scheduleModalOpen && (
        <ScheduleMeetingModal
          lead={lead}
          onClose={() => setScheduleModalOpen(false)}
          onSave={async (payload) => {
            const currentLead = lead;
            if (!_isPersistableLeadId(currentLead?.id)) {
              toast.error("Demo scheduling requires a saved lead");
              return;
            }
            const demoType = String(payload?.type || "product_demo")
              .toLowerCase().trim().replace(/\s+/g, "_");
            const body = {
              lead_id: currentLead.id,
              date: payload?.date || "",
              time: payload?.time || "",
              duration: 45,
              demo_type: demoType,
              product: (Array.isArray(payload?.products) ? payload.products : []).join(", "),
              host_rep_id: payload?.host_user_id || null,
              host_rep_name: payload?.host_name || "",
              attendees: payload?.meetingWith
                ? [{
                    name: payload.meetingWith,
                    email: currentLead?.contacts?.[0]?.email || currentLead?.email || "",
                  }]
                : [],
              meeting_link: payload?.link || "",
              internal_notes: payload?.notes || "",
              notes: payload?.title || "",
              contact_name: currentLead?.contacts?.[0]?.name || currentLead?.contact_name || "",
              email: currentLead?.contacts?.[0]?.email || currentLead?.email || "",
              phone: currentLead?.contacts?.[0]?.phone || currentLead?.phone || "",
              company: currentLead?.company || "",
              tz_offset: (() => {
                const mins = -new Date().getTimezoneOffset();
                const abs = Math.abs(mins);
                return `${mins >= 0 ? "+" : "-"}${String(Math.floor(abs / 60)).padStart(2, "0")}:${String(abs % 60).padStart(2, "0")}`;
              })(),
              timezone: payload?.tz || null,
            };
            try {
              const r = await axios.post(`${_API}/demo-booking/book-internal`, body, {
                headers: _authHeaders(), timeout: 15000,
              });
              const saved = r?.data || {};
              setScheduledDemo({
                id: saved.id || `demo-${Date.now()}`,
                date: body.date,
                time: body.time,
                product: body.product,
                host_rep_name: body.host_rep_name,
                meeting_link: body.meeting_link,
              });
              setScheduleModalOpen(false);
              toast.success(`📅 Demo booked — visible on Demo Bookings dashboard`);
            } catch (e) {
              console.warn("[disp-demo] book-internal failed", e?.response?.status, e?.response?.data);
              const _detail = e?.response?.data?.detail;
              let _msg;
              if (Array.isArray(_detail)) {
                _msg = _detail
                  .map((d) => {
                    if (typeof d === "string") return d;
                    const loc = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== "body").join(".") : "";
                    return loc ? `${loc}: ${d?.msg || "invalid"}` : (d?.msg || JSON.stringify(d));
                  })
                  .join("; ");
              } else if (typeof _detail === "string") {
                _msg = _detail;
              } else if (_detail && typeof _detail === "object") {
                _msg = _detail.msg || JSON.stringify(_detail);
              } else {
                _msg = e.message || "unknown";
              }
              toast.error(`Couldn't book demo: ${_msg}`);
              // Rethrow so ScheduleMeetingModal keeps its dialog open
              // (same pattern DemoBookings.jsx uses for retries).
              throw e;
            }
          }}
        />
      )}
    </>
  );
}

function Field({ label, required, full, children }) {
  return (
    <div style={{ gridColumn: full ? "1 / -1" : "auto" }}>
      <div className="eyebrow" style={{ marginBottom: 4 }}>{label}{required && <span style={{ color: "var(--error)", marginLeft: 3 }}>*</span>}</div>
      {children}
    </div>
  );
}

/* ── Main page ───────────────────────────────────────── */
export default function SalesDialerLive() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Iter 210.15 — the Iter 210.11 batch-POST of tasks-on-save is
  // gone (CreateTaskModal now POSTs each task individually the moment
  // the rep clicks Create). We no longer need `user` at the parent
  // level; the modal reads the current user via its own useAuth().
  // Iter 194 (Vipal — Feb 2026) — campaign-aware queue.
  // Replaces the original "top-50 by AI score" fetch (BW-02 May 13)
  // which broke production for CCM-NY (1,680 leads → 47 shown). When
  // arriving via `?campaign_id=...` or `?source=personal`, we now
  // hydrate from the correct queue endpoint:
  //   • campaign_id     → /api/dialer/queue?campaign_id=X
  //   • source=personal → /api/dialer/personal-campaign
  //   • neither (legacy) → /api/leads top-50 (kept as backstop only)
  //
  // The /api/dialer/queue rows are the rep's claimable work items.
  // We project them into the focus-lead shape the rest of the page
  // already expects via `_queueRowToLead` below.
  const [queue, setQueue] = useState([]);
  const [queueLoading, setQueueLoading] = useState(true);
  const [queueMeta, setQueueMeta] = useState({ source: "leads", campaign_id: null, campaign_name: null });
  const [backupOwner, setBackupOwner] = useState(null);
  const [backupError, setBackupError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setQueueLoading(true);
    const tk = (typeof localStorage !== "undefined" && localStorage.getItem("token")) || "";
    const campaignId = searchParams.get("campaign_id") || searchParams.get("campaign") || null;
    const source = searchParams.get("source") || null;
    const headers = tk ? { Authorization: `Bearer ${tk}` } : {};

    const _queueRowToLead = (row) => {
      // Iter 194 hotfix 2 (post-deploy "Unknown contact") — field name
      // mismatch between `_queue_lead_snapshot` (server.py:25728-25737)
      // and my mapper. The snapshot stores `name` + `practice`, NOT
      // `contact_name` + `company`. My first hotfix read the wrong keys
      // so every card rendered as "Unknown contact" with no clinic.
      //
      // Fix: read the snapshot's actual keys, with sensible aliases for
      // the legacy /api/leads passthrough where rows already have the
      // canonical `contact_name`/`company` shape.
      const snap = row.lead_snapshot || {};
      // Iter 340 hardening — some snapshots persisted `practice` as an
      // object `{group_size, web}` (from realLeadToFocusShape). Coerce
      // to string so downstream renders don't crash React with "Objects
      // are not valid as a React child".
      const _str = (v) => (v && typeof v === "object" ? "" : (v || ""));
      const flat = {
        id: row.lead_id || snap.id,
        // Snapshot uses `practice`; legacy passthrough uses `company`/`clinic`.
        company: _str(snap.practice) || _str(snap.company) || _str(snap.clinic) || _str(snap.company_name) || _str(row.clinic) || "",
        clinic:  _str(snap.practice) || _str(snap.company) || _str(snap.clinic) || _str(row.clinic) || "",
        // Snapshot uses `name`; legacy passthrough uses `contact_name`.
        contact_name: snap.name || snap.contact_name || row.contact_name || "",
        phone: snap.phone || snap.phone_e164 || row.phone || "",
        email: snap.email || "",
        title: snap.title || "",
        stage: snap.stage || "new_lead",
        sub_status: snap.sub_status || "",
        ai_score: snap.ai_score ?? row.ai_score ?? null,
        last_contact_date: snap.last_contact_date || null,
        do_not_call: snap.do_not_call === true,
        specialty: snap.specialty || row.specialty || "",
        city: snap.city || "",
        state: snap.state || "",
        zip: snap.zip || "",
        address: snap.address || "",
        lead_rating: snap.lead_rating || "",
        owner_id: snap.owner_id || null,
        // Iter 181 (Feb 2026) — propagate the contact-attempts count from
        // the enriched snapshot so classifyLead() can distinguish a
        // never-touched Stage=New lead from a re-added follow-up.
        contact_attempts_count: Number(snap.contact_attempts_count || 0),
      };
      const shaped = realLeadToFocusShape(flat);
      if (!shaped) return null;
      // Carry the queue-row id so SalesDialerLive can call
      // /queue/{item_id}/claim and /outcome later.
      shaped._queue_item_id = row.id;
      shaped._queue_status = row.status;
      shaped._queue_campaign_id = row.campaign_id;
      if (row.reason_key) {
        shaped._personal_reason_key = row.reason_key;
        shaped._personal_reason_label = row.reason_label;
      }
      return shaped;
    };

    const fetchAndSet = (req) =>
      req
        .then((res) => {
          if (cancelled) return;
          const arr = Array.isArray(res?.data?.items)
            ? res.data.items
            : Array.isArray(res?.data)
            ? res.data
            : [];
          // dialer_queue rows: only keep pending + claimed (open work).
          const openQueueRows = arr.filter((r) => !r.status || ["pending", "claimed"].includes(r.status));
          const mapped = openQueueRows.map(_queueRowToLead).filter(Boolean);
          // Apply the same open-stage + DNC guard as before so closed
          // / DNC leads accidentally left on the queue don't surface.
          const openStages = new Set(["new_lead", "new", "contacted", "follow_up", "demo_scheduled", "demo_confirmed", "demo_completed", "proposal", "quote_sent", "decision", "negotiation", "qualification", "qualified"]);
          const norm = (s) => String(s || "").toLowerCase().replace(/\s+/g, "_");
          // Campaign queues are already filtered server-side by the shared dialable rule.
          const filtered = (source === "personal_live" || campaignId)
            ? mapped.filter((l) => l.do_not_call !== true)
            : mapped.filter((l) => (!l.stage || openStages.has(norm(l.stage))) && l.do_not_call !== true);
          setQueue(filtered);
        })
        .catch((e) => {
          if (cancelled) return;
          console.warn("[dialer-live] queue fetch failed", e?.response?.status, e?.message);
        })
        .finally(() => { if (!cancelled) setQueueLoading(false); });

    if (source === "personal_live") {
      setQueueMeta({ source: "personal_live", campaign_id: null, campaign_name: searchParams.get("view") === "overdue" ? "Personal Campaign · Overdue" : "Personal Campaign · Today" });
      const req = axios.get(`${_API}/dialer/personal-campaign/live`, { headers, params: { view: searchParams.get("view") === "overdue" ? "overdue" : "today", ...(searchParams.get("owner") ? { owner_id: searchParams.get("owner") } : {}) }, timeout: 20000 })
        .then((res) => { if (!cancelled && res?.data?.backup_for) setBackupOwner({ id: res.data.backup_for, name: res.data.backup_for_name }); return res; })
        .catch((e) => {
          if (!cancelled && e?.response?.status === 403 && searchParams.get("owner")) {
            delete axios.defaults.headers.common["X-PC-Backup-For"];
            setBackupError(e.response.data?.detail || "You don't have backup access to this Personal Campaign");
          }
          throw e;
        })
        .then((res) => ({ data: { items: (res?.data?.leads || []).map((l) => ({
          lead_id: l.id, lead_snapshot: { ...l, practice: l.company, name: l.contact_name }, status: "pending",
          reason_key: l.reason_key, reason_label: l.reason_label,
        })) } }));
      fetchAndSet(req);
    } else if (source === "personal") {
      setQueueMeta({ source: "personal", campaign_id: null, campaign_name: "My Follow-Ups" });
      const req = axios.get(`${_API}/dialer/personal-campaign`, { headers, params: { view: searchParams.get("view") === "overdue" ? "overdue" : "today", ...(searchParams.get("owner") ? { owner_id: searchParams.get("owner") } : {}) }, timeout: 15000 })
        .then((res) => {
          if (cancelled) return res;
          // Flatten the 4 streams into a single array of leads with stream tags.
          const streams = res?.data?.streams || {};
          const allLeads = [];
          ["committed", "decay", "soft_reminder", "hot_topup"].forEach((key) => {
            (streams[key] || []).forEach((l) => allLeads.push({ ...l, _personal_stream: key }));
          });
          return { data: { items: allLeads.map((l) => ({ lead_id: l.id, lead_snapshot: l, status: "pending" })) } };
        });
      fetchAndSet(req);
    } else if (campaignId) {
      setQueueMeta({ source: "campaign", campaign_id: campaignId, campaign_name: null });
      // Iter 194 — campaign-scoped fetch. Server-side cap was 500;
      // we now request up to 10,000 (backend `/dialer/queue` ceiling)
      // so every campaign — including the 6,663-lead CCM Test July 20th —
      // renders its true "Call Queue N of N" without silently truncating.
      const req = axios.get(`${_API}/dialer/queue`, {
        params: { campaign_id: campaignId, status: "open", limit: 10000 },
        headers,
        timeout: 30000,
      });
      fetchAndSet(req);
      // Iter 278 (Jul 2026) — also fetch the campaign detail so the
      // Call Queue panel can render the audience-vs-callable subtitle
      // ("5,017 of 6,663 leads callable"). Runs in parallel with the
      // queue fetch and never blocks it — panel just doesn't get a
      // subtitle if this request fails. Uses the list endpoint and
      // finds our campaign inline (there's no per-id GET yet).
      axios
        .get(`${_API}/dialer/campaigns`, { headers, timeout: 15000 })
        .then((res) => {
          if (cancelled) return;
          const list = Array.isArray(res?.data) ? res.data : (res?.data?.campaigns || []);
          const c = list.find((x) => x.id === campaignId) || {};
          setQueueMeta((prev) => ({
            ...prev,
            campaign_name: c.name || prev.campaign_name,
            audience:       Number(c.leads ?? c.total_leads ?? 0) || null,
            callable:       (typeof c.callable === "number") ? c.callable : null,
            excluded_dnc:            Number(c.excluded_dnc || 0),
            excluded_wrong_specialty: Number(c.excluded_wrong_specialty || 0),
            dialable_summary: c.dialable_summary || null,
          }));
        })
        .catch(() => { /* silent — subtitle just won't render */ });
    } else {
      // Legacy backstop — no campaign context, no personal source. Keep
      // the old "top-50 by AI score" behavior so direct visits to
      // /dialer/live still render something instead of an empty page.
      setQueueMeta({ source: "leads", campaign_id: null, campaign_name: null });
      const req = axios.get(`${_API}/leads`, {
        params: { fields: "slim", limit: 50, sort: "ai_score", sort_dir: "desc" },
        headers, timeout: 12000,
      }).then((res) => {
        const arr = Array.isArray(res?.data) ? res.data : [];
        const mapped = arr.map((l) => ({ data: l })).map((w) => realLeadToFocusShape(w.data)).filter(Boolean);
        return { data: { items: mapped.map((l) => ({ lead_id: l.id, lead_snapshot: l, status: "pending" })) } };
      });
      fetchAndSet(req);
    }
    return () => { cancelled = true; };
    // Iter 194 — testing-agent feedback: deps were `[searchParams]` which is a
    // URLSearchParams object whose identity changes on every render, causing
    // a double GET /api/dialer/queue. Reading the specific string values
    // once and listing them as deps fires the effect only on real navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get("campaign_id"), searchParams.get("source"), searchParams.get("view"), searchParams.get("owner")]);

  // Backup mode: tag every request so calls, notes, dispositions and tasks are marked as backup work.
  const backupOwnerId = searchParams.get("owner") || "";
  useEffect(() => {
    if (!backupOwnerId) return undefined;
    setBackupError("");
    axios.defaults.headers.common["X-PC-Backup-For"] = backupOwnerId;
    return () => { delete axios.defaults.headers.common["X-PC-Backup-For"]; _backupOwner = null; setBackupOwner(null); };
  }, [backupOwnerId]);
  _backupOwner = backupOwner;

  // Phase B · Section 2 — Intelligent work-queue ordering
  // -----------------------------------------------------
  // 1. Sort the queue: uncontacted → callbacks-due → contacted.
  // 2. "Uncontacted only" toggle for high-velocity outbound days.
  // 3. Auto-load a follow-up queue when the primary campaign ends
  //    (every uncontacted lead has been dispositioned).
  // 4. Persist activeIdx to sessionStorage so a refresh / nav-away
  //    returns the rep to the exact slot they were on.
  const [uncontactedOnly, setUncontactedOnly] = useState(false);
  const [queueMode, setQueueMode] = useState("primary"); // "primary" | "followup"
  const [dispositionedIds, setDispositionedIds] = useState(() => new Set());

  // Sorted queue — primary mode shows everyone, follow-up mode shows callbacks only.
  const sortedQueue = useMemo(() => {
    const indexed = queue.map((l, idx) => ({ lead: l, idx, bucket: classifyLead(l) }));
    if (queueMode === "followup") {
      return indexed.filter((r) => r.bucket === "callback").sort((a, b) => a.idx - b.idx);
    }
    if (queueMeta.source === "personal_live") return indexed;
    return indexed.sort((a, b) => {
      const ba = BUCKET_ORDER[a.bucket] ?? 9;
      const bb = BUCKET_ORDER[b.bucket] ?? 9;
      if (ba !== bb) return ba - bb;
      // Within a bucket: keep original order (callers can drag later).
      return a.idx - b.idx;
    });
  }, [queue, queueMode, queueMeta.source]);

  const displayedQueue = useMemo(() => {
    // Issue cb7ef57a (Tatiana, June 25 2026) — leads the rep just
    // dispositioned in this session were not disappearing from the
    // queue, so the counter stayed at "48 of 48" forever and the
    // active slot kept landing back on a completed lead. Filter them
    // out here so the queue shrinks as the rep works through it.
    const base = dispositionedIds.size > 0
      ? sortedQueue.filter((r) => !dispositionedIds.has(r.lead?.id))
      : sortedQueue;
    if (uncontactedOnly) return base.filter((r) => r.bucket === "uncontacted");
    return base;
  }, [sortedQueue, uncontactedOnly, dispositionedIds]);
  // Pre-select the lead passed via ?leadId=… (set by the SalesDialer admin
  // page when a rep hits "Start Call"). Falls back to the first lead in
  // the sorted view (which means an "uncontacted" lead first per spec).
  // Real-mode: the queue is populated async via useEffect above, so we
  // start at 0 and let resolveActive() snap to the right slot when the
  // fetch resolves.
  const [activeIdx, setActiveIdx] = useState(() => {
    // Try to resume saved queue position.
    try {
      const raw = sessionStorage.getItem("dialerQueuePosition");
      if (raw) {
        const { activeIdx: savedIdx } = JSON.parse(raw);
        if (typeof savedIdx === "number" && savedIdx >= 0) return savedIdx;
      }
    } catch { /* ignore */ }
    return 0;
  });

  // Once the queue arrives, snap activeIdx to ?leadId= or first uncontacted.
  useEffect(() => {
    if (queue.length === 0) return;
    const wanted = searchParams.get("leadId");
    if (wanted) {
      const idx = queue.findIndex((l) => String(l.id) === String(wanted));
      if (idx >= 0) { setActiveIdx(idx); return; }
    }
    const firstUncontacted = queue.findIndex((l) => classifyLead(l) === "uncontacted");
    if (firstUncontacted >= 0 && activeIdx >= queue.length) setActiveIdx(firstUncontacted);
    else if (activeIdx >= queue.length) setActiveIdx(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- snap only on initial queue arrival
  }, [queue.length]);

  // Persist activeIdx
  useEffect(() => {
    try { sessionStorage.setItem("dialerQueuePosition", JSON.stringify({ activeIdx, queueMode, ts: Date.now() })); } catch { /* ignore */ }
  }, [activeIdx, queueMode]);
  const [state, setState] = useState("idle"); // idle | ringing | connected | held | wrap-up
  const [timer, setTimer] = useState(0);
  const [transcript, setTranscript] = useState([]);
  // Iter 185 (Feb 2026) — RC call identifiers, captured from
  // `webphone:call-started` and cleared on `webphone:call-ended`. Fed
  // into TransferConferencePicker so /calls/conference can attempt the
  // Cloud API `bring-in` path when available.
  const [rcCallCtx, setRcCallCtx] = useState({ callId: null, telephonySessionId: null, partyId: null });
  useEffect(() => {
    const onStarted = (ev) => {
      const d = ev?.detail || {};
      setRcCallCtx({
        callId: d.call_id || null,
        telephonySessionId: d.telephony_session_id || null,
        partyId: d.party_id || null,
      });
    };
    const onEnded = () => setRcCallCtx({ callId: null, telephonySessionId: null, partyId: null });
    window.addEventListener("webphone:call-started", onStarted);
    window.addEventListener("webphone:call-ended", onEnded);
    return () => {
      window.removeEventListener("webphone:call-started", onStarted);
      window.removeEventListener("webphone:call-ended", onEnded);
    };
  }, []);
  const [nudgeIdx, setNudgeIdx] = useState(0);
  const [powerDial, setPowerDial] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [activeTag, setActiveTag] = useState(null);
  const [disposition, setDisposition] = useState(false);
  const [muted, setMuted] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  // contactsCalled: ordered list of contacts spoken to during the *current* call
  // session. Populated when the call starts and on every quick-dial pivot.
  // Reset on advance/idle.
  const [contactsCalled, setContactsCalled] = useState([]);
  // Phase C · Section 3 — Lead profile slide-in panel
  const [leadProfileOpen, setLeadProfileOpen] = useState(false);

  // ─── Iter 234 (Aug 2026) · Committed-time dialer gating (spec §1E) ───
  // "The dialer's auto-dial mode respects committed times. It pauses the
  //  queue 5 minutes before a committed callback slot with a banner."
  //
  // Data source: each queue lead carries `ownership.next_step_at` (set
  // by the `next_step_committed` ownership event when the rep locks in
  // a callback slot). We scan the entire queue every 30s and surface
  // the soonest slot inside the 5-minute window.
  //
  // When a gate is active:
  //   • Power Dial's `setTimeout(() => startCall(nextLead), 4000)`
  //     chain is suppressed so we don't dial a random lead 30s before
  //     the committed slot fires.
  //   • The banner offers "Dial now" (jump to committed lead + call)
  //     and "Dismiss" (clear gate → normal auto-advance resumes).
  const [committedGate, setCommittedGate] = useState(null); // { leadId, leadName, minutesUntil, secondsUntil }
  const [committedDismissedIds, setCommittedDismissedIds] = useState(() => new Set());
  useEffect(() => {
    const compute = () => {
      const now = Date.now();
      const windowEnd = now + 5 * 60 * 1000; // 5 min ahead
      let soonest = null;
      for (const lg of queue || []) {
        const iso = lg?.ownership?.next_step_at || lg?.next_step_at;
        if (!iso) continue;
        if (committedDismissedIds.has(lg.id)) continue;
        const ts = Date.parse(iso);
        if (!Number.isFinite(ts)) continue;
        if (ts <= now) continue; // already past; not our concern
        if (ts > windowEnd) continue; // still >5min out
        if (!soonest || ts < soonest.ts) {
          soonest = { ts, lead: lg };
        }
      }
      if (!soonest) {
        setCommittedGate(null);
        return;
      }
      const secondsUntil = Math.max(0, Math.floor((soonest.ts - now) / 1000));
      const minutesUntil = Math.floor(secondsUntil / 60);
      setCommittedGate({
        leadId: soonest.lead.id,
        leadName: soonest.lead.clinic || soonest.lead.company || soonest.lead.contact_name || "committed lead",
        minutesUntil,
        secondsUntil,
      });
    };
    compute();
    const t = setInterval(compute, 1000); // tick every second so countdown feels alive
    return () => clearInterval(t);
  }, [queue, committedDismissedIds]);

  const lead = queue[activeIdx];

  // Shared Personal Campaign: while this lead is on a call, hide it from the owner/backups working the same campaign.
  const { user: authUser } = useAuth();
  const holdOwner = searchParams.get("source") === "personal_live" ? (backupOwnerId || authUser?.id || null) : null;
  const heldLeadRef = useRef(null);
  useEffect(() => {
    if (!holdOwner || !lead?.id) return undefined;
    const auth = { headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` } };
    const onStart = () => {
      heldLeadRef.current = lead.id;
      axios.post(`${_API}/dialer/pc-holds`, { owner_id: holdOwner, lead_id: lead.id }, auth).catch(() => {});
    };
    window.addEventListener("webphone:call-started", onStart);
    return () => {
      window.removeEventListener("webphone:call-started", onStart);
      // Moving to another lead (or leaving) releases the hold; logged outcomes release it server-side.
      if (heldLeadRef.current) {
        axios.delete(`${_API}/dialer/pc-holds`, { ...auth, params: { owner_id: holdOwner, lead_id: heldLeadRef.current } }).catch(() => {});
        heldLeadRef.current = null;
      }
    };
  }, [holdOwner, lead?.id]);
  // Pull leads that teammates on this campaign are calling right now out of my queue (checked every 20 s).
  const queueRef = useRef(queue); queueRef.current = queue;
  const activeIdxRef = useRef(activeIdx); activeIdxRef.current = activeIdx;
  useEffect(() => {
    if (!holdOwner) return undefined;
    const auth = { headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` } };
    const tick = () => axios.get(`${_API}/dialer/pc-holds`, { ...auth, params: { owner_id: holdOwner } }).then((r) => {
      const held = new Set(r.data?.lead_ids || []);
      if (!held.size) return;
      const q = queueRef.current;
      const cur = q[activeIdxRef.current]?.id;
      const nq = q.filter((l) => l.id === cur || !held.has(l.id));
      if (nq.length === q.length) return;
      setQueue(nq);
      setActiveIdx(Math.max(0, nq.findIndex((l) => l.id === cur)));
    }).catch(() => {});
    tick();
    const t = setInterval(tick, 20000);
    return () => clearInterval(t);
  }, [holdOwner]);

  // Iter 194 hotfix 2 — the dialer_queue snapshot only stores
  // {name, practice, phone, ai_score, reason, campaign} (see
  // server.py:_queue_lead_snapshot). When the rep selects a lead the
  // active-call panel + Lead Info modal need the full record (email,
  // address, locations, contacts, etc.), so hydrate it once on
  // selection from /api/leads/{id} and merge into the queue slot.
  // Cached per lead id so re-selecting doesn't re-fetch.
  const hydratedIdsRef = useRef(new Set());
  useEffect(() => {
    const leadId = lead?.id;
    if (!leadId) return;
    if (hydratedIdsRef.current.has(leadId)) return;
    // Skip hydration when we're in the legacy /api/leads passthrough
    // mode — that path already produced a rich shape, no need to
    // refetch.
    if (queueMeta.source === "leads") {
      hydratedIdsRef.current.add(leadId);
      return;
    }
    const tk = (typeof localStorage !== "undefined" && localStorage.getItem("token")) || "";
    let cancelled = false;
    axios.get(`${_API}/leads/${encodeURIComponent(leadId)}`, {
      headers: tk ? { Authorization: `Bearer ${tk}` } : {},
      timeout: 10000,
    })
      .then((res) => {
        if (cancelled) return;
        if (!res?.data) return;
        const rich = realLeadToFocusShape(res.data);
        if (!rich) return;
        hydratedIdsRef.current.add(leadId);
        setQueue((q) => q.map((l) => (l?.id === leadId ? {
          ...rich,
          // Preserve the queue-row id so claim/outcome wiring is intact.
          _queue_item_id: l._queue_item_id,
          _queue_status: l._queue_status,
          _queue_campaign_id: l._queue_campaign_id,
          _personal_stream: l._personal_stream,
          _personal_reason_key: l._personal_reason_key,
          _personal_reason_label: l._personal_reason_label,
        } : l)));
      })
      .catch((e) => { console.warn("[dialer-live] hydrate failed", leadId, e?.response?.status); });
    return () => { cancelled = true; };
  }, [lead?.id, queueMeta.source]);

  // Auto-open the lead profile slide-in 4s into a connected call so the rep
  // has full context surfaced without an extra click. Manually closing it
  // ("user dismissed") respects the user's choice until the next call.
  const [profileAutoDismissed, setProfileAutoDismissed] = useState(false);
  useEffect(() => {
    if (state !== "connected" || profileAutoDismissed || leadProfileOpen) return;
    const h = setTimeout(() => setLeadProfileOpen(true), 4000);
    return () => clearTimeout(h);
  }, [state, profileAutoDismissed, leadProfileOpen]);
  useEffect(() => { if (state === "idle") setProfileAutoDismissed(false); }, [state]);

  const updateActiveLead = useCallback((next) => {
    setQueue((q) => q.map((l, i) => (i === activeIdx ? next : l)));
    // BW-04 (Feb 12 PM-5) — fire-and-forget persistence for real leads.
    // We diff against the queue's current snapshot to figure out which
    // sub-collection actually changed and only PATCH the affected array.
    // Demo leads silently skip. We don't await — the slide-in already
    // shows optimistic UI; persistence errors land in console + toast.
    try {
      if (!_isPersistableLeadId(next?.id)) return;
      const prev = queue[activeIdx];
      const contactsChanged = JSON.stringify(prev?.contacts || []) !== JSON.stringify(next?.contacts || []);
      const locationsChanged = JSON.stringify(prev?.locations || []) !== JSON.stringify(next?.locations || []);
      if (contactsChanged) {
        axios.patch(`${_API}/leads/${next.id}/contacts`, { contacts: next.contacts || [] }, { headers: _authHeaders(), timeout: 8000 })
          .catch((e) => { console.warn("[lead.contacts] persist failed:", e?.response?.status, e?.message); });
      }
      if (locationsChanged) {
        axios.patch(`${_API}/leads/${next.id}/locations`, { locations: next.locations || [] }, { headers: _authHeaders(), timeout: 8000 })
          .catch((e) => { console.warn("[lead.locations] persist failed:", e?.response?.status, e?.message); });
      }
    } catch (e) {
      console.warn("[lead.persist] diff/dispatch error:", e?.message);
    }
  }, [activeIdx, queue]);

  // Timer
  useEffect(() => {
    if (state !== "connected" && state !== "held") return;
    const h = setInterval(() => setTimer((t) => t + 1), 1000);
    return () => clearInterval(h);
  }, [state]);

  // Mock transcript stream during connected
  useEffect(() => {
    if (state !== "connected") return;
    let i = 0;
    const h = setInterval(() => {
      if (i >= TRANSCRIPT_SEED.length) return;
      setTranscript((t) => [...t, TRANSCRIPT_SEED[i]]);
      i += 1;
    }, 3500);
    return () => clearInterval(h);
  }, [state]);

  // Rotate AI nudges
  useEffect(() => {
    if (state !== "connected") return;
    const h = setInterval(() => setNudgeIdx((n) => (n + 1) % AI_NUDGES.length), 6000);
    return () => clearInterval(h);
  }, [state]);

  const startCall = useCallback((leadOverride) => {
    // `leadOverride` lets callers (Power Dial auto-advance) pass the
    // *next* lead explicitly. Without it the setTimeout closure would
    // capture the lead that was just dispositioned — dialing the same
    // person again. Always default to the currently-bound `lead`.
    const target = leadOverride || lead;
    if (!target) return;
    // Iter 194 (Vipal — 1st-dial-basis): if the lead has a backing
    // dialer_queue row, claim it atomically BEFORE we kick the SIP
    // dial. The claim is what locks the lead away from other reps on
    // shared-pool campaigns. We don't block the call on the network
    // call — it runs in parallel and we update the queue id from the
    // response so subsequent disposition save can route /outcome to
    // the right row.
    if (target._queue_item_id && target._queue_status === "pending") {
      const tk = (typeof localStorage !== "undefined" && localStorage.getItem("token")) || "";
      axios.post(`${_API}/dialer/queue/${target._queue_item_id}/claim`, {}, {
        headers: tk ? { Authorization: `Bearer ${tk}` } : {},
        timeout: 8000,
      }).catch((err) => {
        const status = err?.response?.status;
        if (status === 409) {
          // Another rep beat us to it. Remove from local queue + toast.
          toast.error(`Already claimed: ${err?.response?.data?.detail || "another rep took it"}`);
          setQueue((q) => q.filter((l) => l.id !== target.id));
        }
        // 404/500 etc — log but don't block the call; the rep is dialing.
      });
    }
    setTimer(0);
    setTranscript([]);
    setNudgeIdx(0);
    setState("ringing");
    const primary = target.contacts?.[0];
    if (primary) {
      // Append the current primary to contactsCalled (dedup by id+phone)
      setContactsCalled((prev) => {
        const key = (c) => `${c?.id || ""}|${c?.phone || ""}|${c?.name || ""}`;
        const k = key(primary);
        if (prev.some((c) => key(c) === k)) return prev;
        return [...prev, { ...primary }];
      });
    }
    // Persist "in-flight" marker so SalesDialer's <ResumeLastCallPill /> can
    // surface the resume affordance if the rep navigates away mid-call.
    try {
      sessionStorage.setItem("dialerActiveSession", JSON.stringify({
        leadId: target?.id,
        leadName: target?.clinic || "",
        contactName: primary?.name || "",
        startedAt: Date.now(),
      }));
    } catch { /* sessionStorage may be unavailable */ }
    // Super-user fix May 2026: actually place the call through the
    // GlobalWebPhoneDock's real RC WebRTC session instead of the
    // legacy `setTimeout` mock. The dock listens for `webphone:dial`,
    // auto-expands its softphone, places the call, and broadcasts
    // `webphone:state` events that we mirror onto `state` here so the
    // CallStage timer + buttons reflect reality. If the dock isn't
    // mounted (rep hasn't connected RC OAuth), we still surface a toast.
    const phoneNumber = primary?.phone || target?.phone || "";
    if (phoneNumber) {
      try {
        window.dispatchEvent(new CustomEvent("webphone:dial", {
          detail: { number: phoneNumber, leadId: target?.id, leadName: target?.clinic || "",
                    campaignId: target?._queue_campaign_id || null, queueItemId: target?._queue_item_id || null },
        }));
        toast.info(`Dialing ${phoneNumber}…`);
      } catch {
        toast.error("Couldn't reach the in-browser softphone — connect RingCentral in Settings.");
        setState("idle");
      }
    } else {
      toast.error("No phone number on this lead.");
      setState("idle");
    }
  }, [lead]);

  // Mirror the GlobalWebPhoneDock's real RC session state onto our local
  // `state` so the CallStage timer, button enable-states, and disposition
  // flow stay in sync with reality. The dock dispatches `webphone:state`
  // every time the underlying SIP session transitions.
  useEffect(() => {
    const onState = (ev) => {
      const next = ev?.detail?.state;
      if (!next) return;
      if (next === "dialing" || next === "ringing") setState("ringing");
      else if (next === "connected") setState("connected");
      else if (next === "ending") setState("connected"); // keep buttons enabled until cleared
      else if (next === "idle") {
        // Iter 192k (Joshua, June 24 — "If I end the call in the softphone
        // pop-up it should trigger the post-call disposition lightbox"). The
        // SIP session ended. If we were mid-call (ringing / connected / held)
        // — regardless of HOW the call ended (CallStage End button vs
        // softphone End button vs remote hangup) — fall into wrap-up AND
        // open the disposition modal. Previously only the CallStage end
        // button opened the modal; if the rep hung up from the softphone
        // dock the state transitioned to wrap-up but the modal never
        // appeared, so the disposition was silently skipped.
        setState((s) => {
          const wasOnCall = s === "ringing" || s === "connected" || s === "held";
          if (wasOnCall) {
            setDisposition(true);
            return "wrap-up";
          }
          return "idle";
        });
      }
      const m = ev?.detail?.muted;
      if (typeof m === "boolean") setMuted(m);
    };
    window.addEventListener("webphone:state", onState);
    return () => window.removeEventListener("webphone:state", onState);
  }, []);

  const endCall = useCallback(() => {
    setState("wrap-up");
    setDisposition(true);
  }, []);

  const [keypadOpen, setKeypadOpen] = useState(false);
  const sendDtmfDigit = useCallback((digit) => {
    try {
      window.dispatchEvent(new CustomEvent("webphone:control", {
        detail: { action: "dtmf", tone: String(digit) },
      }));
      toast.info(`DTMF · ${digit}`, { duration: 800 });
    } catch { /* CustomEvent unavailable */ }
  }, []);

  const handleAction = useCallback((action) => {
    if (action === "start") return startCall();
    if (action === "end") {
      // Hang up the real RC session first, then transition to wrap-up.
      try {
        window.dispatchEvent(new CustomEvent("webphone:control", { detail: { action: "hangup" } }));
      } catch { /* */ }
      return endCall();
    }
    if (action === "mute") {
      // Optimistic toggle for instant feedback; the webphone:state
      // listener will reconcile if the SIP server disagrees.
      setMuted((m) => !m);
      try {
        window.dispatchEvent(new CustomEvent("webphone:control", { detail: { action: "mute" } }));
      } catch { /* */ }
      return;
    }
    if (action === "hold") {
      setState((s) => s === "held" ? "connected" : "held");
      try {
        window.dispatchEvent(new CustomEvent("webphone:control", { detail: { action: "hold" } }));
      } catch { /* */ }
      return;
    }
    if (action === "record") {
      toast.info("RingCentral records calls automatically per your account policy.");
      return;
    }
    if (action === "keypad") {
      setKeypadOpen((v) => !v);
      return;
    }
  }, [startCall, endCall]);

  const onSaveDisposition = useCallback(async ({ outcome, callType, sentiment, stage, substatus, nextStep, decisionMakerReached, budgetConfirmed, recordingUrl, notes: dispNotes, followup, advance, completed, contactDispositions, leadEdits, aiApplied, attachments, followupTasks, emailSentMessageId, demoBookingId, productTags }) => {
    const perContactSummary = Array.isArray(contactDispositions) && contactDispositions.length > 1
      ? ` · ${contactDispositions.length} contacts logged`
      : "";
    const editsCount = leadEdits ? Object.keys(leadEdits).length : 0;
    const editsSummary = editsCount > 0 ? ` · ${editsCount} lead field${editsCount === 1 ? "" : "s"} updated` : "";
    const aiSummary = aiApplied?.appliedKeys?.length ? ` · ✦ ${aiApplied.appliedKeys.length} AI auto-mapped` : "";
    const attachSummary = attachments?.length ? ` · 📎 ${attachments.length} attachment${attachments.length === 1 ? "" : "s"}` : "";
    const tasksSummary = followupTasks?.length ? ` · ✓ ${followupTasks.length} task${followupTasks.length === 1 ? "" : "s"}` : "";
    // Iter 340 — outcome + nextStep are now genuinely optional; only
    // include them in the toast summary when the rep actually picked
    // one. Products discussed also surfaced when present so reps can
    // sanity-check their tags before the modal closes.
    const outcomeSummary = outcome ? ` · ${outcome}` : "";
    const productsSummary = Array.isArray(productTags) && productTags.length
      ? ` · 🏷 ${productTags.length} product${productTags.length === 1 ? "" : "s"}`
      : "";

    // Iter 192g (Ioannis, June 24 — "Got an error after the call when
    // trying to save. The lead is not being removed. The first time
    // everything worked"). Previously: toast.success + advance + close
    // fired OPTIMISTICALLY before the POST resolved. When the save then
    // failed (8s timeout blown by Azure AI tagging), the rep saw a
    // success toast immediately followed by a failure toast — and the
    // modal was already gone, the queue had already advanced, and the
    // lead never got marked contacted in the DB. The rep's data was
    // lost without a retry path.
    //
    // Fix: AWAIT the POST. On success → toast + advance + close. On
    // failure → error toast, keep modal open, keep current lead in
    // queue, no advance. The rep can retry by clicking save again.
    // Backend AI tagging is now deferred (see /api/dispositions
    // background task) so the POST should finish in <1s anyway; the
    // 20s timeout below is purely defensive.
    const currentLead = queue[activeIdx];
    if (_isPersistableLeadId(currentLead?.id)) {
      // Iter 210.15 — tasks are now created individually via the
      // shared CreateTaskModal at the moment the rep clicks "Create
      // Task" in the modal (same flow as the Tasks page and Lead
      // page). The old Iter 210.11 batch-POST-on-save loop is gone.
      // We still forward the collected task ids on the disposition
      // doc as `created_task_ids` (audit + Call Review lightbox
      // linkage) — the ids come straight from the followupTasks
      // list which each row already carries a real backend id.
      const createdTaskIds = (followupTasks || [])
        .map((t) => t.id)
        .filter((id) => id && typeof id === "string");

      try {
        const r = await axios.post(`${_API}/dispositions`, {
          lead_id: currentLead.id,
          outcome, callType, sentiment, stage, substatus, nextStep,
          decisionMakerReached, budgetConfirmed, recordingUrl,
          notes: dispNotes, followup, advance,
          // Iter 210.13 — MUST forward the frontend's `completed` flag
          // to the backend. Without this the server's `if body.completed`
          // guard at server.py:24379 never fires and rep-typed notes
          // never get appended to lead.lead_notes (silent data loss —
          // the notes only show up on the activity_log timeline, not
          // on the Lead page's "Lead Notes" panel where reps look).
          completed: completed !== false,
          contactDispositions, leadEdits, aiApplied,
          attachments, followupTasks,
          // Iter 210.11 — audit trail for what actually got created
          // from Step 4. `createdTaskIds` are the real /api/tasks rows;
          // `emailSentMessageId` is the Microsoft Graph message id
          // (populated only when the rep hit Send); `demoBookingId`
          // is the demo_bookings row id (populated only when the rep
          // saved a demo via ScheduleMeetingModal). Legacy
          // `sendFollowupEmail` boolean stays for backward compat
          // (true only when email actually went out).
          sendFollowupEmail: !!emailSentMessageId,
          emailSentMessageId: emailSentMessageId || null,
          demoBookingId: demoBookingId || null,
          createdTaskIds,
          duration_seconds: timer,
          productTags,
          // Sales Dialer context — the backend validates lead/campaign/queue row.
          campaign_id: currentLead._queue_campaign_id || null,
          queue_item_id: currentLead._queue_item_id || null,
        }, { headers: _authHeaders(), timeout: 20000 });
        console.log("[disposition] persisted", r?.data?.id, "tasks:", createdTaskIds.length, "demo:", demoBookingId, "email:", emailSentMessageId);
        // Iter 236 (Aug 2026 · spec §2G) — Assist-touch interstitial.
        // When the backend flags this disposition as landing on a
        // lead currently locked to another rep, surface an amber
        // "Logged as assist" strip. The touch is still recorded on
        // the timeline; it just doesn't confer ownership.
        if (r?.data?.assist_touch) {
          const _owner = r.data.assist_of_owner_name || "another rep";
          toast.warning(
            `⚠ Logged as assist — ${_owner} owns this lead. Your touch is on the timeline but ownership is unchanged.`,
            { duration: 12000 },
          );
        }
        // Iter 210.13 — Tell any open LeadFocus tab for this lead to
        // refetch. If the rep already had /leads/{id} open in another
        // tab or slide-in, the new lead_notes / lead_edits / activity
        // rows show up without a manual refresh. LeadFocus subscribes
        // to this event at LeadFocus.jsx:1559. Best-effort — swallow
        // any dispatcher error (e.g. non-browser test env).
        try {
          window.dispatchEvent(new CustomEvent("lead-focus:refresh", {
            detail: { leadId: currentLead.id },
          }));
        } catch { /* refresh event is best-effort */ }
        // Iter 188.43 (Aug 2026) — the fire-and-forget follow-up call
        // to /api/dialer/queue/{item_id}/outcome was removed here. The
        // /api/dispositions endpoint already handles both queue-row
        // closure (Iter 192i cleanup) AND lead-lock release
        // (Iter 188.43 addition), so the extra POST was pure noise —
        // it 409-Conflicted on every save because the queue row had
        // already been flipped to `completed` server-side inside the
        // same /dispositions request. Backend behavior is unchanged;
        // this just removes the wasted round-trip + red console log.
      } catch (e) {
        // Iter 192k (Joshua, June 24 — "still getting (422) after redeploy").
        // The bare status code doesn't tell us WHICH field FastAPI rejected.
        // Surface the field path + message so we can fix it on the next pass
        // without another bug-report round-trip. Logs the full Pydantic
        // detail array to the console for deeper debugging.
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        let hint = "";
        if (Array.isArray(detail) && detail.length > 0) {
          const first = detail[0];
          const loc = Array.isArray(first?.loc) ? first.loc.slice(1).join(".") : "";
          const msg = first?.msg || "validation error";
          hint = loc ? ` · ${loc}: ${msg}` : ` · ${msg}`;
        } else if (typeof detail === "string") {
          hint = ` · ${detail}`;
        }
        console.warn("[disposition] persist failed:", status, e?.message, detail);
        toast.error(
          `Save failed (${status || "network"})${hint} — disposition NOT recorded. Please retry.`,
          { duration: 12000 }
        );
        // Bail out: keep modal open, keep lead in queue, no advance.
        // Iter 244 · returning `false` tells the shared DispositionModal
        // buttons NOT to auto-close on save-failure paths — the rep must
        // be able to retry without re-opening the wizard.
        return false;
      }
    }

    // Iter 210.8 — success toast no longer references callType (deleted
    // from UI) or outcome (derived from sub-status server-side). Shows
    // the sub-status + stage change so reps still see what was logged.
    toast.success(`Call logged · ${substatus || "no sub-status"} · stage→${stage}${followup ? ` · follow-up ${followup}` : ""}${perContactSummary}${editsSummary}${aiSummary}${attachSummary}${tasksSummary}${productsSummary}`);
    // Iter 210.11 — the email is already SENT (via the Step 4 composer)
    // by the time we get here, so we don't fake a "queued" toast
    // anymore. Just confirm the send happened for the timeline.
    if (emailSentMessageId) toast.info(`✉ Follow-up email confirmed on record`);
    if (demoBookingId) toast.info(`📅 Demo scheduled on Demo Bookings dashboard`);
    // Iter 210.12 — the "Timeline note appended" toast is gone with
    // the timelineNote textarea. Rep notes from Step 2 still append
    // to lead.lead_notes via the backend save_disposition path.

    // Stage the lead edits onto the queue's active lead so subsequent calls
    // / re-opens reflect the updated profile.
    if (leadEdits && Object.keys(leadEdits).length) {
      setQueue((q) => q.map((l, i) => i === activeIdx ? { ...l, _disp_edits: { ...(l._disp_edits || {}), ...leadEdits }, stage, subStatus: substatus || l.subStatus } : l));
    } else if (stage || substatus) {
      setQueue((q) => q.map((l, i) => i === activeIdx ? { ...l, stage: stage || l.stage, subStatus: substatus || l.subStatus } : l));
    }

    setDisposition(false);
    setState("idle");
    setTimer(0);
    setTranscript([]);
    setNotes([]);
    setContactsCalled([]);
    try { sessionStorage.removeItem("dialerActiveSession"); } catch { /* ignore */ }

    // Mark this lead as dispositioned and check if primary campaign is done.
    let nextDispositioned = dispositionedIds;
    if (currentLead?.id) {
      nextDispositioned = new Set(dispositionedIds);
      nextDispositioned.add(currentLead.id);
      setDispositionedIds(nextDispositioned);
    }

    // Find the next uncontacted lead that hasn't been dispositioned yet.
    // Iter 181 (Feb 2026) — advance through the SORTED view so bucket
    // priority (uncontacted → callback → contacted) actually drives the
    // "next lead" selection. Previously this used the raw `queue` array
    // which meant Stage=New prioritization was ignored on advance.
    if (advance) {
      const _sorted = sortedQueue; // memoized above
      const currentSortedIdx = _sorted.findIndex((r) => r.idx === activeIdx);
      const nextUncontactedSorted = _sorted.find((r, i) =>
        i > currentSortedIdx && r.bucket === "uncontacted" && !nextDispositioned.has(r.lead?.id)
      );
      if (nextUncontactedSorted) {
        setActiveIdx(nextUncontactedSorted.idx);
        if (powerDial) {
          // Pass the *next* lead explicitly so the 4s timer doesn't
          // re-dial the one we just dispositioned (closure-capture bug).
          const nextLead = nextUncontactedSorted.lead;
          // Iter 234 — respect committed-time gate: if a committed
          // callback is within the 5-minute window and hasn't been
          // dismissed, do NOT auto-dial the next random lead. The
          // banner surfaces the "Dial now" action instead.
          if (committedGate) {
            toast.info("Committed call approaching — auto-dial paused. Use the banner to dial now.", { duration: 4000 });
          } else {
            toast.info(`Power Dial — auto-dialing ${nextLead?.clinic || "next lead"} in 4s…`, { duration: 4000 });
            setTimeout(() => startCall(nextLead), 4000);
          }
        }
      } else if (queueMode === "primary") {
        // Primary campaign complete — auto-load follow-up queue
        const firstCallbackSorted = _sorted.find((r) =>
          r.bucket === "callback" && !nextDispositioned.has(r.lead?.id) && r.idx !== activeIdx
        );
        if (firstCallbackSorted) {
          setQueueMode("followup");
          setActiveIdx(firstCallbackSorted.idx);
          toast.success("Primary campaign complete · loaded follow-up queue", { duration: 4500 });
        } else {
          // Fall through — pick the next row in the sorted view (any bucket)
          // that isn't the current active and isn't already dispositioned.
          const anyNext = _sorted.find((r, i) =>
            i > currentSortedIdx && r.idx !== activeIdx && !nextDispositioned.has(r.lead?.id)
          );
          if (anyNext) {
            setActiveIdx(anyNext.idx);
            // Iter 234 — respect committed-time gate.
            if (powerDial && !committedGate) setTimeout(() => startCall(anyNext.lead), 4000);
          }
        }
      } else {
        // followup mode — just walk forward in the sorted view.
        const anyNext = _sorted.find((r, i) =>
          i > currentSortedIdx && r.idx !== activeIdx && !nextDispositioned.has(r.lead?.id)
        );
        if (anyNext) {
          setActiveIdx(anyNext.idx);
          // Iter 234 — respect committed-time gate.
          if (powerDial && !committedGate) setTimeout(() => startCall(anyNext.lead), 4000);
        }
      }
    }
    // Iter 244 · Explicit success return so the shared DispositionModal
    // buttons close the modal after this callback resolves. Dialer's
    // own `setDisposition(false)` above already unmounts the modal,
    // making the follow-up onClose a harmless idempotent call.
    return true;
  }, [activeIdx, queue, sortedQueue, powerDial, startCall, dispositionedIds, queueMode, timer, committedGate]);

  const onPolish = useCallback(() => {
    const summary = notes.map((n) => `• ${n.body}`).join("\n");
    toast.success("AI polished notes ready in disposition modal");
    setNotes([{ id: "polished", ts: timer, body: `Polished summary:\n${summary}`, tag: null }]);
  }, [notes, timer]);

  return (
    <div data-testid="sales-dialer-demo" style={{ height: "calc(100vh - 0px)", display: "flex", flexDirection: "column", background: "var(--page-bg)" }}>
      {backupOwnerId && <div style={{ padding: "6px 16px 0" }}>{backupError
        ? <div data-testid="live-backup-denied" style={{ padding: "7px 12px", borderRadius: 8, background: "#FEE2E2", border: "0.5px solid #FCA5A5", color: "#991B1B", fontSize: 12, fontWeight: 600 }}>{backupError}. Your backup access may have ended — go back to the Sales Dialer to pick another campaign.</div>
        : <BackupBanner testid="live-backup-banner" name={backupOwner?.name || "…"} />}</div>}
      <PageHead
        state={state}
        contact={lead?.contacts?.[0]?.name}
        clinic={lead?.clinic}
        onSettings={() => toast.info("Dialer settings (mock)")}
        onBack={() => navigate("/dialer")}
        onOpenProfile={() => setLeadProfileOpen((v) => !v)}
        profileOpen={leadProfileOpen}
        profileEnabled={state === "connected" || state === "held" || state === "ringing"}
      />
      <RcReconnectBanner />
      {/* Iter 234 (spec §1E) — Committed-time gate banner. Appears when
          a committed callback is <5 min out and auto-dial has been
          paused. */}
      {committedGate && (
        <CommittedCallGateBanner
          gate={committedGate}
          onDialNow={() => {
            const idx = (queue || []).findIndex((l) => l?.id === committedGate.leadId);
            if (idx >= 0) {
              setActiveIdx(idx);
              // Small delay lets React commit the active-idx swap first
              // so `startCall()`'s `leadOverride` reads the fresh row.
              setTimeout(() => startCall(queue[idx]), 100);
              setCommittedDismissedIds((s) => { const n = new Set(s); n.add(committedGate.leadId); return n; });
            } else {
              toast.error("Committed lead is not in the current queue.");
            }
          }}
          onDismiss={() => {
            setCommittedDismissedIds((s) => { const n = new Set(s); n.add(committedGate.leadId); return n; });
          }}
        />
      )}
      {/* Empty / loading state — shown when the queue hasn't resolved yet (real mode async fetch)
          OR the queue is genuinely empty. Without this guard, downstream <CallStage> + cards
          deref `lead.clinic` / `lead.contacts[0]` on an undefined value and crash the page. */}
      {!lead ? (
        <div data-testid="dialer-queue-empty" style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 10, padding: 40, color: "var(--text-3)" }}>
          {queueLoading ? (
            <>
              <div style={{ width: 32, height: 32, borderRadius: "50%", border: "2.5px solid var(--border-soft)", borderTopColor: "var(--blue)", animation: "spin 1s linear infinite" }} />
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--navy)" }}>Loading queue…</div>
              <div style={{ fontSize: 11 }}>Pulling your top hot leads from the database.</div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--navy)" }}>No open leads in your queue</div>
              <div style={{ fontSize: 11, maxWidth: 360, textAlign: "center" }}>You're caught up. Add new leads, advance an existing one to an open stage, or wait for the next assignment.</div>
              <button onClick={() => navigate("/leads")} className="btn btn-primary" style={{ marginTop: 8, padding: "6px 14px", fontSize: 12 }}>View leads</button>
            </>
          )}
        </div>
      ) : (
      <div className="dialer-grid" style={{ flex: 1, display: "grid", gridTemplateColumns: "320px 1fr 360px", gap: 18, padding: 22, overflow: "auto" }}>
        <CallQueuePanel
          queue={queue}
          activeIdx={activeIdx}
          onPick={setActiveIdx}
          powerDial={powerDial}
          setPowerDial={setPowerDial}
          onOpenLead={(l) => navigate(`/lead-focus/${l.id}`)}
          uncontactedOnly={uncontactedOnly}
          setUncontactedOnly={setUncontactedOnly}
          displayedQueue={displayedQueue}
          queueMode={queueMode}
          onResetQueue={() => { setQueueMode("primary"); setDispositionedIds(new Set()); toast.info("Queue reset to primary campaign"); }}
          queueMeta={queueMeta}
        />
        <div style={{ display: "grid", gridTemplateRows: "minmax(min-content, 1.35fr) minmax(0, 1fr)", gap: 18, minHeight: 0 }}>
          <CallStage lead={lead} state={state} timer={timer} onAction={handleAction} transcript={transcript} muted={muted} keypadOpen={keypadOpen} onDtmf={sendDtmfDigit} currentCallId={rcCallCtx.callId} telephonySessionId={rcCallCtx.telephonySessionId} partyId={rcCallCtx.partyId} />
          <DialerContactsAddressesCard
            lead={lead}
            onChange={updateActiveLead}
            onOpenEmail={() => setEmailOpen(true)}
            onOpenLeadInfo={() => { setLeadProfileOpen(true); setProfileAutoDismissed(false); }}
            callActive={state === "connected" || state === "held" || state === "ringing"}
            onQuickDial={(contact) => {
              const isLive = state === "connected" || state === "held" || state === "ringing";
              // Track the contact regardless of call state — we'll dedupe in startCall too.
              setContactsCalled((prev) => {
                const key = (c) => `${c?.id || ""}|${c?.phone || ""}|${c?.name || ""}`;
                const k = key(contact);
                if (prev.some((c) => key(c) === k)) return prev;
                return [...prev, { ...contact }];
              });
              if (isLive) {
                toast.info(`Ending current call and dialing ${contact?.name || "contact"}…`);
                setState("idle");
                setTimer(0);
                setTranscript([]);
                setTimeout(() => startCall(), 350);
              } else {
                toast.info(`Dialing ${contact?.name || "contact"} (${contact?.phone || "—"})…`);
                startCall();
              }
            }}
          />
        </div>
        <TalkTrackPanel lead={lead} currentNudge={AI_NUDGES[nudgeIdx]} />
      </div>
      )}
      {lead && <NoteTaker open={noteOpen} setOpen={setNoteOpen} lead={lead} timer={timer} notes={notes} setNotes={setNotes} activeTag={activeTag} setActiveTag={setActiveTag} onPolish={onPolish} />}
      {disposition && lead && <DispositionModal lead={lead} timer={timer} notes={notes} contactsCalled={contactsCalled} onClose={() => { setDisposition(false); setState("idle"); setContactsCalled([]); try { sessionStorage.removeItem("dialerActiveSession"); } catch { /* ignore */ } }} onSave={onSaveDisposition} />}
      {lead && <LeadProfileSlideIn
        open={leadProfileOpen}
        lead={lead}
        onClose={() => { setLeadProfileOpen(false); setProfileAutoDismissed(true); }}
        onOpenLead={(l) => navigate(`/lead-focus/${l.id}`)}
        onChangeLead={updateActiveLead}
        onCall={(l) => startCall(l)}
        onEmail={() => setEmailOpen(true)}
      />}
      {emailOpen && lead && (
        <DialerEmailComposer
          lead={lead}
          resources={[]}
          learningSlides={[]}
          onClose={() => setEmailOpen(false)}
          onSend={({ to }) => {
            setEmailOpen(false);
            toast.success(`Email queued to ${to} via Microsoft 365`);
          }}
        />
      )}
      <style>{`
        @keyframes ringPulse {
          0% { transform: scale(0.9); opacity: 0.8; }
          100% { transform: scale(1.4); opacity: 0; }
        }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        @media (max-width: 1280px) {
          .dialer-grid { grid-template-columns: 280px 1fr !important; }
          .dialer-grid > *:last-child { display: none !important; }
        }
        @media (max-width: 960px) {
          .dialer-grid { grid-template-columns: 1fr !important; overflow-y: auto !important; }
        }
      `}</style>
    </div>
  );
}
